// ─── Website Enquiries Tab ──────────────────────────────────────────────────
const WebsiteEnquiriesTab = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // TODO: Replace with actual API endpoint if available
    const fetchEnquiries = async () => {
      setLoading(true);
      setError('');
      try {
        // Example: const res = await axios.get(`${API_URL}/api/website-enquiries`, { headers: authHeader() });
        // setEnquiries(res.data || []);
        setEnquiries([]); // Placeholder: no data
      } catch (err) {
        setError('Failed to fetch website enquiries.');
        setEnquiries([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEnquiries();
  }, []);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-xl font-black text-black">Website Enquiries</h2>
      {loading && <div className="text-gray-600">Loading...</div>}
      {error && <div className="text-red-600 mb-3">{error}</div>}
      {!loading && enquiries.length === 0 && <div className="text-gray-500">No website enquiries found.</div>}
      {!loading && enquiries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full rounded-2xl bg-white shadow-sm">
            <thead>
              <tr>
                <th className="px-3 py-2 border">Name</th>
                <th className="px-3 py-2 border">Email</th>
                <th className="px-3 py-2 border">Phone</th>
                <th className="px-3 py-2 border">Message</th>
                <th className="px-3 py-2 border">Date</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((row, idx) => (
                <tr key={row.id || idx}>
                  <td className="px-3 py-2 border">{row.name}</td>
                  <td className="px-3 py-2 border">{row.email}</td>
                  <td className="px-3 py-2 border">{row.phone}</td>
                  <td className="px-3 py-2 border">{row.message}</td>
                  <td className="px-3 py-2 border">{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import TargetWidget from '../components/TargetWidget';
import { InstallmentDuesPanel } from '../components/ServiceChargeInstallments';
import { fmtDate, fmtDateTime } from '../utils/dates';
import { getDistanceInfo, isPreferredLocationMatch, resolvePointFromText, KNOWN_PLACE_NAMES } from '../utils/keralaGeo';
import {
  fuzzyScore, parseSalary, latestSalary, qualMatch, parseNumberRange, QUAL_LEVELS, bandFor,
  scoreVacancyForCandidate, scoreCandidateForCompany, filtersFromCandidate, filtersFromVacancy
} from '../utils/aiMatch';


const INTERVIEW_STATUSES = ['Not Scheduled', 'Scheduled', 'Selected', 'Shortlisted', 'Waiting for the result', 'Rejected', 'Shortlisted for next level', 'No updates', 'No Show'];
const INTERVIEW_MODES = ['Online', 'Direct', 'Phone', 'Walk-in', 'Panel'];
// eslint-disable-next-line no-unused-vars
const OUTCOMES = ['Pending', 'Selected', 'Rejected', 'Shortlisted', 'Shortlisted for next level', 'Waiting for the result', 'No updates'];

// ─── Utilities ────────────────────────────────────────────────────────────────
const token = () => localStorage.getItem('token');
const authHeader = () => ({ Authorization: `Bearer ${token()}` });
const getPhoneDigits = (v) => String(v || '').replace(/\D/g, '');
const getWADigits = (v) => { const d = getPhoneDigits(v); return d.length === 10 ? `91${d}` : d; };

// ─── Core match scorer ────────────────────────────────────────────────────────
// Point budget (max before 100 cap): ~108 — gives room so a near-perfect match
// isn't artificially penalised by the cap.
//   Qualification  : 25
//   Department     : 20
//   Role / Title   : 20
//   Location       : 12
//   Section        :  8
//   Experience     : 12  (presence + role relevance)
//   Gender fit     :  5
//   Salary         :  6
// ─────────────────────────────────────────────────────────────────────────────
const matchScore = (reg, vac) => {
  if (!vac) return { score: 0, band: 'No Vacancy', reasons: ['Select a vacancy.'], warnings: [] };
  let score = 0; const reasons = [], warnings = [];

  // ── Qualification (25 pts) ──────────────────────────────────────────────────
  const mq = String(reg.highest_qualification || '').trim();
  const vq = String(vac.qualification || '').trim();
  const qm = qualMatch(mq, vq);
  if (vq) {
    if (qm >= 0.9)       { score += 25; reasons.push(`Qualification matches: ${mq}.`); }
    else if (qm >= 0.65) { score += Math.round(qm * 22); reasons.push('Qualification meets requirement.'); }
    else if (qm >= 0.3)  { score += Math.round(qm * 16); reasons.push('Qualification partially meets requirement.'); }
    else if (qm > 0)     { score += 3; }
    else if (mq)         { warnings.push(`Qualification mismatch — vacancy needs ${vq}, member has ${mq}.`); }
    else                 { warnings.push(`Qualification not specified — vacancy needs ${vq}.`); }
  } else {
    // No vacancy requirement — partial credit for having any qualification
    if (mq) { score += 8; reasons.push(`Has qualification: ${mq}.`); }
  }

  // ── Department (20 pts) ────────────────────────────────────────────────────
  const rd = String(reg.department || ''), vd = String(vac.department || '');
  const deptFS = fuzzyScore(rd, vd);
  if (deptFS >= 0.9)      { score += 20; reasons.push('Department exactly matches.'); }
  else if (deptFS >= 0.6) { score += Math.round(deptFS * 18); reasons.push('Department closely matches.'); }
  else if (deptFS > 0)    { score += Math.round(deptFS * 12); reasons.push('Department partially overlaps.'); }
  else if (rd && vd)      { warnings.push('Department mismatch.'); }

  // ── Role / Job Title (20 pts) ──────────────────────────────────────────────
  const ej = String(reg.expecting_job || ''), vt = String(vac.title || '');
  const roleFS = fuzzyScore(ej, vt);
  if (roleFS >= 0.85)     { score += 20; reasons.push('Expected role matches vacancy title.'); }
  else if (roleFS >= 0.5) { score += Math.round(roleFS * 18); reasons.push('Expected role aligns with vacancy title.'); }
  else if (roleFS > 0)    { score += Math.round(roleFS * 12); reasons.push('Role title partially overlaps.'); }
  else                    { warnings.push('Role title not closely aligned.'); }

  // ── Location / Distance (12 pts) — real geo-distance when resolvable, ──────
  // ── falling back to fuzzy district/place text matching otherwise. ──────────
  const pl = String(reg.preferred_location || reg.place || '').toLowerCase();
  const cd = String(reg.district || '').toLowerCase();
  const vl = `${vac.location || ''} ${vac.district || ''}`.toLowerCase();
  const locFS = Math.max(
    pl ? fuzzyScore(pl, vl) : 0,
    cd ? fuzzyScore(cd, vl) : 0,
    (pl && vl.includes(pl)) ? 1 : 0,
    (cd && vl.includes(cd)) ? 1 : 0
  );
  const distInfo = getDistanceInfo(reg, vac);
  if (distInfo) {
    const approxTag = distInfo.approx ? ' (approx.)' : '';
    if (distInfo.km <= 15)      { score += 12; reasons.push(`📍 ${distInfo.km} km from vacancy — local candidate${approxTag}.`); }
    else if (distInfo.km <= 30) { score += 10; reasons.push(`📍 ${distInfo.km} km from vacancy — commutable${approxTag}.`); }
    else if (distInfo.km <= 60) { score += 6;  reasons.push(`📍 ${distInfo.km} km from vacancy${approxTag}.`); }
    else                        { score += 2;  warnings.push(`📍 ${distInfo.km} km away — likely needs relocation${approxTag}.`); }
  } else if (locFS >= 0.5)    { score += 12; reasons.push('Location aligns.'); }
  else if (locFS > 0)  { score += 5;  reasons.push('Location partially matches.'); }
  else                 { warnings.push('Location alignment weak.'); }

  // ── Preferred location intent (3 pts bonus) — member explicitly named this ─
  // ── vacancy's area as a preference, beyond mere geographic proximity. ──────
  if (isPreferredLocationMatch(reg, vac)) { score += 3; reasons.push('🎯 Matches candidate’s preferred location.'); }

  // ── Section (8 pts) ────────────────────────────────────────────────────────
  const rs = String(reg.section || ''), vs = String(vac.section || '');
  const secFS = fuzzyScore(rs, vs);
  if (secFS >= 0.9)    { score += 8; reasons.push('Section matches.'); }
  else if (secFS > 0)  { score += Math.round(secFS * 6); reasons.push('Section partially overlaps.'); }

  // ── Experience (12 pts) ────────────────────────────────────────────────────
  const expLen = Array.isArray(reg.experience_details) ? reg.experience_details.filter((e) => e?.title_post || e?.company_name).length : 0;
  const vtLower = vt.toLowerCase();
  if (expLen > 0) {
    score += Math.min(expLen * 3, 8); reasons.push(`${expLen} experience record${expLen > 1 ? 's' : ''}.`);
    if ((reg.experience_details || []).some((e) => fuzzyScore(e?.title_post, vtLower) > 0 || fuzzyScore(e?.exp_department, vd.toLowerCase()) > 0)) {
      score += 4; reasons.push('Past experience relevant to vacancy.');
    }
  } else { warnings.push('No experience history.'); }

  // ── Gender fit (5 pts) ─────────────────────────────────────────────────────
  const vGender = String(vac.gender || '').toLowerCase().trim();
  const mGender = String(reg.gender || '').toLowerCase().trim();
  if (vGender && vGender !== 'any' && vGender !== 'both' && vGender !== 'male/female') {
    if (mGender && mGender === vGender) { score += 5; reasons.push('Gender requirement met.'); }
    else if (mGender && mGender !== vGender) { warnings.push('Gender preference may not match.'); }
  } else if (vGender) { score += 5; reasons.push('Gender requirement: open.'); }

  // ── Salary (6 pts) ─────────────────────────────────────────────────────────
  const cs = latestSalary(reg), vs2 = parseSalary(vac.salary);
  if (cs && vs2) {
    const gap = Math.abs(cs - vs2) / Math.max(cs, vs2);
    if (gap <= 0.15)      { score += 6; reasons.push('Salary close to vacancy offer.'); }
    else if (gap <= 0.35) { score += 3; reasons.push('Salary moderately aligned.'); }
    else                  { warnings.push('Salary gap may need discussion.'); }
  }

  const s = Math.min(score, 100);
  const band = s >= 80 ? 'Strong Match' : s >= 60 ? 'Good Match' : s >= 40 ? 'Moderate Match' : 'Weak Match';
  return { score: s, band, reasons: reasons.length ? reasons : ['Limited profile signals.'], warnings };
};

// ─── Badge helpers ────────────────────────────────────────────────────────────
const iBadge = (st) => { if (st === 'Selected') return 'bg-emerald-100 text-emerald-700'; if (st === 'Scheduled') return 'bg-blue-100 text-blue-700'; if (st === 'Shortlisted' || st === 'Shortlisted for next level') return 'bg-cyan-100 text-cyan-700'; if (st === 'Waiting for the result') return 'bg-amber-100 text-amber-700'; if (st === 'Rejected' || st === 'No Show') return 'bg-red-100 text-red-700'; return 'bg-gray-100 text-gray-500'; };
const INTERVIEW_EVENT_OUTCOMES = ['Pending', 'Selected', 'Rejected', 'Shortlisted', 'Shortlisted for next level', 'Waiting for the result', 'No updates', 'No Show'];
// "Pending" alone is confusing: say whether the interview is still coming up or its result was never entered
const interviewEventLabel = (ev) => {
  if (ev.outcome && ev.outcome !== 'Pending') return ev.outcome;
  const when = ev.interview_date ? new Date(`${ev.interview_date}T${ev.interview_time || '23:59'}`) : null;
  return when && !Number.isNaN(when.getTime()) && when > new Date() ? 'Upcoming' : 'Result not updated';
};
const oBadge = (o) => { if (o === 'Upcoming') return 'bg-blue-100 text-blue-700'; if (o === 'Result not updated') return 'bg-orange-100 text-orange-700 border border-orange-200'; if (o === 'No Show') return 'bg-red-50 text-red-600'; return oBadgeBase(o); };
const oBadgeBase = (o) => { if (o === 'Selected') return 'bg-emerald-100 text-emerald-700'; if (o === 'Rejected') return 'bg-red-100 text-red-700'; if (o === 'Shortlisted' || o === 'Shortlisted for next level') return 'bg-cyan-100 text-cyan-700'; if (o === 'Waiting for the result') return 'bg-amber-100 text-amber-700'; return 'bg-gray-100 text-gray-500'; };
const sBadge = (s) => { if (s >= 80) return 'bg-emerald-100 text-emerald-700 border-emerald-200'; if (s >= 60) return 'bg-cyan-100 text-cyan-700 border-cyan-200'; if (s >= 40) return 'bg-amber-100 text-amber-700 border-amber-200'; return 'bg-gray-100 text-gray-500 border-gray-200'; };
const avChip = (v) => { const ok = v === 'Available' || v === 'Accepted'; const bad = v === 'Not Available' || v === 'Rejected'; return ok ? 'bg-emerald-100 text-emerald-700' : bad ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'; };
const avIcon = (v) => { const ok = v === 'Available' || v === 'Accepted'; const bad = v === 'Not Available' || v === 'Rejected'; return ok ? '✅' : bad ? '❌' : '⏳'; };
const PartnerTag = ({ row }) => Number(row?.via_partner) === 1 ? (
  <span
    className="inline-flex items-center gap-0.5 rounded-full bg-amber-400 border border-amber-500 px-1.5 py-0.5 text-[9px] font-black text-black leading-none whitespace-nowrap"
    title={row.partner_org_name ? `Via Partner: ${row.partner_org_name}` : 'Via Partner'}
  >
    🤝 {row.partner_org_name ? row.partner_org_name : 'Partner Ref'}
  </span>
) : null;

// ─── Central API URL ──────────────────────────────────────────────────────────
// (API_URL already declared above, do not redeclare)
// ─── Central API URL ──────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── Applied Members Tab ──────────────────────────────────────────────────────
const AppliedMembersTab = () => {
  const [applied, setApplied] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  useEffect(() => {
    const fetchApplied = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/api/recruiter/applied-members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setApplied(res.data?.applied || []);
      } catch (err) {
        setError('Failed to fetch applied members.');
        setApplied([]);
      } finally {
        setLoading(false);
      }
    };
    fetchApplied();
  }, []);

  const totalPages = Math.max(1, Math.ceil(applied.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paginated = applied.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-xl font-black text-black">Applied Members</h2>
      {loading && <div className="text-gray-600">Loading...</div>}
      {error && <div className="text-red-600 mb-3">{error}</div>}
      {!loading && applied.length === 0 && <div className="text-gray-500">No applications found.</div>}
      {!loading && applied.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full rounded-2xl bg-white shadow-sm">
            <thead>
              <tr>
                <th className="px-3 py-2 border">Member Name</th>
                <th className="px-3 py-2 border">Email</th>
                <th className="px-3 py-2 border">Phone</th>
                <th className="px-3 py-2 border">Job Title</th>
                <th className="px-3 py-2 border">Company</th>
                <th className="px-3 py-2 border">Department</th>
                <th className="px-3 py-2 border">Section</th>
                <th className="px-3 py-2 border">Location</th>
                <th className="px-3 py-2 border">Applied At</th>
                <th className="px-3 py-2 border">Resume</th>
                <th className="px-3 py-2 border">Photo</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((row) => (
                <tr key={row.application_id}>
                  <td className="px-3 py-2 border font-semibold">{row.user_name}</td>
                  <td className="px-3 py-2 border">{row.user_email}</td>
                  <td className="px-3 py-2 border">{row.user_phone}</td>
                  <td className="px-3 py-2 border">{row.job_title}</td>
                  <td className="px-3 py-2 border">{row.job_company}</td>
                  <td className="px-3 py-2 border">{row.job_department}</td>
                  <td className="px-3 py-2 border">{row.job_section}</td>
                  <td className="px-3 py-2 border">{row.job_location} {row.job_district ? `, ${row.job_district}` : ''}</td>
                  <td className="px-3 py-2 border">{row.applied_at ? new Date(row.applied_at).toLocaleString() : '-'}</td>
                  <td className="px-3 py-2 border">
                    {row.user_resume ? (
                      <a href={row.user_resume.startsWith('http') ? row.user_resume : `${API_URL}${row.user_resume}`} target="_blank" rel="noreferrer" className="text-blue-600 underline">View</a>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-2 border">
                    {row.user_photo ? (
                      <a href={row.user_photo.startsWith('http') ? row.user_photo : `${API_URL}${row.user_photo}`} target="_blank" rel="noreferrer" className="inline-block" title="Open photo">
                        <img src={row.user_photo.startsWith('http') ? row.user_photo : `${API_URL}${row.user_photo}`} alt="Talent" className="w-10 h-10 rounded object-cover border" />
                      </a>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">«</button>
          <button type="button" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
            .map((n, i, arr) => (
              <React.Fragment key={n}>
                {i > 0 && arr[i - 1] !== n - 1 && <span className="text-gray-400 text-sm px-1">…</span>}
                <button type="button" onClick={() => setPage(n)}
                  className={`rounded-xl border px-3 py-1.5 text-sm font-bold transition ${safePage === n ? 'bg-black text-yellow-400 border-black' : 'hover:bg-gray-50'}`}>
                  {n}
                </button>
              </React.Fragment>
            ))}
          <button type="button" onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next ›</button>
          <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">»</button>
          <span className="text-sm text-gray-500 ml-2">Page {safePage} of {totalPages}</span>
        </div>
      )}
    </div>
  );
};

// ─── PUT helper — always sends full payload  ──────────────────────────────────
const putWorkflow = (id, reg, extra = {}) => axios.put(
  `${API_URL}/api/staff/recruiter/registrations/${id}`,
  {
    recruiter_notes: reg.recruiter_notes || '',
    selected_job_id: reg.selected_job_id || null,
    is_shortlisted: Number(reg.is_shortlisted || 0),
    candidate_availability: reg.candidate_availability || 'Pending',
    candidate_availability_note: reg.candidate_availability_note || '',
    vacancy_availability: reg.vacancy_availability || 'Pending',
    vacancy_availability_note: reg.vacancy_availability_note || '',
    hr_assistant_confirmation: reg.hr_assistant_confirmation || 'Pending',
    assistant_recruiter_confirmation: reg.assistant_recruiter_confirmation || 'Pending',
    interview_date: reg.interview_date || '',
    interview_time: reg.interview_time || '',
    interview_location: reg.interview_location || '',
    interview_mode: reg.interview_mode || 'Direct',
    reporting_officer: reg.reporting_officer || '',
    interview_status: reg.interview_status || 'Not Scheduled',
    interview_note: reg.interview_note || '',
    service_charge_status: reg.service_charge_status || 'Pending',
    service_charge_amount: reg.service_charge_amount || '',
    offer_letter_status: reg.offer_letter_status || 'Locked',
    welcome_letter_status: reg.welcome_letter_status || 'Locked',
    placement_status: reg.placement_status || 'Open',
    placement_note: reg.placement_note || '',
    offered_job_title: reg.offered_job_title || '',
    offered_salary: reg.offered_salary || '',
    joining_date: reg.joining_date || '',
    work_location: reg.work_location || '',
    probation_period: reg.probation_period || '',
    work_schedule: reg.work_schedule || '',
    other_terms: reg.other_terms || '',
    leave_days: reg.leave_days || '',
    agreement_execution: reg.agreement_execution || '',
    ...extra,
  },
  { headers: authHeader() }
);

// ─── Notification Bell ────────────────────────────────────────────────────────
const NotificationBell = () => {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const fetchNotes = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/api/staff/notifications`, { headers: authHeader() }); setNotes(res.data?.notifications || []); } catch {}
  }, []);
  useEffect(() => { fetchNotes(); const t = setInterval(fetchNotes, 15000); return () => clearInterval(t); }, [fetchNotes]);
  const unread = notes.filter((n) => !n.is_read).length;
  const markAll = async () => { try { await axios.put(`${API_URL}/api/staff/notifications/mark-all-read`, {}, { headers: authHeader() }); setNotes((p) => p.map((n) => ({ ...n, is_read: 1 }))); } catch {} };
  return (
    <div className="relative">
      <button type="button" onClick={() => { setOpen((p) => !p); if (!open) fetchNotes(); }} className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white hover:bg-white/20">
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
        {unread > 0 && <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-black text-black">Alerts</p>
            <div className="flex gap-3">{unread > 0 && <button type="button" onClick={markAll} className="text-xs font-bold text-indigo-600 hover:underline">Mark all read</button>}<button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-400 hover:text-black">✕</button></div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notes.length === 0 ? <p className="py-6 text-center text-sm text-gray-400">No notifications yet.</p> : notes.slice(0, 20).map((n) => (
              <div key={n.id} className={`border-b border-gray-50 px-4 py-3 ${!n.is_read ? 'bg-amber-50' : ''}`}>
                <p className={`text-sm font-bold ${!n.is_read ? 'text-amber-900' : 'text-gray-700'}`}>{n.title}</p>
                <p className="mt-0.5 text-xs text-gray-500 leading-4">{n.message}</p>
                {n.created_at && <p className="mt-1 text-[10px] text-gray-400">{fmtDateTime(n.created_at)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Contact Link ─────────────────────────────────────────────────────────────
const ContactLink = ({ value, type = 'call' }) => {
  const label = String(value || '').trim();
  if (!label) return <span className="text-gray-400">—</span>;
  const digits = type === 'whatsapp' ? getWADigits(label) : getPhoneDigits(label);
  if (!digits) return <span>{label}</span>;
  return <a href={type === 'whatsapp' ? `https://wa.me/${digits}` : `tel:${digits}`} target={type === 'whatsapp' ? '_blank' : undefined} rel={type === 'whatsapp' ? 'noreferrer' : undefined} className="font-semibold text-blue-600 underline underline-offset-2">{label}</a>;
};

// ─── Profile Modal ────────────────────────────────────────────────────────────
const ProfileModal = ({ reg, onClose }) => {
  if (!reg) return null;
  const exp = Array.isArray(reg.experience_details) ? reg.experience_details : [];
  const fileUrl = (path) => !path ? null : (path.startsWith('http') ? path : `${API_URL}${path}`);
  const photoUrl = fileUrl(reg.photo_file);
  const resumeUrl = fileUrl(reg.resume_file);

  const infoFields = [
    ['Phone', <ContactLink key="p" value={reg.contact_number} />],
    ['WhatsApp', <ContactLink key="w" value={reg.whatsapp_number} type="whatsapp" />],
    ['Email', reg.email],
    ['District', reg.district],
    ['Place', reg.place],
    ['Qualification', reg.highest_qualification],
    ['Qual. Department', reg.qualification_department],
    ['Completed Year', reg.completed_year],
    ['Exam Status', reg.exam_status],
    ['Department', reg.department],
    ['Section', reg.section],
    ['Expecting Job', reg.expecting_job],
    ['Age', reg.age],
    ['Marital Status', reg.marital_status],
    ['Guardian Number', reg.guardian_number],
    ['Contact Through', reg.contact_through],
    ['License Category', reg.license_category],
    ['Highlight Language', reg.highlight_language],
    ['Pref. Location', reg.preferred_location],
    ['Google Location', reg.google_location],
    ['Job Priority', reg.job_priority],
    ['Rating', reg.rating],
    ['How you heard about Dani', reg.how_you_heard],
    ['Additional Qualification', reg.additional_qualification],
    ['Remarks', reg.remarks],
    ['Date of Communication', reg.date_of_communication],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 rounded-t-3xl bg-black p-6">
          <div className="flex items-center gap-4">
            {photoUrl
              ? <img src={photoUrl} alt={reg.name} className="h-16 w-16 rounded-2xl object-cover border-2 border-white/20 flex-shrink-0" />
              : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-2xl font-black text-white flex-shrink-0">{(reg.name || '?')[0]}</div>}
            <div>
              <p className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Profile</p>
              <h3 className="text-lg font-black text-white">{reg.name}</h3>
              <p className="text-xs text-yellow-300">{reg.registration_number} · {reg.expecting_job}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Info grid */}
          <div className="grid grid-cols-2 gap-2">
            {infoFields.map(([l, v], i) => (
              <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400">{l}</p>
                <p className="mt-0.5 text-sm font-semibold text-gray-800">{v || <span className="text-gray-400">—</span>}</p>
              </div>
            ))}
          </div>

          {/* Experience */}
          {exp.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-400">Experience</p>
              <div className="space-y-2">
                {exp.map((e, i) => (
                  <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-sm space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-gray-800">{e.title_post || '—'}</span>
                      {e.exp_department && <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">{e.exp_department}</span>}
                    </div>
                    <div className="text-gray-600">{e.company_name || '—'}{e.location ? ` · ${e.location}` : ''}</div>
                    <div className="flex gap-3 text-xs">
                      {e.salary && <span className="text-emerald-600 font-semibold">{e.salary}</span>}
                      {e.total_years && <span className="text-gray-500">{e.total_years} yrs</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resume & Photo links */}
          <div className="flex flex-wrap gap-3">
            {resumeUrl
              ? <a href={resumeUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2 text-sm font-bold text-yellow-400 hover:bg-gray-900">📄 View Resume ↗</a>
              : <span className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-400">📄 No Resume</span>}
            {photoUrl
              ? <a href={photoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2 text-sm font-bold text-yellow-400 hover:bg-gray-900">🖼 View Photo ↗</a>
              : <span className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-400">🖼 No Photo</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Availability Request Modal ───────────────────────────────────────────────
const AvailabilityRequestModal = ({ reg, vacancies, onDone, onClose }) => {
  const [vacId, setVacId] = useState(reg.selected_job_id ? String(reg.selected_job_id) : '');
  const [vacSearch, setVacSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const openVacs = useMemo(() => {
    const base = vacancies.filter((v) => !Number(v.is_filled));
    if (!vacSearch.trim()) return base;
    const q = vacSearch.toLowerCase();
    return base.filter((v) => `${v.title} ${v.company} ${v.department}`.toLowerCase().includes(q));
  }, [vacancies, vacSearch]);

  const selectedVac = vacancies.find((v) => String(v.id) === vacId) || null;

  const handleSend = async () => {
    setSaving(true);
    try {
      await putWorkflow(reg.id, reg, {
        selected_job_id: vacId ? Number(vacId) : (reg.selected_job_id || null),
        is_shortlisted: 1,
        candidate_availability: 'Pending',
        vacancy_availability: 'Pending',
        hr_assistant_confirmation: 'Pending',
        assistant_recruiter_confirmation: 'Pending',
      });
      onDone();
    } catch { alert('Failed to send availability check.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-16">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl bg-black px-6 py-5">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Check Availability</p>
            <h3 className="text-lg font-black text-white">{reg.name}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>
        <div className="p-5 space-y-5">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            This will shortlist the candidate and notify <strong>HR Assistant</strong> to confirm member availability and <strong>Assistant Recruiter</strong> to confirm company vacancy availability.
          </div>
          <div>
            <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">Link to Vacancy</p>
            {selectedVac ? (
              <div className="flex items-center justify-between rounded-xl border border-amber-300 bg-amber-50 px-3 py-2">
                <div><p className="text-sm font-black text-amber-900">{selectedVac.title}</p><p className="text-xs text-amber-600">{selectedVac.company}</p></div>
                <button type="button" onClick={() => setVacId('')} className="text-xs text-amber-400 hover:text-red-500 font-bold">Change</button>
              </div>
            ) : (
              <div>
                <input type="text" value={vacSearch} onChange={(e) => setVacSearch(e.target.value)} placeholder="Search open vacancies…" className="mb-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                <div className="max-h-36 overflow-y-auto space-y-1 rounded-xl border border-gray-100 bg-gray-50 p-1">
                  <button type="button" onClick={() => setVacId('')} className="w-full rounded-lg px-3 py-1.5 text-left text-xs font-bold text-gray-500 hover:bg-white">No specific vacancy</button>
                  {openVacs.map((v) => (
                    <button key={v.id} type="button" onClick={() => setVacId(String(v.id))} className="w-full rounded-lg px-3 py-1.5 text-left text-xs hover:bg-white">
                      <span className="font-bold text-gray-800">{v.title}</span> <span className="text-gray-400">— {v.company}</span>{v.partner_org && <span className="ml-1 whitespace-nowrap rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-black text-black">🤝 {v.partner_org}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSend} disabled={saving} className="rounded-xl bg-black px-6 py-2 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60">{saving ? 'Sending…' : '📩 Send Availability Check'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Schedule Interview Modal ─────────────────────────────────────────────────
const ScheduleModal = ({ reg, vacancies, onDone, onClose }) => {
  const [vacId, setVacId] = useState(reg.selected_job_id ? String(reg.selected_job_id) : '');
  const [vacSearch, setVacSearch] = useState('');
  const [form, setForm] = useState({
    interview_date: reg.interview_date || '',
    interview_time: reg.interview_time || '',
    interview_location: reg.interview_location || '',
    interview_mode: reg.interview_mode || 'Direct',
    reporting_officer: reg.reporting_officer || '',
    interview_note: reg.interview_note || '',
  });
  const [saving, setSaving] = useState(false);

  const openVacs = useMemo(() => {
    const base = vacancies.filter((v) => !Number(v.is_filled));
    if (!vacSearch.trim()) return base;
    const q = vacSearch.toLowerCase();
    return base.filter((v) => `${v.title} ${v.company} ${v.department} ${v.location}`.toLowerCase().includes(q));
  }, [vacancies, vacSearch]);

  const selectedVac = vacancies.find((v) => String(v.id) === vacId) || null;

  const handleSave = async () => {
    if (!vacId) {
      alert('Please choose the vacancy this interview is for. It is needed to track the placement and any partner commission.');
      return;
    }
    if (!form.interview_date || !form.interview_time || !form.interview_location || !form.reporting_officer) {
      alert('Please fill Date, Time, Location and Reporting Officer.');
      return;
    }
    setSaving(true);
    try {
      await putWorkflow(reg.id, reg, {
        selected_job_id: vacId ? Number(vacId) : (reg.selected_job_id || null),
        is_shortlisted: 1,
        candidate_availability: reg.candidate_availability || 'Available',
        vacancy_availability: reg.vacancy_availability || 'Available',
        hr_assistant_confirmation: reg.hr_assistant_confirmation || 'Accepted',
        assistant_recruiter_confirmation: reg.assistant_recruiter_confirmation || 'Accepted',
        interview_date: form.interview_date,
        interview_time: form.interview_time,
        interview_location: form.interview_location,
        interview_mode: form.interview_mode,
        reporting_officer: form.reporting_officer,
        interview_status: 'Scheduled',
        interview_note: form.interview_note,
      });
      await axios.post(
        `${API_URL}/api/staff/recruiter/registrations/${reg.id}/interview-events`,
        {
          vacancy_id: vacId ? Number(vacId) : null,
          interview_date: form.interview_date,
          interview_time: form.interview_time,
          interview_location: form.interview_location,
          interview_mode: form.interview_mode,
          reporting_officer: form.reporting_officer,
          candidate_status: 'Pending',
          outcome: 'Pending',
          recruiter_notes: form.interview_note,
        },
        { headers: authHeader() }
      );
      onDone();
    } catch { alert('Failed to schedule interview.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-8">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl bg-black px-6 py-5">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Schedule Interview</p>
            <h3 className="text-lg font-black text-white">{reg.name}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">Vacancy * <span className="normal-case tracking-normal font-bold text-gray-400">(needed to track the placement and any partner commission)</span></p>
            {selectedVac ? (
              <div className="flex items-center justify-between rounded-xl border border-indigo-300 bg-indigo-50 px-3 py-2">
                <div><p className="text-sm font-black text-indigo-900">{selectedVac.title}</p><p className="text-xs text-indigo-600">{selectedVac.company} · {selectedVac.location}</p></div>
                <button type="button" onClick={() => setVacId('')} className="text-xs text-indigo-400 hover:text-red-500 font-bold">Change</button>
              </div>
            ) : (
              <div>
                <input type="text" value={vacSearch} onChange={(e) => setVacSearch(e.target.value)} placeholder="Search open vacancies…" className="mb-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                <div className="max-h-36 overflow-y-auto space-y-1 rounded-xl border border-gray-100 bg-gray-50 p-1">
                  {openVacs.map((v) => (
                    <button key={v.id} type="button" onClick={() => setVacId(String(v.id))} className="w-full rounded-lg px-3 py-1.5 text-left text-xs hover:bg-white">
                      <span className="font-bold text-gray-800">{v.title}</span> <span className="text-gray-400">— {v.company}</span>{v.partner_org && <span className="ml-1 whitespace-nowrap rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-black text-black">🤝 {v.partner_org}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Date *</span><input type="date" value={form.interview_date} onChange={(e) => setForm((f) => ({ ...f, interview_date: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Time *</span><input type="time" value={form.interview_time} onChange={(e) => setForm((f) => ({ ...f, interview_time: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Location *</span><input value={form.interview_location} onChange={(e) => setForm((f) => ({ ...f, interview_location: e.target.value }))} placeholder="Venue / link" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Mode</span><select value={form.interview_mode} onChange={(e) => setForm((f) => ({ ...f, interview_mode: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">{INTERVIEW_MODES.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
            <label className="block sm:col-span-2"><span className="text-xs font-extrabold uppercase text-gray-500">Reporting Officer *</span><input value={form.reporting_officer} onChange={(e) => setForm((f) => ({ ...f, reporting_officer: e.target.value }))} placeholder="Manager name" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block sm:col-span-2"><span className="text-xs font-extrabold uppercase text-gray-500">Notes</span><input value={form.interview_note} onChange={(e) => setForm((f) => ({ ...f, interview_note: e.target.value }))} placeholder="Special instructions…" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-black px-6 py-2 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60">{saving ? 'Scheduling…' : 'Confirm Schedule'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Outcome Modal ────────────────────────────────────────────────────────────
const OutcomeModal = ({ reg, onDone, onClose }) => {
  const [status, setStatus] = useState(reg.interview_status || 'Scheduled');
  const [note, setNote] = useState(reg.interview_note || '');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try { await putWorkflow(reg.id, reg, { interview_status: status, interview_note: note }); onDone(); }
    catch { alert('Failed to update status.'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl bg-black px-5 py-4">
          <div><p className="text-xs font-extrabold uppercase text-yellow-400">Update Result</p><h3 className="font-black text-white">{reg.name}</h3></div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Interview Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
              {INTERVIEW_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Note</span><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional…" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-black px-6 py-2 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Offer Modal ──────────────────────────────────────────────────────────────
const OfferModal = ({ reg, onDone, onClose }) => {
  const [form, setForm] = useState({
    offered_job_title: reg.offered_job_title || reg.selected_job_title || '',
    offered_salary: reg.offered_salary || '',
    joining_date: reg.joining_date || '',
    work_location: reg.work_location || '',
    probation_period: reg.probation_period || '',
    work_schedule: reg.work_schedule || '',
    other_terms: reg.other_terms || '',
    leave_days: reg.leave_days || '',
    agreement_execution: reg.agreement_execution || 'No',
  });
  const [saving, setSaving] = useState(false);
  const f = (k) => ({ value: form[k], onChange: (e) => setForm((p) => ({ ...p, [k]: e.target.value })) });

  const handleSave = async () => {
    setSaving(true);
    try { await putWorkflow(reg.id, reg, { ...form }); onDone(); }
    catch { alert('Failed to save offer terms.'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-8">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl bg-black px-6 py-5">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Offer Terms</p>
            <h3 className="text-lg font-black text-white">{reg.name}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Offered Job Title</span><input {...f('offered_job_title')} placeholder="e.g. Senior Accountant" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Salary / Package</span><input {...f('offered_salary')} placeholder="e.g. ₹32,000/month" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Joining Date</span><input type="date" {...f('joining_date')} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Work Location</span><input {...f('work_location')} placeholder="Office / city" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Probation Period</span><input {...f('probation_period')} placeholder="e.g. 3 months" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Work Schedule</span><input {...f('work_schedule')} placeholder="Mon–Sat 9am–6pm" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Leave Days</span><input {...f('leave_days')} placeholder="e.g. 15 days/year" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
            <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Agreement Execution</span><select {...f('agreement_execution')} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"><option value="No">No</option><option value="Yes">Yes</option></select></label>
            <label className="block sm:col-span-2"><span className="text-xs font-extrabold uppercase text-gray-500">Other Terms</span><textarea {...f('other_terms')} rows={3} placeholder="Notice period, accommodation, transport, uniform…" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-black px-6 py-2 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60">{saving ? 'Saving…' : 'Save Offer Terms'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Charge Modal ─────────────────────────────────────────────────────────────
const ChargeModal = ({ reg, onDone, onClose }) => {
  const [amount, setAmount] = useState(reg.service_charge_amount || '');
  const [status, setStatus] = useState(reg.service_charge_status || 'Pending');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!amount.trim()) { alert('Enter the service charge amount.'); return; }
    setSaving(true);
    try { await putWorkflow(reg.id, reg, { service_charge_amount: amount, service_charge_status: status }); onDone(); }
    catch { alert('Failed to save.'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl bg-black px-5 py-4">
          <div><p className="text-xs font-extrabold uppercase text-yellow-400">Service Charge</p><h3 className="font-black text-white">{reg.name}</h3></div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Amount *</span><input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. ₹5,000 or 1 month salary" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" /></label>
          <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
              {['Pending', 'Sent', 'Paid'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-black px-6 py-2 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Resign Modal ─────────────────────────────────────────────────────────────
const RESIGN_REASONS = ['Not happy with the job', 'Salary issue', 'Location / travel', 'Work environment', 'Company terminated', 'Personal reasons', 'Got another job', 'Other'];
const ResignModal = ({ reg, jobText, onDone, onClose }) => {
  const [reason, setReason] = useState('');
  const [lastDay, setLastDay] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const handleSave = async () => {
    if (!reason) { setError('Choose a reason.'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API_URL}/api/staff/recruiter/registrations/${reg.id}/resign`, { reason, last_working_day: lastDay, note }, { headers: authHeader() });
      onDone();
    } catch (err) { setError(err?.response?.data?.error || 'Failed to mark as resigned.'); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl bg-black px-5 py-4">
          <div><p className="text-xs font-extrabold uppercase text-yellow-400">Mark Resigned</p><h3 className="font-black text-white">{reg.name}</h3></div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-xs text-amber-800 ring-1 ring-amber-200">
            {reg.name} will leave <b>{jobText}</b> and come back to your pipeline as a fresh candidate, ready for a new job. The opening at that company becomes available again. This placement is kept in the member's history.
          </div>
          <div>
            <span className="text-xs font-extrabold uppercase text-gray-500">Why did they resign?</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {RESIGN_REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)}
                  className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${reason === r ? 'bg-black text-yellow-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{r}</button>
              ))}
            </div>
          </div>
          <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Last working day</span>
            <input type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </label>
          <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">Note</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional — what happened, what they want next…" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </label>
          {error && <p className="text-xs font-bold text-red-600">{error}</p>}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-black px-6 py-2 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60">{saving ? 'Saving…' : '↩ Mark Resigned'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Earlier placements the member resigned from — loaded on demand.
const PastPlacements = ({ reg }) => {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/staff/recruiter/registrations/${reg.id}/placement-history`, { headers: authHeader() })
      .then((r) => { if (!cancelled) setHistory(r.data?.history || []); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [reg.id, reg.past_placements]);
  if (!history || history.length === 0) return null;
  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/60 overflow-hidden">
      <div className="px-4 py-2 border-b border-orange-100"><p className="text-[10px] font-black uppercase tracking-widest text-orange-700">↩ Previous placements</p></div>
      <div className="divide-y divide-orange-100">
        {history.map((h) => (
          <div key={h.id} className="px-4 py-2.5 text-xs">
            <p className="font-bold text-gray-800">{h.job_title || 'Job'}{h.company ? ` @ ${h.company}` : ''}</p>
            <p className="text-gray-500 mt-0.5">
              {h.placed_at ? `Placed ${fmtDate(h.placed_at)}` : 'Placed'} · Resigned {h.last_working_day ? fmtDate(h.last_working_day) : fmtDate(h.resigned_at)} · <span className="font-semibold text-orange-700">{h.resign_reason}</span>
            </p>
            {h.resign_note && <p className="text-gray-500 italic mt-0.5">“{h.resign_note}”</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Member Card ──────────────────────────────────────────────────────────────
const MemberCard = ({ reg, vacancies, onRefresh, groupMode = false, isGroupSelected = false, onToggleGroupSelect = null, detailMode = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [modal, setModal] = useState(null);
  const [events, setEvents] = useState(null);
  const [sendingToHr, setSendingToHr] = useState(false);
  const [sendingResend, setSendingResend] = useState(false);
  const [deletingReg, setDeletingReg] = useState(false);

  const vac = reg.selected_job_id ? vacancies.find((v) => String(v.id) === String(reg.selected_job_id)) : null;
  const isAvailPending = Number(reg.is_shortlisted) > 0 && (reg.interview_status === 'Not Scheduled' || reg.interview_status === 'Shortlisted' || !reg.interview_status);
  const allConfirmed = reg.candidate_availability === 'Available' && reg.vacancy_availability === 'Available' && reg.hr_assistant_confirmation === 'Accepted' && reg.assistant_recruiter_confirmation === 'Accepted';
  const anyBlocked = reg.candidate_availability === 'Not Available' || reg.vacancy_availability === 'Not Available' || reg.hr_assistant_confirmation === 'Rejected' || reg.assistant_recruiter_confirmation === 'Rejected';
  const isSelected = reg.interview_status === 'Selected';
  const isPlaced = Number(reg.payment_received) > 0 || reg.interview_status === 'Placed';

  const fetchHistory = async () => {
    if (events !== null) return;
    try {
      const r = await axios.get(`${API_URL}/api/staff/recruiter/registrations/${reg.id}/interview-events`, { headers: authHeader() });
      setEvents(r.data?.events || []);
    } catch { setEvents([]); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${reg.name} from your dashboard?`)) return;
    setDeletingReg(true);
    try { await axios.delete(`${API_URL}/api/staff/recruiter/registrations/${reg.id}`, { headers: authHeader() }); onRefresh(); }
    catch { alert('Failed to remove.'); setDeletingReg(false); }
  };

  const handleSendToHr = async () => {
    if (!window.confirm(`Send offer for ${reg.name} to HR?`)) return;
    setSendingToHr(true);
    try { await putWorkflow(reg.id, reg, { offer_sent_to_hr: 1 }); onRefresh(); }
    catch { alert('Failed to send.'); } finally { setSendingToHr(false); }
  };

  const handleResendToHr = async () => {
    if (!window.confirm(`Re-send updated offer for ${reg.name} to HR? This will reset the delivery status so HR can re-deliver the updated offer to the member.`)) return;
    setSendingResend(true);
    try {
      await axios.put(`${API_URL}/api/staff/recruiter/registrations/${reg.id}/offer-resend`, {}, { headers: authHeader() });
      onRefresh();
    } catch { alert('Failed to re-send offer.'); } finally { setSendingResend(false); }
  };

  const handleEventOutcome = async (eventId, outcome) => {
    try {
      const r = await axios.put(`${API_URL}/api/staff/recruiter/registrations/${reg.id}/interview-events/${eventId}`, { outcome }, { headers: authHeader() });
      setEvents((prev) => (prev || []).map((e) => (e.id === eventId ? r.data.event : e)));
    } catch (err) { alert(err?.response?.data?.error || 'Failed to update interview result.'); }
  };

  const reloadHistory = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/staff/recruiter/registrations/${reg.id}/interview-events`, { headers: authHeader() });
      setEvents(r.data?.events || []);
    } catch { /* keep what is shown */ }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this interview event? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/staff/recruiter/registrations/${reg.id}/interview-events/${eventId}`, { headers: authHeader() });
      setEvents((prev) => (prev || []).filter((e) => e.id !== eventId));
      onRefresh();
    } catch { alert('Failed to delete interview event.'); }
  };

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && events === null) fetchHistory();
  };

  useEffect(() => {
    if (detailMode && events === null) fetchHistory();
  }, [detailMode]); // eslint-disable-line

  // Left border accent based on status
  const cardAccent = isPlaced ? 'border-l-black' :
    isSelected ? 'border-l-emerald-500' :
    reg.interview_status === 'Scheduled' ? 'border-l-blue-500' :
    ['Waiting for the result', 'Shortlisted', 'Shortlisted for next level', 'Rejected', 'No Show'].includes(reg.interview_status) ? 'border-l-cyan-400' :
    allConfirmed ? 'border-l-yellow-400' :
    isAvailPending ? 'border-l-amber-400' :
    anyBlocked ? 'border-l-red-400' : 'border-l-gray-200';

  return (
    <>
      <div className={`rounded-2xl border bg-white shadow-sm overflow-hidden border-l-4 ${cardAccent} hover:shadow-md transition-shadow ${isGroupSelected ? 'border-black ring-2 ring-black ring-offset-1' : 'border-gray-200'}`}>
        {/* ── Collapsed header ── */}
        <button type="button" onClick={detailMode ? undefined : (groupMode ? onToggleGroupSelect : handleExpand)} className={`w-full text-left px-4 py-3.5${detailMode ? ' cursor-default' : ''}`}>
          <div className="flex items-center gap-3">
            {groupMode && (
              <div className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2 transition-all ${isGroupSelected ? 'border-black bg-black text-white' : 'border-gray-300 bg-white'}`}>
                {isGroupSelected && <span className="text-xs font-black leading-none">✓</span>}
              </div>
            )}
            {reg.photo_file
              ? <img src={reg.photo_file.startsWith('http') ? reg.photo_file : `${API_URL}${reg.photo_file}`} alt={reg.name} className="h-10 w-10 flex-shrink-0 rounded-xl object-cover" />
              : <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-black text-sm font-black text-yellow-400">{(reg.name || '?')[0]}</div>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-black text-gray-900 text-sm">{reg.name}</p>
                <span className="text-[11px] text-gray-400 font-medium">{reg.registration_number}</span>
                {reg.plan_type === 'prime' && <span className="rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-black text-black leading-none">👑 PRIME</span>}
                <PartnerTag row={reg} />
                {isPlaced && <span className="rounded-full bg-black px-2 py-0.5 text-[10px] font-extrabold text-yellow-400">🏆 PLACED</span>}
                {!isPlaced && Number(reg.past_placements) > 0 && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-extrabold text-orange-700" title={reg.placement_note || ''}>↩ RESIGNED · RE-PLACING</span>}
                {!isPlaced && reg.interview_status && reg.interview_status !== 'Not Scheduled' && (
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${iBadge(reg.interview_status)}`}>{reg.interview_status}</span>
                )}
                {allConfirmed && isAvailPending && <span className="rounded-full border border-yellow-300 bg-yellow-100 px-2 py-0.5 text-[10px] font-extrabold text-yellow-800">✅ READY</span>}
                {isAvailPending && !allConfirmed && !anyBlocked && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold text-amber-700">⏳ AWAITING</span>}
                {anyBlocked && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-extrabold text-red-700">⚠ BLOCKED</span>}
              </div>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                <p className="text-xs text-gray-600 font-semibold">{vac ? `${vac.title} @ ${vac.company}` : reg.expecting_job}</p>
                {reg.interview_date && <span className="text-xs text-gray-400">📅 {reg.interview_date}{reg.interview_time ? ` ${reg.interview_time}` : ''}</span>}
              </div>
            </div>
            {!detailMode && <span className="text-gray-400 text-sm flex-shrink-0 font-bold">{expanded ? '▲' : '▼'}</span>}
          </div>
        </button>

        {/* ── Expanded section ── */}
        {(expanded || detailMode) && (
          <div className="border-t border-gray-100 bg-gray-50/40 px-4 pb-4 pt-3">
            <div className="flex gap-4 items-start">

              {/* ── LEFT: Member profile & info ── */}
              <div className="w-52 flex-shrink-0 space-y-3">

                {/* Profile card */}
                <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-col items-center text-center gap-2">
                  {reg.photo_file
                    ? <img src={reg.photo_file.startsWith('http') ? reg.photo_file : `${API_URL}${reg.photo_file}`} alt={reg.name} className="h-16 w-16 rounded-xl object-cover border border-gray-200" />
                    : <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-black text-xl font-black text-yellow-400">{(reg.name || '?')[0]}</div>}
                  <div>
                    <p className="font-black text-gray-900 text-sm leading-snug">{reg.name}</p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">{reg.registration_number}</p>
                    {reg.plan_type === 'prime' && <span className="mt-1 inline-block rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-black text-black leading-none">👑 PRIME</span>}
                    <PartnerTag row={reg} />
                  </div>
                  {vac && (
                    <div className="w-full rounded-lg bg-gray-50 border border-gray-100 px-2 py-1.5 text-[10px] text-gray-600 font-semibold leading-snug">
                      {vac.title} @ {vac.company}
                    </div>
                  )}
                  {!vac && reg.expecting_job && (
                    <div className="w-full rounded-lg bg-gray-50 border border-gray-100 px-2 py-1.5 text-[10px] text-gray-500 leading-snug">
                      {reg.expecting_job}
                    </div>
                  )}
                </div>

                {/* Contact */}
                <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-1.5">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Contact</p>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-400 shrink-0">📞</span>
                    <ContactLink value={reg.contact_number} />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-400 shrink-0">💬</span>
                    <ContactLink value={reg.whatsapp_number} type="whatsapp" />
                  </div>
                  {reg.district && <p className="text-xs text-gray-500">📍 {reg.district}</p>}
                  {reg.department && <p className="text-xs text-gray-500">🏢 {reg.department}</p>}
                </div>

                {/* Availability confirmations */}
                {isAvailPending && (
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Availability</p>
                    <div className="space-y-1.5">
                      {[
                        ['Member', reg.candidate_availability, 'HR Asst.'],
                        ['Company', reg.vacancy_availability, 'AR'],
                        ['HR Confirm', reg.hr_assistant_confirmation, 'HR Asst.'],
                        ['AR Confirm', reg.assistant_recruiter_confirmation, 'AR'],
                      ].map(([label, val, by]) => (
                        <div key={label} className={`rounded-lg px-2 py-1.5 text-xs border ${avChip(val)} ${val === 'Available' || val === 'Accepted' ? 'border-emerald-200' : val === 'Not Available' || val === 'Rejected' ? 'border-red-200' : 'border-gray-200'}`}>
                          <div className="flex items-center justify-between gap-1">
                            <p className="font-extrabold text-[11px]">{label}</p>
                            <p className="font-bold text-[10px]">{avIcon(val)} {val || 'Pending'}</p>
                          </div>
                          <p className="italic opacity-50 text-[9px] mt-0.5">{by}</p>
                        </div>
                      ))}
                    </div>
                    {reg.candidate_availability_note && <p className="mt-2 text-[10px] text-gray-600 italic">HR: {reg.candidate_availability_note}</p>}
                    {reg.vacancy_availability_note && <p className="mt-0.5 text-[10px] text-gray-600 italic">AR: {reg.vacancy_availability_note}</p>}
                  </div>
                )}

                {/* Offer terms */}
                {isSelected && (reg.offered_salary || reg.offered_job_title) && (
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    <div className="flex items-center justify-between gap-1 mb-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Offer Terms</p>
                      {!Number(reg.offer_sent_to_hr)
                        ? <button type="button" onClick={handleSendToHr} disabled={sendingToHr} className="rounded-lg bg-black px-2 py-0.5 text-[10px] font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60">{sendingToHr ? '…' : '📨 Send to HR'}</button>
                        : <div className="flex items-center gap-1.5">
                            <span className="text-[9px] font-extrabold text-emerald-600">✓ Sent to HR</span>
                            <button type="button" onClick={handleResendToHr} disabled={sendingResend} className="rounded-lg border border-amber-400 bg-amber-50 px-2 py-0.5 text-[9px] font-extrabold text-amber-700 hover:bg-amber-100 disabled:opacity-60">{sendingResend ? '…' : '🔄 Re-send'}</button>
                          </div>}
                    </div>
                    <div className="space-y-1 text-xs">
                      {reg.offered_job_title && <p><span className="font-bold text-gray-400">Title:</span> <span className="text-gray-800">{reg.offered_job_title}</span></p>}
                      {reg.offered_salary && <p><span className="font-bold text-gray-400">Salary:</span> <span className="font-black text-black">{reg.offered_salary}</span></p>}
                      {reg.joining_date && <p><span className="font-bold text-gray-400">Joining:</span> {reg.joining_date}</p>}
                      {reg.work_location && <p><span className="font-bold text-gray-400">Location:</span> {reg.work_location}</p>}
                      {reg.probation_period && <p><span className="font-bold text-gray-400">Probation:</span> {reg.probation_period}</p>}
                      {reg.work_schedule && <p><span className="font-bold text-gray-400">Schedule:</span> {reg.work_schedule}</p>}
                      {reg.leave_days && <p><span className="font-bold text-gray-400">Leave:</span> {reg.leave_days}</p>}
                      {reg.agreement_execution && <p><span className="font-bold text-gray-400">Agreement:</span> <span className={reg.agreement_execution === 'Yes' ? 'font-black text-black' : ''}>{reg.agreement_execution}</span></p>}
                      {reg.other_terms && <p><span className="font-bold text-gray-400">Terms:</span> {reg.other_terms}</p>}
                    </div>
                  </div>
                )}

                {/* Service charge */}
                {isSelected && reg.service_charge_amount && (
                  <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2.5">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Service Charge</p>
                      <p className="mt-0.5 font-black text-sm text-gray-900">
                        {reg.service_charge_amount}
                        <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold ${reg.service_charge_status === 'Paid' ? 'bg-black text-yellow-400' : reg.service_charge_status === 'Sent' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{reg.service_charge_status || 'Pending'}</span>
                      </p>
                    </div>
                    <button type="button" onClick={() => setModal('charge')} className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-bold text-gray-700 hover:bg-gray-50">Edit</button>
                  </div>
                )}

              </div>
              {/* ── RIGHT: Pipeline ── */}
              <div className="flex-1 min-w-0 space-y-3">

                {Number(reg.past_placements) > 0 && <PastPlacements reg={reg} />}

                {/* ── Placed: full placement history ── */}
                {isPlaced ? (
                  <div className="space-y-3">
                    {/* Completion banner */}
                    <div className="flex items-center gap-3 rounded-xl bg-black px-4 py-3">
                      <span className="text-xl">🏆</span>
                      <div className="flex-1">
                        <p className="text-sm font-black text-yellow-400">Placement Complete</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {reg.offered_job_title ? `${reg.offered_job_title}${vac?.company ? ` @ ${vac.company}` : ''}` : 'Placement confirmed'}
                        </p>
                      </div>
                      {reg.placed_at && <span className="text-xs text-gray-500">{fmtDate(reg.placed_at)}</span>}
                      <button type="button" onClick={() => setModal('resign')} className="rounded-xl border border-orange-300/40 bg-orange-500/15 px-3 py-1.5 text-xs font-extrabold text-orange-200 hover:bg-orange-500/30 transition">↩ Mark Resigned</button>
                    </div>

                    {/* Step-by-step history timeline */}
                    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                      <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Placement History</p>
                      </div>
                      <div className="px-4 py-3 space-y-0">
                        {[
                          {
                            icon: '📋',
                            label: 'Registered',
                            detail: reg.registration_number ? `#${reg.registration_number}` : null,
                            date: reg.registration_created_at,
                            done: true,
                          },
                          {
                            icon: '📤',
                            label: 'Forwarded to Recruiter',
                            detail: reg.forwarded_to_name || null,
                            date: reg.forwarded_at,
                            done: true,
                          },
                          {
                            icon: '🔍',
                            label: 'Shortlisted for Vacancy',
                            detail: vac ? `${vac.title} @ ${vac.company}` : reg.selected_job_title ? `${reg.selected_job_title}${reg.selected_job_company ? ` @ ${reg.selected_job_company}` : ''}` : null,
                            date: reg.shortlisted_at,
                            done: Number(reg.is_shortlisted) > 0,
                          },
                          {
                            icon: '✅',
                            label: 'Availability Confirmed',
                            detail: 'Member + Company + HR + AR confirmed',
                            date: reg.hr_assistant_confirmation === 'Accepted' ? (reg.candidate_availability_updated_at || reg.vacancy_availability_updated_at) : null,
                            done: allConfirmed,
                          },
                          ...(events || []).map((ev) => ({
                            icon: ev.outcome === 'Selected' ? '🎯' : ev.outcome === 'Rejected' ? '❌' : ev.outcome === 'No Show' ? '🚫' : '📅',
                            label: `Interview — ${interviewEventLabel(ev)}${ev.vacancy_company ? ` · ${ev.vacancy_company}` : ''}`,
                            detail: [ev.interview_location, ev.interview_mode, ev.reporting_officer].filter(Boolean).join(' · ') || null,
                            date: ev.interview_date ? `${ev.interview_date}${ev.interview_time ? ` ${ev.interview_time}` : ''}` : ev.created_at,
                            done: true,
                            isRaw: !!ev.interview_date,
                            note: ev.recruiter_notes || null,
                          })),
                          {
                            icon: '📄',
                            label: 'Offer Terms Finalised',
                            detail: [
                              reg.offered_job_title,
                              reg.offered_salary ? `₹${reg.offered_salary}` : null,
                              reg.joining_date ? `Joining: ${reg.joining_date}` : null,
                            ].filter(Boolean).join(' · ') || null,
                            date: reg.offer_sent_to_hr_at,
                            done: Number(reg.offer_sent_to_hr) > 0,
                          },
                          {
                            icon: reg.member_offer_response === 'Accepted' ? '🤝' : reg.member_offer_response === 'Rejected' ? '❌' : '⏳',
                            label: `Member ${reg.member_offer_response === 'Accepted' ? 'Accepted Offer' : reg.member_offer_response === 'Rejected' ? 'Rejected Offer' : 'Offer Response Pending'}`,
                            detail: null,
                            date: null,
                            done: reg.member_offer_response === 'Accepted',
                            failed: reg.member_offer_response === 'Rejected',
                          },
                          {
                            icon: '💰',
                            label: 'Service Charge Paid',
                            detail: reg.service_charge_amount ? `₹${reg.service_charge_amount}` : null,
                            date: reg.placed_at,
                            done: reg.service_charge_status === 'Paid' || isPlaced,
                            highlight: true,
                          },
                          {
                            icon: '🏆',
                            label: 'Placement Complete',
                            detail: reg.placement_note || null,
                            date: reg.placed_at,
                            done: true,
                            highlight: true,
                          },
                        ].filter((step) => step.done || step.failed).map((step, i, arr) => (
                          <div key={i} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black border-2 ${step.failed ? 'border-red-300 bg-red-50 text-red-600' : step.highlight ? 'border-black bg-black text-yellow-400' : 'border-emerald-300 bg-emerald-50 text-emerald-700'}`}>
                                {step.icon}
                              </div>
                              {i < arr.length - 1 && <div className="w-0.5 flex-1 min-h-[12px] bg-gray-200 my-0.5" />}
                            </div>
                            <div className="pb-3 flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <p className={`text-xs font-extrabold leading-snug ${step.failed ? 'text-red-600' : step.highlight ? 'text-black' : 'text-gray-800'}`}>{step.label}</p>
                                {step.date && (
                                  <span className="text-[10px] text-gray-400 whitespace-nowrap shrink-0">
                                    {step.isRaw ? step.date : fmtDate(step.date)}
                                  </span>
                                )}
                              </div>
                              {step.detail && <p className={`text-[11px] mt-0.5 ${step.highlight ? 'font-black text-emerald-700' : 'text-gray-500'}`}>{step.detail}</p>}
                              {step.note && <p className="text-[11px] italic text-gray-400 mt-0.5">"{step.note}"</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── Not yet placed: pipeline track ── */
                  (Number(reg.is_shortlisted) > 0 || (reg.interview_status && reg.interview_status !== 'Not Scheduled')) && (
                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400">Placement Track</p>
                      <div className="flex items-center overflow-x-auto pb-1">
                        {[
                          { label: 'Shortlisted', done: Number(reg.is_shortlisted) > 0 },
                          { label: 'Confirmed', done: allConfirmed },
                          { label: 'Scheduled', done: ['Scheduled', 'Selected', 'Rejected', 'Shortlisted', 'Shortlisted for next level', 'Waiting for the result', 'No Show', 'Placed'].includes(reg.interview_status) },
                          { label: 'Result', done: ['Selected', 'Shortlisted', 'Shortlisted for next level', 'Placed'].includes(reg.interview_status), failed: reg.interview_status === 'Rejected', skip: !reg.interview_status || reg.interview_status === 'Not Scheduled' },
                          { label: 'Offer→HR', done: Number(reg.offer_sent_to_hr) > 0, skip: !isSelected && !isPlaced },
                          { label: 'Accepted', done: reg.member_offer_response === 'Accepted', failed: reg.member_offer_response === 'Rejected', skip: !isSelected && !isPlaced },
                          { label: 'Paid', done: reg.service_charge_status === 'Paid' || isPlaced, skip: !isSelected && !isPlaced },
                          { label: '🏆', done: isPlaced, skip: !isSelected && !isPlaced },
                        ].filter((s) => !s.skip).map((stage, i, arr) => (
                          <React.Fragment key={i}>
                            <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-black border-2 ${stage.done ? (stage.label === '🏆' ? 'bg-yellow-400 border-yellow-400 text-black' : 'bg-black border-black text-white') : stage.failed ? 'bg-red-500 border-red-400 text-white' : 'bg-white border-gray-300 text-gray-300'}`}>
                                {stage.done ? (stage.label === '🏆' ? '🏆' : '✓') : stage.failed ? '✗' : i + 1}
                              </div>
                              <span className={`text-[9px] font-bold whitespace-nowrap ${stage.done ? 'text-gray-800' : stage.failed ? 'text-red-500' : 'text-gray-400'}`}>{stage.label}</span>
                            </div>
                            {i < arr.length - 1 && <div className={`h-0.5 w-6 flex-shrink-0 self-center mb-3.5 ${stage.done ? 'bg-black' : 'bg-gray-200'}`} />}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )
                )}

                {/* Interview history */}
                {events !== null && events.length > 0 && (
                  <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Interview History</p>
                    </div>
                    <div className="px-3 py-2 space-y-1.5">
                      {events.map((ev) => (
                        <div key={ev.id} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-xs">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-black text-gray-900">{ev.interview_date || '—'} {ev.interview_time}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${oBadge(interviewEventLabel(ev))}`}>{interviewEventLabel(ev)}</span>
                            {ev.vacancy_company && <span className="font-bold text-gray-600">{ev.vacancy_title ? `${ev.vacancy_title} @ ` : ''}{ev.vacancy_company}</span>}
                            {ev.interview_mode && <span className="text-gray-400">{ev.interview_mode}</span>}
                            {ev.candidate_status && ev.candidate_status !== 'Pending' && <span className="text-gray-400">🧑 {ev.candidate_status}</span>}
                            <select
                              value={ev.outcome || 'Pending'}
                              onChange={(e) => handleEventOutcome(ev.id, e.target.value)}
                              title="Set the result of this interview"
                              className="ml-auto rounded-lg border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-gray-700"
                            >
                              {INTERVIEW_EVENT_OUTCOMES.map((o) => <option key={o} value={o}>{o === 'Pending' ? 'Result: not set' : o}</option>)}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleDeleteEvent(ev.id)}
                              title="Delete this event"
                              className="rounded-lg border border-red-100 bg-white px-2 py-0.5 text-[10px] font-bold text-red-400 hover:bg-red-50 hover:text-red-600 flex-shrink-0"
                            >🗑</button>
                          </div>
                          {ev.interview_location && <p className="mt-0.5 text-gray-500">📍 {ev.interview_location}{ev.reporting_officer ? ` · ${ev.reporting_officer}` : ''}</p>}
                          {ev.recruiter_notes && <p className="mt-0.5 italic text-gray-400">"{ev.recruiter_notes}"</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                  <button type="button" onClick={() => setModal('profile')} className="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50 transition">👤 Profile</button>

                  {!Number(reg.is_shortlisted) && !isPlaced && (
                    <button type="button" onClick={() => setModal('avail')} className="rounded-xl bg-black px-3 py-1.5 text-xs font-extrabold text-yellow-400 hover:bg-gray-900 transition">📩 Check Availability</button>
                  )}

                  {isAvailPending && !allConfirmed && (
                    <button type="button" onClick={() => setModal('avail')} className="rounded-xl bg-black px-3 py-1.5 text-xs font-extrabold text-yellow-400 hover:bg-gray-900 transition">🔄 Resend Check</button>
                  )}

                  {!isPlaced && isSelected !== true && (allConfirmed || (!isAvailPending && reg.interview_status === 'Not Scheduled') || reg.interview_status === 'Scheduled') && (
                    <button type="button" onClick={() => setModal('schedule')} className="rounded-xl bg-black px-3 py-1.5 text-xs font-extrabold text-yellow-400 hover:bg-gray-900 transition">📅 {reg.interview_status === 'Scheduled' ? 'Re-Schedule' : 'Schedule Interview'}</button>
                  )}

                  {reg.interview_status && reg.interview_status !== 'Not Scheduled' && !isPlaced && (
                    <button type="button" onClick={() => setModal('outcome')} className="rounded-xl bg-black px-3 py-1.5 text-xs font-extrabold text-yellow-400 hover:bg-gray-900 transition">✏️ Update Result</button>
                  )}

                  {isSelected && !isPlaced && (
                    <button type="button" onClick={() => setModal('offer')} className="rounded-xl bg-black px-3 py-1.5 text-xs font-extrabold text-yellow-400 hover:bg-gray-900 transition">🏆 {reg.offered_job_title ? 'Edit Offer' : 'Enter Offer Terms'}</button>
                  )}

                  {isSelected && !isPlaced && (
                    <button type="button" onClick={() => setModal('charge')} className="rounded-xl bg-black px-3 py-1.5 text-xs font-extrabold text-yellow-400 hover:bg-gray-900 transition">💰 {reg.service_charge_amount ? 'Edit Charge' : 'Set Service Charge'}</button>
                  )}

                  {!isPlaced && (
                    <button type="button" onClick={handleDelete} disabled={deletingReg} className="rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 disabled:opacity-50 transition">🗑 Remove</button>
                  )}
                </div>

              </div>
            </div>
          </div>
        )}
      </div>

      {modal === 'profile' && <ProfileModal reg={reg} onClose={() => setModal(null)} />}
      {modal === 'avail' && <AvailabilityRequestModal reg={reg} vacancies={vacancies} onDone={() => { setModal(null); onRefresh(); }} onClose={() => setModal(null)} />}
      {modal === 'schedule' && <ScheduleModal reg={reg} vacancies={vacancies} onDone={() => { setModal(null); onRefresh(); }} onClose={() => setModal(null)} />}
      {modal === 'outcome' && <OutcomeModal reg={reg} onDone={() => { setModal(null); onRefresh(); if (events !== null) reloadHistory(); }} onClose={() => setModal(null)} />}
      {modal === 'offer' && <OfferModal reg={reg} onDone={() => { setModal(null); onRefresh(); }} onClose={() => setModal(null)} />}
      {modal === 'charge' && <ChargeModal reg={reg} onDone={() => { setModal(null); onRefresh(); }} onClose={() => setModal(null)} />}
      {modal === 'resign' && <ResignModal reg={reg} jobText={reg.offered_job_title || (vac ? `${vac.title} @ ${vac.company}` : 'the current job')} onDone={() => { setModal(null); onRefresh(); }} onClose={() => setModal(null)} />}
    </>
  );
};

// ─── Group Interview Modal ────────────────────────────────────────────────────
const GroupInterviewModal = ({ selectedRegs, vacancies, onDone, onClose, initialVacId = '' }) => {
  const [vacId, setVacId] = useState(initialVacId);
  const [vacSearch, setVacSearch] = useState('');
  const [form, setForm] = useState({
    interview_date: '',
    interview_time: '',
    interview_location: '',
    interview_mode: 'Direct',
    reporting_officer: '',
    interview_note: '',
  });
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);

  const openVacs = useMemo(() => {
    const base = vacancies.filter((v) => !Number(v.is_filled));
    if (!vacSearch.trim()) return base;
    const q = vacSearch.toLowerCase();
    return base.filter((v) => `${v.title} ${v.company} ${v.department}`.toLowerCase().includes(q));
  }, [vacancies, vacSearch]);

  const selectedVac = vacancies.find((v) => String(v.id) === vacId) || null;

  const handleSave = async () => {
    if (!vacId) {
      alert('Please choose the vacancy this interview is for. It is needed to track the placement and any partner commission.');
      return;
    }
    if (!form.interview_date || !form.interview_time || !form.interview_location || !form.reporting_officer) {
      alert('Please fill Date, Time, Location and Reporting Officer.');
      return;
    }
    setSaving(true);
    let done = 0;
    setProgress({ done: 0, total: selectedRegs.length });
    for (const reg of selectedRegs) {
      try {
        await putWorkflow(reg.id, reg, {
          selected_job_id: vacId ? Number(vacId) : (reg.selected_job_id || null),
          is_shortlisted: 1,
          candidate_availability: 'Available',
          vacancy_availability: 'Available',
          hr_assistant_confirmation: 'Accepted',
          assistant_recruiter_confirmation: 'Accepted',
          interview_date: form.interview_date,
          interview_time: form.interview_time,
          interview_location: form.interview_location,
          interview_mode: form.interview_mode,
          reporting_officer: form.reporting_officer,
          interview_status: 'Scheduled',
          interview_note: form.interview_note,
        });
        await axios.post(
          `${API_URL}/api/staff/recruiter/registrations/${reg.id}/interview-events`,
          {
            vacancy_id: vacId ? Number(vacId) : null,
            interview_date: form.interview_date,
            interview_time: form.interview_time,
            interview_location: form.interview_location,
            interview_mode: form.interview_mode,
            reporting_officer: form.reporting_officer,
            candidate_status: 'Pending',
            outcome: 'Pending',
            recruiter_notes: form.interview_note,
          },
          { headers: authHeader() }
        );
      } catch { /* continue with remaining candidates */ }
      done++;
      setProgress({ done, total: selectedRegs.length });
    }
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-10 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between rounded-t-3xl bg-black px-6 py-5">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Group Interview</p>
            <h3 className="mt-0.5 text-lg font-black text-white">Schedule one slot for {selectedRegs.length} candidates</h3>
            <p className="mt-1 text-xs text-gray-400">Same date, time, location will be applied to all selected candidates at once.</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Candidate chips */}
          <div>
            <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">Candidates in this group ({selectedRegs.length})</p>
            <div className="flex flex-wrap gap-2">
              {selectedRegs.map((r) => (
                <div key={r.id} className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-bold text-gray-800">
                  {r.photo_file
                    ? <img src={r.photo_file.startsWith('http') ? r.photo_file : `${API_URL}${r.photo_file}`} alt={r.name} className="h-5 w-5 rounded-full object-cover flex-shrink-0" />
                    : <div className="flex h-5 w-5 items-center justify-center rounded-full bg-black text-[9px] font-black text-yellow-400 flex-shrink-0">{(r.name || '?')[0]}</div>}
                  <span>{r.name}</span>
                  {r.interview_status && r.interview_status !== 'Not Scheduled' && (
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-extrabold ${iBadge(r.interview_status)}`}>{r.interview_status}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Vacancy picker */}
          <div>
            <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">Vacancy * <span className="normal-case tracking-normal font-bold text-gray-400">(applies to all selected candidates)</span></p>
            {selectedVac ? (
              <div className="flex items-center justify-between rounded-xl border border-indigo-300 bg-indigo-50 px-3 py-2.5">
                <div>
                  <p className="text-sm font-black text-indigo-900">{selectedVac.title}</p>
                  <p className="text-xs text-indigo-600">{selectedVac.company}{selectedVac.location ? ` · ${selectedVac.location}` : ''}</p>
                </div>
                <button type="button" onClick={() => { setVacId(''); setVacSearch(''); }} className="text-xs text-indigo-400 hover:text-red-500 font-bold">Change</button>
              </div>
            ) : (
              <div>
                <input type="text" value={vacSearch} onChange={(e) => setVacSearch(e.target.value)} placeholder="Search by job title, company…" className="mb-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                <div className="max-h-32 overflow-y-auto space-y-0.5 rounded-xl border border-gray-100 bg-gray-50 p-1">
                  {openVacs.map((v) => (
                    <button key={v.id} type="button" onClick={() => { setVacId(String(v.id)); setVacSearch(''); }} className="w-full rounded-lg px-3 py-1.5 text-left text-xs hover:bg-white">
                      <span className="font-bold text-gray-800">{v.title}</span> <span className="text-gray-400">— {v.company}</span>{v.partner_org && <span className="ml-1 whitespace-nowrap rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-black text-black">🤝 {v.partner_org}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Interview details */}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-extrabold uppercase text-gray-500">Date *</span>
              <input type="date" value={form.interview_date} onChange={(e) => setForm((f) => ({ ...f, interview_date: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </label>
            <label className="block">
              <span className="text-xs font-extrabold uppercase text-gray-500">Time *</span>
              <input type="time" value={form.interview_time} onChange={(e) => setForm((f) => ({ ...f, interview_time: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </label>
            <label className="block">
              <span className="text-xs font-extrabold uppercase text-gray-500">Location / Venue *</span>
              <input value={form.interview_location} onChange={(e) => setForm((f) => ({ ...f, interview_location: e.target.value }))} placeholder="Office address or meeting link" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </label>
            <label className="block">
              <span className="text-xs font-extrabold uppercase text-gray-500">Mode</span>
              <select value={form.interview_mode} onChange={(e) => setForm((f) => ({ ...f, interview_mode: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                {INTERVIEW_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-extrabold uppercase text-gray-500">Reporting Officer *</span>
              <input value={form.reporting_officer} onChange={(e) => setForm((f) => ({ ...f, reporting_officer: e.target.value }))} placeholder="Manager / HR contact name" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-extrabold uppercase text-gray-500">Notes / Instructions (optional)</span>
              <input value={form.interview_note} onChange={(e) => setForm((f) => ({ ...f, interview_note: e.target.value }))} placeholder="What to bring, dress code, parking info…" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </label>
          </div>

          {/* Progress bar */}
          {progress && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-sm font-bold text-blue-800">Scheduling {progress.done} / {progress.total} candidates…</p>
              <div className="mt-2 h-2 w-full rounded-full bg-blue-200">
                <div className="h-2 rounded-full bg-blue-600 transition-all duration-300" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-1 border-t border-gray-100">
            <p className="text-xs text-gray-400">All {selectedRegs.length} candidates will be set to <strong>Scheduled</strong> with the same interview details.</p>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-black px-6 py-2.5 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60 whitespace-nowrap">
                {saving ? `Scheduling ${progress?.done || 0}/${selectedRegs.length}…` : `📅 Schedule ${selectedRegs.length} Candidates`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Pipeline Tab ─────────────────────────────────────────────────────────────
const PipelineTab = ({ registrations, vacancies, onRefresh, jumpToId, onJumpHandled }) => {
  const [search, setSearch] = useState('');
  const [activeStage, setActiveStage] = useState('all');
  const [groupMode, setGroupMode] = useState(false);
  const [groupSelectedIds, setGroupSelectedIds] = useState(new Set());
  const [groupModal, setGroupModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);

  // Reset page when stage or search changes
  useEffect(() => { setPage(1); }, [activeStage, search]);

  // Auto-select a candidate when jumping from AI Match tab
  useEffect(() => {
    if (!jumpToId) return;
    setActiveStage('all');
    setSearch('');
    setSelectedId(jumpToId);
    if (onJumpHandled) onJumpHandled();
  }, [jumpToId, onJumpHandled]);

  const stageData = useMemo(() => {
    const fresh = [], awaitingConfirm = [], readyToSchedule = [], scheduled = [], result = [], selected = [], placed = [];
    for (const r of registrations) {
      if (Number(r.payment_received) > 0 || r.interview_status === 'Placed') { placed.push(r); continue; }
      if (r.interview_status === 'Selected') { selected.push(r); continue; }
      if (r.interview_status === 'Scheduled') { scheduled.push(r); continue; }
      if (['Waiting for the result', 'Shortlisted', 'Shortlisted for next level', 'Rejected', 'No Show', 'No updates'].includes(r.interview_status)) { result.push(r); continue; }
      if (Number(r.is_shortlisted) > 0) {
        const allOk = r.candidate_availability === 'Available' && r.vacancy_availability === 'Available' && r.hr_assistant_confirmation === 'Accepted' && r.assistant_recruiter_confirmation === 'Accepted';
        if (allOk) readyToSchedule.push(r); else awaitingConfirm.push(r);
        continue;
      }
      fresh.push(r);
    }
    return [
      { id: 'all', label: 'All', members: registrations },
      { id: 'fresh', label: 'Fresh', members: fresh },
      { id: 'awaiting', label: 'Awaiting', members: awaitingConfirm },
      { id: 'ready', label: 'Ready', members: readyToSchedule },
      { id: 'scheduled', label: 'Scheduled', members: scheduled },
      { id: 'result', label: 'Result', members: result },
      { id: 'selected', label: 'Selected', members: selected },
      { id: 'placed', label: '🏆 Placed', members: placed },
    ];
  }, [registrations]);

  const activeStageData = stageData.find((s) => s.id === activeStage) || stageData[0];

  const filtered = useMemo(() => {
    const base = activeStageData.members;
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter((r) => `${r.name} ${r.registration_number} ${r.expecting_job} ${r.department} ${r.district}`.toLowerCase().includes(q));
  }, [activeStageData.members, search]);

  const PAGE_SIZE = 25;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const pagedFiltered = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const placedCount = stageData.find((s) => s.id === 'placed')?.members.length || 0;
  const selectedCount = stageData.find((s) => s.id === 'selected')?.members.length || 0;
  const inProgressCount = registrations.filter((r) => Number(r.is_shortlisted) > 0 && !Number(r.payment_received) && r.interview_status !== 'Placed').length;

  const effectiveSelectedId = (selectedId && filtered.some((r) => r.id === selectedId)) ? selectedId : (filtered[0]?.id ?? null);
  const selectedReg = filtered.find((r) => r.id === effectiveSelectedId) ?? null;

  return (
    <div>
      {/* ── KPI row ── */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total Assigned', value: registrations.length, dot: 'bg-gray-800' },
          { label: 'In Progress', value: inProgressCount, dot: 'bg-amber-400' },
          { label: 'Selected', value: selectedCount, dot: 'bg-emerald-500' },
          { label: 'Placed', value: placedCount, dot: 'bg-yellow-400' },
        ].map(({ label, value, dot }) => (
          <div key={label} className="rounded-2xl border border-gray-200 bg-white px-5 py-4 flex items-center gap-3">
            <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${dot}`} />
            <div>
              <p className="text-2xl font-black text-gray-900 leading-none">{value}</p>
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Pipeline stage selector ── */}
      <div className="mb-5 overflow-x-auto pb-1">
        <div className="flex items-center min-w-max rounded-2xl border border-gray-200 bg-white p-1.5 shadow-sm gap-0">
          {stageData.map((stage, i) => (
            <React.Fragment key={stage.id}>
              <button
                type="button"
                onClick={() => { setActiveStage(stage.id); setSearch(''); }}
                className={`flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-extrabold transition whitespace-nowrap ${activeStage === stage.id ? 'bg-black text-yellow-400 shadow-sm' : 'text-gray-500 hover:text-black hover:bg-gray-50'}`}
              >
                {stage.label}
                <span className={`rounded-full min-w-[20px] px-1.5 py-0.5 text-center text-[10px] font-black leading-none ${activeStage === stage.id ? 'bg-yellow-400 text-black' : stage.members.length > 0 ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}`}>
                  {stage.members.length}
                </span>
              </button>
              {i < stageData.length - 1 && (
                <span className="text-gray-300 text-base px-1 flex-shrink-0 select-none">›</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Two-column: left member list + right detail ── */}
      <div className="flex gap-4 items-start">

        {/* ── LEFT: compact member list ── */}
        <div className="w-60 xl:w-72 flex-shrink-0 flex flex-col gap-3 sticky top-4">

          {/* Search + Group toggle */}
          <div className="flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search…`}
              className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
            <button
              type="button"
              onClick={() => { setGroupMode((m) => !m); setGroupSelectedIds(new Set()); }}
              title="Group Interview"
              className={`flex items-center gap-1 rounded-xl border px-2.5 py-2 text-sm font-extrabold transition ${groupMode ? 'border-black bg-black text-yellow-400' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-400'}`}
            >
              👥
              {groupMode && groupSelectedIds.size > 0 && (
                <span className="rounded-full bg-yellow-400 px-1.5 py-0.5 text-[9px] font-black text-black">{groupSelectedIds.size}</span>
              )}
            </button>
          </div>

          {/* Group action bar */}
          {groupMode && (
            <div className={`flex flex-col gap-2 rounded-xl border px-3 py-2.5 transition-all ${groupSelectedIds.size > 0 ? 'border-black bg-black' : 'border-dashed border-gray-300 bg-gray-50'}`}>
              {groupSelectedIds.size === 0
                ? <p className="text-xs font-semibold text-gray-500">Tap members to add to group interview.</p>
                : (
                  <>
                    <p className="text-xs font-extrabold text-white">{groupSelectedIds.size} selected</p>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setGroupSelectedIds(new Set())} className="rounded-lg border border-white/30 px-2 py-1 text-[10px] font-bold text-gray-300 hover:text-white">Clear</button>
                      <button type="button" onClick={() => setGroupModal(true)} className="flex-1 rounded-lg bg-yellow-400 px-2 py-1 text-[10px] font-extrabold text-black hover:bg-yellow-300">📅 Schedule ({groupSelectedIds.size})</button>
                    </div>
                  </>
                )
              }
            </div>
          )}

          {/* Scrollable member list */}
          <div className="space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 310px)' }}>
            {filtered.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-gray-200 py-10 text-center">
                <p className="text-2xl mb-2">👥</p>
                <p className="text-xs font-bold text-gray-500">{search ? 'No matches.' : activeStage === 'all' ? 'No members assigned.' : 'No members in this stage.'}</p>
                {!search && activeStage === 'all' && <p className="text-[10px] text-gray-400 mt-1">HR Assistant forwards registrations to your dashboard.</p>}
              </div>
            ) : (
              pagedFiltered.map((reg) => {
                const isActive = effectiveSelectedId === reg.id;
                const regVac = reg.selected_job_id ? vacancies.find((v) => String(v.id) === String(reg.selected_job_id)) : null;
                const regIsPlaced = Number(reg.payment_received) > 0 || reg.interview_status === 'Placed';
                const regIsPrime = reg.plan_type === 'prime';
                return (
                  <button
                    key={reg.id}
                    type="button"
                    onClick={() =>
                      groupMode
                        ? setGroupSelectedIds((prev) => { const next = new Set(prev); if (next.has(reg.id)) next.delete(reg.id); else next.add(reg.id); return next; })
                        : setSelectedId(reg.id)
                    }
                    className={`relative w-full rounded-xl border text-left transition overflow-hidden ${
                      isActive && !groupMode
                        ? regIsPrime ? 'bg-yellow-900 border-yellow-500 shadow-md' : 'bg-black border-black shadow-md'
                        : groupSelectedIds.has(reg.id) ? 'border-black bg-gray-900'
                        : regIsPrime ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-400 shadow-[0_0_0_1px_rgba(234,179,8,0.3)] hover:border-yellow-500 hover:from-yellow-100 hover:to-amber-100'
                        : 'bg-white border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                  >
                    {/* Gold left stripe for prime */}
                    {regIsPrime && <span className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-400 rounded-l-xl" />}
                    <div className={`flex items-center gap-2 ${regIsPrime ? 'pl-3 pr-3 py-2.5' : 'px-3 py-2.5'}`}>
                      {groupMode && (
                        <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-all ${groupSelectedIds.has(reg.id) ? 'border-yellow-400 bg-yellow-400 text-black' : 'border-gray-300 bg-white'}`}>
                          {groupSelectedIds.has(reg.id) && <span className="text-[9px] font-black leading-none">✓</span>}
                        </div>
                      )}
                      {reg.photo_file
                        ? <img src={reg.photo_file.startsWith('http') ? reg.photo_file : `${API_URL}${reg.photo_file}`} alt={reg.name} className={`h-8 w-8 flex-shrink-0 rounded-lg object-cover ${regIsPrime && !isActive ? 'ring-2 ring-yellow-400' : ''}`} />
                        : <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-xs font-black ${
                            isActive && !groupMode ? 'bg-yellow-400 text-black' :
                            regIsPrime ? 'bg-yellow-400 text-black' :
                            'bg-black text-yellow-400'
                          }`}>{(reg.name || '?')[0]}</div>
                      }
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 flex-wrap">
                          <p className={`font-black text-xs truncate ${isActive && !groupMode ? 'text-white' : 'text-gray-900'}`}>{reg.name}</p>
                          {regIsPrime && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-yellow-400 px-1.5 py-0.5 text-[8px] font-black leading-none text-black flex-shrink-0">
                              👑 PRIME
                            </span>
                          )}
                          <PartnerTag row={reg} />
                        </div>
                        <p className={`text-[10px] truncate mt-0.5 ${isActive && !groupMode ? 'text-yellow-200' : regIsPrime ? 'text-amber-700' : 'text-gray-500'}`}>
                          {regVac ? `${regVac.title} @ ${regVac.company}` : (reg.expecting_job || reg.department || '—')}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-1">
                        {regIsPlaced && <span className="rounded-full bg-yellow-400 px-1.5 py-0.5 text-[9px] font-extrabold text-black">🏆</span>}
                        {!regIsPlaced && reg.interview_status && reg.interview_status !== 'Not Scheduled' && (
                          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-extrabold whitespace-nowrap ${isActive && !groupMode ? 'bg-yellow-400 text-black' : iBadge(reg.interview_status)}`}>
                            {reg.interview_status.length > 9 ? reg.interview_status.slice(0, 9) + '…' : reg.interview_status}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {totalPages > 1 && (
            <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
              <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">«</button>
              <button type="button" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
                .map((n, i, arr) => (
                  <React.Fragment key={n}>
                    {i > 0 && arr[i - 1] !== n - 1 && <span className="text-gray-400 text-sm px-1">…</span>}
                    <button type="button" onClick={() => setPage(n)}
                      className={`rounded-xl border px-3 py-1.5 text-sm font-bold transition ${safePage === n ? 'bg-black text-yellow-400 border-black' : 'hover:bg-gray-50'}`}>
                      {n}
                    </button>
                  </React.Fragment>
                ))}
              <button type="button" onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next ›</button>
              <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">»</button>
              <span className="text-sm text-gray-500 ml-2">Page {safePage} of {totalPages}</span>
            </div>
          )}
        </div>

        {/* ── RIGHT: Detail panel ── */}
        <div className="flex-1 min-w-0">
          {selectedReg ? (
            <MemberCard
              key={selectedReg.id}
              reg={selectedReg}
              vacancies={vacancies}
              onRefresh={onRefresh}
              detailMode={true}
              groupMode={groupMode}
              isGroupSelected={groupSelectedIds.has(selectedReg.id)}
              onToggleGroupSelect={() => setGroupSelectedIds((prev) => { const next = new Set(prev); if (next.has(selectedReg.id)) next.delete(selectedReg.id); else next.add(selectedReg.id); return next; })}
            />
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-gray-200 py-20 text-center">
              <p className="text-4xl mb-3">👈</p>
              <p className="font-bold text-gray-500">Select a member from the list to view their pipeline.</p>
            </div>
          )}
        </div>

      </div>

      {groupModal && (
        <GroupInterviewModal
          selectedRegs={registrations.filter((r) => groupSelectedIds.has(r.id))}
          vacancies={vacancies}
          onDone={() => { setGroupModal(false); setGroupMode(false); setGroupSelectedIds(new Set()); onRefresh(); }}
          onClose={() => setGroupModal(false)}
        />
      )}
    </div>
  );
};

// ─── Vacancies Tab ────────────────────────────────────────────────────────────
// Filled vacancy needed again — add openings so it goes live
const RepublishModal = ({ vac, onDone, onClose }) => {
  const [openings, setOpenings] = useState('1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await axios.post(`${API_URL}/api/staff/recruiter/vacancies/${vac.id}/republish`, { openings: Number(openings) }, { headers: authHeader() });
      onDone();
    } catch (err) { setError(err?.response?.data?.error || 'Failed to republish.'); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl bg-black px-5 py-4">
          <div><p className="text-xs font-extrabold uppercase text-yellow-400">Republish Vacancy</p><h3 className="font-black text-white">{vac.title}</h3></div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">{vac.company}{vac.location ? ` · ${vac.location}` : ''} — {vac.placed_count} placed so far. It will show as open again in Vacancies and AI Match.</p>
          <label className="block"><span className="text-xs font-extrabold uppercase text-gray-500">How many new openings?</span>
            <input inputMode="numeric" value={openings} onChange={(e) => setOpenings(e.target.value.replace(/D/g, '').slice(0, 3))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </label>
          {error && <p className="text-xs font-bold text-red-600">{error}</p>}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving || !Number(openings)} className="rounded-xl bg-black px-6 py-2 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60">{saving ? 'Saving…' : '🔁 Republish'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const VacanciesTab = ({ vacancies, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [view, setView] = useState('open'); // 'open' | 'filled'
  const [republishVac, setRepublishVac] = useState(null);
  const PAGE_SIZE_VAC = 25;

  const openVacancies = useMemo(() => vacancies.filter((v) => !Number(v.is_filled)), [vacancies]);
  const filledVacancies = useMemo(() => vacancies.filter((v) => Number(v.is_filled)), [vacancies]);
  const listed = view === 'filled' ? filledVacancies : openVacancies;

  const filtered = useMemo(() => {
    if (!search.trim()) return listed;
    const q = search.toLowerCase();
    return listed.filter((v) => `${v.title} ${v.company} ${v.location} ${v.district} ${v.department}`.toLowerCase().includes(q));
  }, [listed, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE_VAC));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE_VAC, safePage * PAGE_SIZE_VAC);

  return (
    <div>
      <div className="mb-5 grid grid-cols-2 gap-3">
        {[
          { id: 'open', l: 'Open Vacancies', c: openVacancies.length, dot: 'bg-emerald-500' },
          { id: 'filled', l: 'Filled — click to republish', c: filledVacancies.length, dot: 'bg-gray-400' },
        ].map(({ id, l, c, dot }) => (
          <button key={l} type="button" onClick={() => { setView(id); setPage(1); }} className={`rounded-2xl border px-5 py-4 flex items-center gap-3 text-left transition ${view === id ? 'border-black bg-white ring-2 ring-yellow-400' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
            <div className={`h-2 w-2 rounded-full flex-shrink-0 ${dot}`} />
            <div><p className="text-2xl font-black text-gray-900">{c}</p><p className="text-[10px] font-black uppercase tracking-widest mt-0.5 text-gray-400">{l}</p></div>
          </button>
        ))}
      </div>
      <div className="mb-4">
        <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search vacancy…" className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
      </div>
      {paged.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 py-16 text-center text-gray-400"><p className="text-4xl mb-3">💼</p><p className="font-bold">{search ? 'No vacancies match your search.' : view === 'filled' ? 'No filled vacancies.' : 'No open vacancies.'}</p></div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Table header */}
          <div className="grid grid-cols-[2fr_1.6fr_1.4fr_1fr_1fr_80px] bg-black px-4 py-2.5 text-[10px] font-extrabold uppercase tracking-widest text-yellow-400">
            <span>Job Title</span>
            <span>Company</span>
            <span>Location</span>
            <span>Salary</span>
            <span>Dept / Exp</span>
            <span className="text-center">{view === 'filled' ? 'Action' : 'Added'}</span>
          </div>
          {/* Rows */}
          {paged.map((v, idx) => {
            return (
              <div
                key={v.id}
                className={`grid grid-cols-[2fr_1.6fr_1.4fr_1fr_1fr_80px] items-center gap-x-2 px-4 py-3 text-sm transition-colors hover:bg-yellow-50/40
                  ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}
                  border-t border-gray-100`}
              >
                {/* Title */}
                <div className="min-w-0">
                  <p className="font-black text-gray-900 truncate">{v.title}</p>
                  {Number(v.total_openings) > 1 && (
                    <p className="text-[10px] font-extrabold text-emerald-700 mt-0.5">
                      {Number(v.placed_count) > 0 ? `${v.placed_count} of ${v.total_openings} placed · ` : ''}{v.openings_left} opening{Number(v.openings_left) === 1 ? '' : 's'} left
                    </p>
                  )}
                  {v.description && <p className="text-[11px] text-gray-400 truncate mt-0.5">{v.description}</p>}
                </div>
                {/* Company */}
                <p className="font-semibold text-gray-700 truncate">{v.company || <span className="text-gray-300">—</span>}</p>
                {/* Location */}
                <p className="text-gray-500 truncate text-xs">
                  {v.location ? `${v.location}${v.district ? `, ${v.district}` : ''}` : <span className="text-gray-300">—</span>}
                </p>
                {/* Salary */}
                <p className="text-gray-700 font-semibold truncate text-xs">{v.salary || <span className="text-gray-300">—</span>}</p>
                {/* Dept / Exp */}
                <div className="min-w-0">
                  {v.department && <p className="text-[11px] text-gray-500 truncate">{v.department}</p>}
                  {v.experience && <p className="text-[11px] text-gray-400 truncate">{v.experience}</p>}
                  {!v.department && !v.experience && <span className="text-gray-300 text-xs">—</span>}
                </div>
                {/* Date added */}
                <div className="flex justify-center">
                  {view === 'filled' ? (
                    <button type="button" onClick={() => setRepublishVac(v)} title={v.placed_member_name ? `Last placed: ${v.placed_member_name}` : ''} className="rounded-lg bg-black px-2 py-1 text-[11px] font-extrabold text-yellow-400 hover:bg-gray-900">🔁 Republish</button>
                  ) : (
                    <span className="text-[11px] text-gray-400">
                      {v.created_at ? fmtDate(v.created_at) : '—'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">«</button>
          <button type="button" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
            .map((n, i, arr) => (
              <React.Fragment key={n}>
                {i > 0 && arr[i - 1] !== n - 1 && <span className="text-gray-400 text-sm px-1">…</span>}
                <button type="button" onClick={() => setPage(n)}
                  className={`rounded-xl border px-3 py-1.5 text-sm font-bold transition ${safePage === n ? 'bg-black text-yellow-400 border-black' : 'hover:bg-gray-50'}`}>
                  {n}
                </button>
              </React.Fragment>
            ))}
          <button type="button" onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next ›</button>
          <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">»</button>
          <span className="text-sm text-gray-500 ml-2">Page {safePage} of {totalPages}</span>
        </div>
      )}
      {republishVac && <RepublishModal vac={republishVac} onDone={() => { setRepublishVac(null); setView('open'); if (onRefresh) onRefresh(); }} onClose={() => setRepublishVac(null)} />}
    </div>
  );
};

// ─── AI Match Studio ──────────────────────────────────────────────────────────
// Two analyses: "Jobs for a candidate" ranks open vacancies against what a
// candidate wants; "Candidates for a company" ranks unplaced candidates against
// what a company asks for. Scoring lives in utils/aiMatch.js.
const AI_TONE = {
  emerald: { ring: '#10b981', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  sky: { ring: '#0ea5e9', pill: 'bg-sky-50 text-sky-700 ring-sky-200' },
  amber: { ring: '#f59e0b', pill: 'bg-amber-50 text-amber-700 ring-amber-200' },
  rose: { ring: '#f43f5e', pill: 'bg-rose-50 text-rose-700 ring-rose-200' },
  slate: { ring: '#cbd5e1', pill: 'bg-slate-50 text-slate-500 ring-slate-200' },
};
const CRIT_STYLE = {
  match: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  partial: 'bg-amber-50 text-amber-700 ring-amber-200',
  miss: 'bg-rose-50 text-rose-700 ring-rose-200',
  unknown: 'bg-slate-50 text-slate-500 ring-slate-200',
};
const CRIT_BAR = { match: 'bg-emerald-500', partial: 'bg-amber-400', miss: 'bg-rose-400', unknown: 'bg-slate-300' };
const CRIT_MARK = { match: '✓', partial: '≈', miss: '✗', unknown: '?' };
const aiInput = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-violet-400 focus:outline-none focus:ring-4 focus:ring-violet-100';
const MATCH_PAGE_SIZE = 20;

const EMPTY_JOB_SEARCH = {
  designation: '', department: '', location: '', radiusKm: '', expectedSalary: '', companyTypes: [],
  checkEligibility: true, must: {}, strictUnknown: false, includeUnknownLocation: false,
};
const EMPTY_CANDIDATE_SEARCH = {
  designation: '', department: '', gender: 'Any', ageMin: '', ageMax: '', marital: 'Any', minQualLevel: '0', qualText: '',
  experience: 'Any', salaryBudget: '', location: '', radiusKm: '', must: { gender: true }, strictUnknown: false, includeUnknownLocation: false,
};

const distinctValues = (values) => {
  const seen = new Map();
  values.forEach((v) => { const t = String(v || '').trim(); if (t.length >= 2 && !seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t); });
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
};

const ScoreRing = ({ score, band, size = 60 }) => {
  const r = (size - 8) / 2, c = 2 * Math.PI * r;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#eef2ff" strokeWidth="6" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={AI_TONE[band.tone].ring} strokeWidth="6" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - (score ?? 0) / 100)} style={{ transition: 'stroke-dashoffset .6s ease' }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[15px] font-black leading-none text-slate-900">{score ?? '—'}{score !== null && <span className="text-[9px] font-bold">%</span>}</span>
      </div>
    </div>
  );
};

const CriteriaChips = ({ criteria }) => (
  <div className="mt-2 flex flex-wrap gap-1.5">
    {criteria.map((c) => (
      <span key={c.key} title={`${c.label}: ${c.detail}`} className={`inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${CRIT_STYLE[c.status]}`}>
        <span className="font-black">{CRIT_MARK[c.status]}</span><span>{c.icon}</span><span className="truncate">{c.detail}</span>
      </span>
    ))}
  </div>
);

const CriteriaBreakdown = ({ criteria }) => (
  <div className="mt-3 space-y-2 rounded-2xl bg-slate-50 p-3">
    {criteria.map((c) => (
      <div key={c.key} className="grid grid-cols-[1.5rem_7rem_1fr_3.5rem] items-center gap-2 text-xs">
        <span>{c.icon}</span>
        <span className="font-bold text-slate-700">{c.label}</span>
        <div className="min-w-0">
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-200"><div className={`h-full rounded-full ${CRIT_BAR[c.status]}`} style={{ width: `${(c.earned / c.weight) * 100}%` }} /></div>
          <p className="mt-0.5 truncate text-[11px] text-slate-500">{c.detail}</p>
        </div>
        <span className="text-right font-bold text-slate-500">{Math.round(c.earned)}/{c.weight}</span>
      </div>
    ))}
  </div>
);

const FilterField = ({ icon, label, mustKey, must, onToggleMust, children }) => (
  <div>
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{icon} {label}</span>
      {mustKey && (
        <button type="button" onClick={() => onToggleMust(mustKey)} title="Must-have: hide results that fail this requirement"
          className={`rounded-full px-2 py-0.5 text-[10px] font-black transition ${must[mustKey] ? 'bg-violet-600 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:bg-violet-50 hover:text-violet-600'}`}>
          {must[mustKey] ? '🔒 Must' : '+ Must'}
        </button>
      )}
    </div>
    {children}
  </div>
);

const Pills = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const [val, text] = Array.isArray(o) ? o : [o, o];
      const on = String(value) === String(val);
      return (
        <button key={val} type="button" onClick={() => onChange(val)}
          className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${on ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          {text}
        </button>
      );
    })}
  </div>
);

const LocationRadiusField = ({ filters, setFilter, center }) => (
  <div className="space-y-2.5 rounded-2xl bg-gradient-to-br from-sky-50 to-indigo-50 p-3 ring-1 ring-sky-100">
    <div>
      <p className="mb-1.5 text-[11px] font-extrabold uppercase tracking-wider text-sky-700">📍 Location centre</p>
      <input list="ai-place-names" value={filters.location} onChange={(e) => setFilter('location', e.target.value)}
        placeholder="Town, district or Google Maps link" className={aiInput} />
      {filters.location.trim() && (
        <p className={`mt-1 text-[11px] font-semibold ${center ? 'text-sky-700' : 'text-rose-600'}`}>
          {center ? `✓ ${center.label}${center.precision === 'district' ? ' — distances are approximate' : ''}` : '⚠ Place not recognised — try a nearby town or paste a Google Maps link'}
        </p>
      )}
    </div>
    <div>
      <p className="mb-1.5 text-[11px] font-extrabold uppercase tracking-wider text-sky-700">🧭 Radius</p>
      <div className="flex flex-wrap items-center gap-1.5">
        {[['', 'Any'], ['5', '5 km'], ['10', '10 km'], ['25', '25 km'], ['50', '50 km']].map(([v, t]) => (
          <button key={t} type="button" onClick={() => setFilter('radiusKm', v)}
            className={`rounded-xl px-2.5 py-1.5 text-xs font-bold transition ${String(filters.radiusKm) === v ? 'bg-sky-600 text-white shadow-sm' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-sky-300'}`}>{t}</button>
        ))}
        <input type="number" min="1" value={filters.radiusKm} onChange={(e) => setFilter('radiusKm', e.target.value)} placeholder="km"
          className="w-16 rounded-xl border border-slate-200 bg-white px-2 py-1.5 text-xs focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100" />
      </div>
      {Number(filters.radiusKm) > 0 && (
        <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] font-semibold text-slate-600">
          <input type="checkbox" checked={filters.includeUnknownLocation} onChange={(e) => setFilter('includeUnknownLocation', e.target.checked)} className="accent-sky-600" />
          Also show results whose location is unknown
        </label>
      )}
      {Number(filters.radiusKm) > 0 && !center && <p className="mt-1 text-[11px] font-semibold text-amber-600">Set a location centre to use the radius.</p>}
    </div>
  </div>
);

const StartFromPicker = ({ label, items, selectedId, onSelect, onClear, render, searchText, placeholder }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const selected = items.find((it) => String(it.id) === String(selectedId));
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (s ? items.filter((it) => searchText(it).toLowerCase().includes(s)) : items).slice(0, 30);
  }, [items, q, searchText]);
  if (selected) {
    return (
      <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 p-3 text-white shadow-md">
        <div className="min-w-0 flex-1">{render(selected, true)}</div>
        <button type="button" onClick={onClear} className="rounded-xl bg-white/15 px-2.5 py-1 text-xs font-bold hover:bg-white/25">Change</button>
      </div>
    );
  }
  return (
    <div className="relative">
      <p className="mb-1.5 text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{label}</p>
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder} className={aiInput} />
      {open && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-2xl bg-white p-1 shadow-xl ring-1 ring-slate-200">
          {matches.length === 0 ? <p className="px-3 py-4 text-center text-xs text-slate-400">Nothing found</p> : matches.map((it) => (
            <button key={it.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onSelect(it); setQ(''); setOpen(false); }}
              className="w-full rounded-xl px-3 py-2 text-left hover:bg-violet-50">
              {render(it, false)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MatchTab = ({ registrations, vacancies, onRefresh, onGoToPipeline }) => {
  const [mode, setMode] = useState('jobs'); // 'jobs' = jobs for a candidate · 'candidates' = candidates for a company
  const [jobSearch, setJobSearch] = useState(EMPTY_JOB_SEARCH);
  const [candSearch, setCandSearch] = useState(EMPTY_CANDIDATE_SEARCH);
  const [selRegId, setSelRegId] = useState('');
  const [selVacId, setSelVacId] = useState('');
  const [sortBy, setSortBy] = useState('score');
  const [minScore, setMinScore] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [page, setPage] = useState(1);
  const [profileReg, setProfileReg] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [copied, setCopied] = useState('');

  const openRegs = useMemo(() => registrations.filter((r) => !Number(r.payment_received) && r.interview_status !== 'Placed'), [registrations]);
  const openVacs = useMemo(() => vacancies.filter((v) => !Number(v.is_filled)), [vacancies]);
  const activeReg = registrations.find((r) => String(r.id) === selRegId) || null;
  const activeVac = vacancies.find((v) => String(v.id) === selVacId) || null;

  const isJobs = mode === 'jobs';
  const filters = isJobs ? jobSearch : candSearch;
  const setFilters = isJobs ? setJobSearch : setCandSearch;
  const setFilter = useCallback((k, v) => setFilters((f) => ({ ...f, [k]: v })), [setFilters]);
  const toggleMust = (k) => setFilters((f) => ({ ...f, must: { ...f.must, [k]: !f.must[k] } }));

  const center = useMemo(() => (filters.location.trim() ? resolvePointFromText(filters.location) : null), [filters.location]);

  const titleOptions = useMemo(() => distinctValues(isJobs ? openVacs.map((v) => v.title) : openRegs.map((r) => r.expecting_job)), [isJobs, openVacs, openRegs]);
  const deptOptions = useMemo(() => distinctValues([...openVacs.map((v) => v.department), ...openRegs.map((r) => r.department)]), [openVacs, openRegs]);
  const companyTypeOptions = useMemo(() => distinctValues(openVacs.map((v) => v.company_category)), [openVacs]);

  const results = useMemo(() => (isJobs
    ? openVacs.map((vac) => ({ id: vac.id, vac, ...scoreVacancyForCandidate(vac, jobSearch, { center, profile: activeReg }) }))
    : openRegs.map((reg) => ({ id: reg.id, reg, ...scoreCandidateForCompany(reg, candSearch, { center }) }))
  ), [isJobs, openVacs, openRegs, jobSearch, candSearch, center, activeReg]);

  const hasCriteria = results.some((r) => r.criteria.length > 0);
  const hidden = results.filter((r) => r.excludedBy);
  const hiddenReasons = useMemo(() => {
    const counts = {};
    hidden.forEach((r) => { const k = r.excludedBy.status === 'unknown' ? `${r.excludedBy.label} unknown` : `${r.excludedBy.label}`; counts[k] = (counts[k] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [hidden]);

  const visible = useMemo(() => {
    const list = results.filter((r) => !r.excludedBy && (!hasCriteria || (r.score ?? 0) >= minScore));
    const salaryOf = (r) => parseNumberRange(r.vac?.salary)?.max ?? -1;
    const prime = (r) => (r.reg?.plan_type === 'prime' ? 1 : 0);
    list.sort((a, b) => {
      if (sortBy === 'distance') { const d = (a.km ?? Infinity) - (b.km ?? Infinity); if (d) return d; }
      if (sortBy === 'salary') { const d = salaryOf(b) - salaryOf(a); if (d) return d; }
      if (sortBy === 'recent') return Number(b.id) - Number(a.id);
      const d = (b.score ?? -1) - (a.score ?? -1);
      if (d) return d;
      if (prime(b) !== prime(a)) return prime(b) - prime(a);
      return (a.km ?? Infinity) - (b.km ?? Infinity);
    });
    return list;
  }, [results, hasCriteria, minScore, sortBy]);

  const bandCounts = useMemo(() => ({
    excellent: visible.filter((r) => r.score >= 80).length,
    strong: visible.filter((r) => r.score >= 65 && r.score < 80).length,
    fair: visible.filter((r) => r.score >= 45 && r.score < 65).length,
  }), [visible]);

  useEffect(() => { setPage(1); }, [filters, mode, sortBy, minScore]);
  const totalPages = Math.max(1, Math.ceil(visible.length / MATCH_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = visible.slice((safePage - 1) * MATCH_PAGE_SIZE, safePage * MATCH_PAGE_SIZE);

  const switchMode = (m) => { setMode(m); setSelectedIds(new Set()); setExpandedId(null); setShowHidden(false); setSortBy('score'); setMinScore(0); };
  const toggleSelect = (id) => setSelectedIds((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  const pickCandidate = (reg) => { setSelRegId(String(reg.id)); setJobSearch((f) => ({ ...f, ...filtersFromCandidate(reg) })); setSelectedIds(new Set()); };
  const pickVacancy = (vac) => { setSelVacId(String(vac.id)); setCandSearch((f) => ({ ...f, ...filtersFromVacancy(vac) })); setSelectedIds(new Set()); };
  const resetFilters = () => {
    if (isJobs) { setSelRegId(''); setJobSearch(EMPTY_JOB_SEARCH); } else { setSelVacId(''); setCandSearch(EMPTY_CANDIDATE_SEARCH); }
    setSelectedIds(new Set()); setMinScore(0);
  };

  const flash = (msg) => { setCopied(msg); setTimeout(() => setCopied(''), 2500); };
  const selectedResults = visible.filter((r) => selectedIds.has(r.id));

  // Jobs are shared without the employer's name so candidates go through us.
  const jobsMessage = () => {
    const lines = selectedResults.map(({ vac }, i) => [
      `${i + 1}. *${vac.title}*${vac.department ? ` (${vac.department})` : ''}`,
      [vac.location || vac.district ? `📍 ${[vac.location, vac.district].filter(Boolean).join(', ')}` : '', vac.salary ? `💰 ${vac.salary}` : '', vac.experience ? `🧭 ${vac.experience}` : ''].filter(Boolean).join('  '),
    ].join('\n'));
    return `Hi${activeReg ? ` ${activeReg.name}` : ''}, here ${selectedResults.length === 1 ? 'is a job' : `are ${selectedResults.length} jobs`} matching your profile from Dani Job Club:\n\n${lines.join('\n\n')}\n\nReply with the number of the job you are interested in and we will arrange the interview.`;
  };
  const sendJobsOnWhatsApp = () => {
    const wa = getWADigits(activeReg?.whatsapp_number || activeReg?.contact_number);
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(jobsMessage())}`, '_blank', 'noopener');
  };
  const copyText = async (text, msg) => { try { await navigator.clipboard.writeText(text); flash(msg); } catch { flash('Copy failed — your browser blocked clipboard access'); } };
  const exportCandidatesCsv = () => {
    const rows = (selectedResults.length ? selectedResults : visible).map(({ reg, score, km }) => [
      reg.name, reg.registration_number, reg.contact_number, reg.whatsapp_number, reg.place, reg.district, reg.highest_qualification,
      reg.age, reg.gender, reg.marital_status, reg.expecting_job, score ?? '', km ?? '',
    ]);
    const header = ['Name', 'Reg No', 'Phone', 'WhatsApp', 'Place', 'District', 'Qualification', 'Age', 'Gender', 'Marital Status', 'Expecting Job', 'Match %', 'Distance km'];
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `matched-candidates${activeVac ? `-${activeVac.title}` : ''}.csv`.replace(/[^\w.-]+/g, '-');
    a.click(); URL.revokeObjectURL(url);
  };

  const DistancePill = ({ km, approx }) => (km === null || km === undefined
    ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-400">📍 ? km</span>
    : <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${approx ? 'border border-dashed border-slate-300 bg-white text-slate-500' : 'bg-sky-100 text-sky-700'}`}>📍 {approx ? '~' : ''}{km} km</span>);

  return (
    <div className="space-y-5">
      <datalist id="ai-place-names">{KNOWN_PLACE_NAMES.map((p) => <option key={p} value={p} />)}</datalist>
      <datalist id="ai-title-options">{titleOptions.map((p) => <option key={p} value={p} />)}</datalist>
      <datalist id="ai-dept-options">{deptOptions.map((p) => <option key={p} value={p} />)}</datalist>

      {/* Hero + mode switch */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-6 text-white shadow-lg">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-fuchsia-300/20 blur-2xl" />
        <div className="relative">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/70">✨ AI Match Studio</p>
          <h2 className="mt-1 text-2xl font-black">Find the right fit in seconds</h2>
          <p className="mt-1 text-sm text-white/80">Tell us what is wanted — every {isJobs ? 'vacancy' : 'candidate'} is checked point by point and ranked.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              ['jobs', '🎯', 'Jobs for a Candidate', 'Designation, location, salary, department and company type → matching vacancies'],
              ['candidates', '🏢', 'Candidates for a Company', 'Gender, age, marital status, qualification, salary, experience and km radius → matching candidates'],
            ].map(([id, icon, title, desc]) => (
              <button key={id} type="button" onClick={() => switchMode(id)}
                className={`flex items-start gap-3 rounded-2xl p-4 text-left transition ${mode === id ? 'bg-white text-slate-900 shadow-xl' : 'bg-white/10 text-white ring-1 ring-white/25 hover:bg-white/20'}`}>
                <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-xl ${mode === id ? 'bg-gradient-to-br from-indigo-100 to-violet-100' : 'bg-white/15'}`}>{icon}</span>
                <span>
                  <span className="block text-sm font-black">{title}</span>
                  <span className={`mt-0.5 block text-xs ${mode === id ? 'text-slate-500' : 'text-white/75'}`}>{desc}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
        {/* ── Requirements panel ── */}
        <aside className="space-y-4 self-start rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          <div className="flex items-center justify-between">
            <p className="text-sm font-black text-slate-900">{isJobs ? 'What the candidate wants' : 'What the company needs'}</p>
            <button type="button" onClick={resetFilters} className="text-xs font-bold text-slate-400 hover:text-rose-600">↺ Reset</button>
          </div>

          {isJobs ? (
            <StartFromPicker
              label="Start from a candidate (auto-fills)" placeholder="Search candidate name, job, place…"
              items={openRegs} selectedId={selRegId} onSelect={pickCandidate} onClear={() => setSelRegId('')}
              searchText={(r) => `${r.name} ${r.registration_number} ${r.expecting_job} ${r.place} ${r.district} ${r.contact_number}`}
              render={(r, sel) => (
                <div className="min-w-0">
                  <p className={`truncate text-sm font-black ${sel ? 'text-white' : 'text-slate-800'}`}>{r.name}{r.plan_type === 'prime' && ' 👑'}</p>
                  <p className={`truncate text-xs ${sel ? 'text-white/80' : 'text-slate-400'}`}>{[r.expecting_job, r.highest_qualification, r.place || r.district].filter(Boolean).join(' · ')}</p>
                </div>
              )} />
          ) : (
            <StartFromPicker
              label="Start from a vacancy (auto-fills)" placeholder="Search job title, company…"
              items={openVacs} selectedId={selVacId} onSelect={pickVacancy} onClear={() => setSelVacId('')}
              searchText={(v) => `${v.title} ${v.company} ${v.department} ${v.location} ${v.district}`}
              render={(v, sel) => (
                <div className="min-w-0">
                  <p className={`truncate text-sm font-black ${sel ? 'text-white' : 'text-slate-800'}`}>{v.title}</p>
                  <p className={`truncate text-xs ${sel ? 'text-white/80' : 'text-slate-400'}`}>{[v.company, v.location || v.district, Number(v.total_openings) > 1 ? `${v.openings_left} openings left` : ''].filter(Boolean).join(' · ')}</p>
                </div>
              )} />
          )}
          {(isJobs ? activeReg : activeVac) && (
            <p className="-mt-2 rounded-xl bg-violet-50 px-3 py-2 text-[11px] font-semibold text-violet-700">✨ Filled from the {isJobs ? 'profile' : 'vacancy'} — change anything below.</p>
          )}

          <FilterField icon="💼" label="Designation" mustKey="designation" must={filters.must} onToggleMust={toggleMust}>
            <input list="ai-title-options" value={filters.designation} onChange={(e) => setFilter('designation', e.target.value)} placeholder="e.g. Accountant, Sales Executive" className={aiInput} />
          </FilterField>
          <FilterField icon="🏷️" label="Department" mustKey="department" must={filters.must} onToggleMust={toggleMust}>
            <input list="ai-dept-options" value={filters.department} onChange={(e) => setFilter('department', e.target.value)} placeholder="e.g. Accounts, Sales" className={aiInput} />
          </FilterField>

          <LocationRadiusField filters={filters} setFilter={setFilter} center={center} />

          {isJobs ? (
            <>
              <FilterField icon="💰" label="Expected salary (₹/month)" mustKey="salary" must={filters.must} onToggleMust={toggleMust}>
                <input inputMode="numeric" value={filters.expectedSalary} onChange={(e) => setFilter('expectedSalary', e.target.value.replace(/\D/g, ''))} placeholder="e.g. 18000" className={aiInput} />
              </FilterField>
              <FilterField icon="🏢" label="Type of company" mustKey="companyType" must={filters.must} onToggleMust={toggleMust}>
                {companyTypeOptions.length === 0 ? <p className="text-xs text-slate-400">No company types recorded on vacancies yet.</p> : (
                  <div className="flex flex-wrap gap-1.5">
                    {companyTypeOptions.map((t) => {
                      const on = filters.companyTypes.includes(t);
                      return (
                        <button key={t} type="button" onClick={() => setFilter('companyTypes', on ? filters.companyTypes.filter((x) => x !== t) : [...filters.companyTypes, t])}
                          className={`rounded-xl px-2.5 py-1 text-xs font-bold transition ${on ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                          {on ? '✓ ' : ''}{t}
                        </button>
                      );
                    })}
                  </div>
                )}
              </FilterField>
              {activeReg && (
                <FilterField icon="✅" label="Company eligibility" mustKey="eligibility" must={filters.must} onToggleMust={toggleMust}>
                  <label className="flex cursor-pointer items-start gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    <input type="checkbox" checked={filters.checkEligibility} onChange={(e) => setFilter('checkEligibility', e.target.checked)} className="mt-0.5 accent-violet-600" />
                    Check {activeReg.name.split(' ')[0]}'s gender, age, qualification and experience against each company's conditions
                  </label>
                </FilterField>
              )}
            </>
          ) : (
            <>
              <FilterField icon="⚧" label="Gender" mustKey="gender" must={filters.must} onToggleMust={toggleMust}>
                <Pills options={['Any', 'Male', 'Female']} value={filters.gender} onChange={(v) => setFilter('gender', v)} />
              </FilterField>
              <FilterField icon="🎂" label="Age between" mustKey="age" must={filters.must} onToggleMust={toggleMust}>
                <div className="flex items-center gap-2">
                  <input inputMode="numeric" value={filters.ageMin} onChange={(e) => setFilter('ageMin', e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="From" className={aiInput} />
                  <span className="text-xs font-bold text-slate-400">to</span>
                  <input inputMode="numeric" value={filters.ageMax} onChange={(e) => setFilter('ageMax', e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="To" className={aiInput} />
                </div>
              </FilterField>
              <FilterField icon="💍" label="Marital status" mustKey="marital" must={filters.must} onToggleMust={toggleMust}>
                <Pills options={['Any', 'Single', 'Married']} value={filters.marital} onChange={(v) => setFilter('marital', v)} />
              </FilterField>
              <FilterField icon="🎓" label="Minimum qualification" mustKey="qualification" must={filters.must} onToggleMust={toggleMust}>
                <Pills options={QUAL_LEVELS.map((q) => [String(q.level), q.label])} value={filters.minQualLevel} onChange={(v) => setFilter('minQualLevel', v)} />
                <input value={filters.qualText} onChange={(e) => setFilter('qualText', e.target.value)} placeholder="Specific course (optional) e.g. B.Com" className={`${aiInput} mt-2`} />
              </FilterField>
              <FilterField icon="🧭" label="Experience" mustKey="experience" must={filters.must} onToggleMust={toggleMust}>
                <Pills options={['Any', 'Fresher', 'Experienced']} value={filters.experience} onChange={(v) => setFilter('experience', v)} />
              </FilterField>
              <FilterField icon="💰" label="Salary budget (₹/month, max)" mustKey="salary" must={filters.must} onToggleMust={toggleMust}>
                <input inputMode="numeric" value={filters.salaryBudget} onChange={(e) => setFilter('salaryBudget', e.target.value.replace(/\D/g, ''))} placeholder="e.g. 20000" className={aiInput} />
              </FilterField>
            </>
          )}

          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-dashed border-slate-200 px-3 py-2 text-[11px] font-semibold text-slate-500">
            <input type="checkbox" checked={filters.strictUnknown} onChange={(e) => setFilter('strictUnknown', e.target.checked)} className="mt-0.5 accent-violet-600" />
            Strict: also hide results with missing data for a 🔒 Must requirement
          </label>
        </aside>

        {/* ── Results ── */}
        <section className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded-3xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200/80">
            <p className="mr-1 text-sm font-black text-slate-900">{visible.length} {isJobs ? 'vacanc' : 'candidate'}{isJobs ? (visible.length === 1 ? 'y' : 'ies') : (visible.length === 1 ? '' : 's')}</p>
            {hasCriteria && (
              <>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${AI_TONE.emerald.pill}`}>{bandCounts.excellent} Excellent</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${AI_TONE.sky.pill}`}>{bandCounts.strong} Strong</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${AI_TONE.amber.pill}`}>{bandCounts.fair} Fair</span>
              </>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {hasCriteria && (
                <select value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-600 focus:outline-none focus:ring-4 focus:ring-violet-100">
                  <option value={0}>All scores</option><option value={45}>Fair and above</option><option value={65}>Strong and above</option><option value={80}>Excellent only</option>
                </select>
              )}
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-600 focus:outline-none focus:ring-4 focus:ring-violet-100">
                <option value="score">Best match</option>
                <option value="distance">Nearest first</option>
                {isJobs && <option value="salary">Highest salary</option>}
                <option value="recent">Newest first</option>
              </select>
            </div>
          </div>

          {hiddenReasons.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2.5 text-xs text-amber-800 ring-1 ring-amber-200">
              <span className="font-black">{hidden.length} hidden:</span>
              {hiddenReasons.map(([reason, n]) => <span key={reason} className="rounded-full bg-white px-2 py-0.5 font-semibold ring-1 ring-amber-200">{n} · {reason}</span>)}
              <button type="button" onClick={() => setShowHidden((v) => !v)} className="ml-auto font-bold underline-offset-2 hover:underline">{showHidden ? 'Hide them' : 'Show them'}</button>
            </div>
          )}

          {!hasCriteria && (
            <div className="rounded-2xl bg-gradient-to-r from-violet-50 to-indigo-50 px-4 py-3 text-sm text-violet-800 ring-1 ring-violet-100">
              <span className="font-black">Tip:</span> {isJobs ? 'pick a candidate or type a designation, location or salary' : 'pick a vacancy or set gender, age, qualification or a km radius'} to rank the list. Until then everything is shown unscored.
            </div>
          )}

          {visible.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white py-16 text-center">
              <p className="text-4xl">🔍</p>
              <p className="mt-2 font-black text-slate-700">No {isJobs ? 'vacancies' : 'candidates'} match these requirements</p>
              <p className="mt-1 text-sm text-slate-400">Try a wider radius, remove a 🔒 Must, or lower the score filter.</p>
            </div>
          ) : paged.map((r, i) => {
            const rank = (safePage - 1) * MATCH_PAGE_SIZE + i + 1;
            const isSel = selectedIds.has(r.id);
            const expanded = expandedId === r.id;
            return (
              <div key={r.id} className={`group rounded-3xl bg-white p-4 shadow-sm ring-1 transition hover:shadow-md ${isSel ? 'ring-2 ring-violet-500' : r.reg?.plan_type === 'prime' ? 'ring-amber-300' : 'ring-slate-200/80'}`}>
                <div className="flex items-start gap-3">
                  <button type="button" onClick={() => toggleSelect(r.id)} title="Select"
                    className={`mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 text-[10px] font-black transition ${isSel ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-300 bg-white text-transparent hover:border-violet-400'}`}>✓</button>
                  <div className="flex flex-col items-center gap-1">
                    <ScoreRing score={hasCriteria ? r.score : null} band={hasCriteria ? r.band : bandFor(null)} />
                    <span className="text-[10px] font-black text-slate-300">#{rank}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    {isJobs ? (
                      <>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="text-[15px] font-black text-slate-900">{r.vac.title}</p>
                          {hasCriteria && <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ring-1 ${AI_TONE[r.band.tone].pill}`}>{r.band.label}</span>}
                          {Number(r.vac.total_openings) > 1 && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">{r.vac.openings_left} of {r.vac.total_openings} open</span>}
                          {r.vac.partner_org && <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[10px] font-bold text-fuchsia-700">🤝 {r.vac.partner_org}</span>}
                          {r.preferred && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-200">🎯 Preferred area</span>}
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {[r.vac.company_firm_organisation || r.vac.company, r.vac.company_category, [r.vac.location, r.vac.district].filter(Boolean).join(', '), r.vac.salary && `₹ ${r.vac.salary}`].filter(Boolean).join(' · ')}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="text-[15px] font-black text-slate-900">{r.reg.name}</p>
                          {r.reg.plan_type === 'prime' && <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-black text-black">👑 PRIME</span>}
                          <PartnerTag row={r.reg} />
                          {hasCriteria && <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ring-1 ${AI_TONE[r.band.tone].pill}`}>{r.band.label}</span>}
                          {r.reg.interview_status && r.reg.interview_status !== 'Not Scheduled' && <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${iBadge(r.reg.interview_status)}`}>{r.reg.interview_status}</span>}
                          <span className="text-[11px] text-slate-400">{r.reg.registration_number}</span>
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {[r.reg.expecting_job, r.reg.highest_qualification, r.reg.age && `${r.reg.age} yrs`, r.reg.gender, r.reg.place || r.reg.district].filter(Boolean).join(' · ')}
                        </p>
                      </>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {(r.km !== null || center) && <DistancePill km={r.km} approx={r.approx} />}
                    </div>
                    {r.criteria.length > 0 && <CriteriaChips criteria={r.criteria} />}
                    {expanded && r.criteria.length > 0 && <CriteriaBreakdown criteria={r.criteria} />}
                  </div>
                  <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
                    {r.criteria.length > 0 && (
                      <button type="button" onClick={() => setExpandedId(expanded ? null : r.id)} className="rounded-xl px-2.5 py-1 text-xs font-bold text-violet-600 hover:bg-violet-50">{expanded ? 'Less' : 'Why?'}</button>
                    )}
                    {isJobs ? (
                      activeReg && onGoToPipeline && (
                        <button type="button" onClick={() => onGoToPipeline(activeReg, r.vac.id)} className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1.5 text-xs font-extrabold text-white shadow-sm hover:opacity-90">↗ Pipeline</button>
                      )
                    ) : (
                      <>
                        <button type="button" onClick={() => setProfileReg(r.reg)} className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-200">👤 Profile</button>
                        <div className="flex gap-1">
                          {r.reg.contact_number && <a href={`tel:${getPhoneDigits(r.reg.contact_number)}`} title="Call" className="rounded-xl bg-sky-50 px-2 py-1 text-xs hover:bg-sky-100">📞</a>}
                          {(r.reg.whatsapp_number || r.reg.contact_number) && <a href={`https://wa.me/${getWADigits(r.reg.whatsapp_number || r.reg.contact_number)}`} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="rounded-xl bg-emerald-50 px-2 py-1 text-xs hover:bg-emerald-100">💬</a>}
                        </div>
                        {activeVac && onGoToPipeline && (
                          <button type="button" onClick={() => onGoToPipeline(r.reg, activeVac.id)} className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1.5 text-xs font-extrabold text-white shadow-sm hover:opacity-90">↗ Pipeline</button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {showHidden && hidden.length > 0 && (
            <div className="space-y-2 rounded-3xl bg-slate-50 p-3 ring-1 ring-slate-200">
              <p className="px-1 text-[11px] font-extrabold uppercase tracking-wider text-slate-400">Hidden by your 🔒 Must requirements / radius</p>
              {hidden.map((r) => (
                <div key={r.id} className="flex items-center gap-3 rounded-2xl bg-white px-3 py-2 opacity-80 ring-1 ring-slate-200">
                  <p className="min-w-0 flex-1 truncate text-sm font-bold text-slate-700">{isJobs ? `${r.vac.title} · ${r.vac.company || ''}` : `${r.reg.name} · ${r.reg.expecting_job || ''}`}</p>
                  <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700 ring-1 ring-rose-200">{r.excludedBy.label}: {r.excludedBy.detail}</span>
                  {!isJobs && <button type="button" onClick={() => setProfileReg(r.reg)} className="text-xs font-bold text-slate-500 hover:text-violet-600">Profile</button>}
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
              <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-xl bg-white px-3 py-1.5 text-sm font-bold ring-1 ring-slate-200 disabled:opacity-40">«</button>
              <button type="button" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={safePage === 1} className="rounded-xl bg-white px-3 py-1.5 text-sm font-bold ring-1 ring-slate-200 disabled:opacity-40">‹ Prev</button>
              <span className="px-2 text-sm font-semibold text-slate-500">Page {safePage} of {totalPages}</span>
              <button type="button" onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={safePage === totalPages} className="rounded-xl bg-white px-3 py-1.5 text-sm font-bold ring-1 ring-slate-200 disabled:opacity-40">Next ›</button>
              <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-xl bg-white px-3 py-1.5 text-sm font-bold ring-1 ring-slate-200 disabled:opacity-40">»</button>
            </div>
          )}

          {/* Action bar */}
          {(selectedIds.size > 0 || (!isJobs && visible.length > 0)) && (
            <div className="sticky bottom-4 z-10 flex flex-wrap items-center gap-2 rounded-3xl bg-slate-900/95 px-4 py-3 text-white shadow-2xl backdrop-blur">
              <div className="mr-auto min-w-0">
                <p className="text-sm font-extrabold">{selectedIds.size > 0 ? `${selectedIds.size} selected` : `${visible.length} candidates in this list`}</p>
                {copied ? <p className="text-xs text-emerald-300">{copied}</p> : <p className="truncate text-xs text-white/60">{isJobs ? (activeReg ? `Share with ${activeReg.name}` : 'Pick a candidate to share jobs on WhatsApp') : (activeVac ? `${activeVac.title} · ${activeVac.company}` : 'Select candidates to act on them')}</p>}
              </div>
              {selectedIds.size > 0 && <button type="button" onClick={() => setSelectedIds(new Set())} className="rounded-xl px-3 py-2 text-xs font-bold text-white/70 hover:text-white">Clear</button>}
              {isJobs ? (
                <>
                  <button type="button" onClick={() => copyText(jobsMessage(), 'Job list copied')} className="rounded-xl bg-white/10 px-3 py-2 text-xs font-bold hover:bg-white/20">📋 Copy job list</button>
                  {activeReg && (activeReg.whatsapp_number || activeReg.contact_number) && (
                    <button type="button" onClick={sendJobsOnWhatsApp} className="rounded-xl bg-emerald-500 px-4 py-2 text-xs font-extrabold text-white hover:bg-emerald-400">💬 Send {selectedIds.size} job{selectedIds.size === 1 ? '' : 's'} on WhatsApp</button>
                  )}
                </>
              ) : (
                <>
                  <button type="button" onClick={exportCandidatesCsv} className="rounded-xl bg-white/10 px-3 py-2 text-xs font-bold hover:bg-white/20">⬇ Export {selectedIds.size > 0 ? 'selected' : 'list'} (CSV)</button>
                  {selectedIds.size > 0 && (
                    <>
                      <button type="button" onClick={() => copyText(selectedResults.map(({ reg }) => `${reg.name} - ${reg.contact_number}`).join('\n'), 'Phone numbers copied')} className="rounded-xl bg-white/10 px-3 py-2 text-xs font-bold hover:bg-white/20">📋 Copy numbers</button>
                      <button type="button" onClick={() => setShowGroupModal(true)} className="rounded-xl bg-gradient-to-r from-amber-300 to-yellow-400 px-4 py-2 text-xs font-extrabold text-black hover:opacity-90">📅 Group interview ({selectedIds.size})</button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {showGroupModal && (
        <GroupInterviewModal
          selectedRegs={registrations.filter((r) => selectedIds.has(r.id))}
          vacancies={vacancies}
          initialVacId={selVacId}
          onDone={() => { setShowGroupModal(false); setSelectedIds(new Set()); if (onRefresh) onRefresh(); }}
          onClose={() => setShowGroupModal(false)}
        />
      )}
      {profileReg && <ProfileModal reg={profileReg} onClose={() => setProfileReg(null)} />}
    </div>
  );
};

// ─── Search & Explore Tab ─────────────────────────────────────────────────────
const SearchTab = ({ registrations, vacancies }) => {
  const [keyword, setKeyword] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterQual, setFilterQual] = useState('');
  const [filterJob, setFilterJob] = useState('');
  const [filterMarital, setFilterMarital] = useState('');
  const [filterMinRating, setFilterMinRating] = useState('');
  const [filterHasExp, setFilterHasExp] = useState(false);
  const [filterHasResume, setFilterHasResume] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [matchVacId, setMatchVacId] = useState('');
  const [profileReg, setProfileReg] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [keyword, filterDistrict, filterDept, filterQual, filterJob, filterMarital, filterMinRating, filterHasExp, filterHasResume, filterStatus, sortBy]);

  const openVacs = useMemo(() => vacancies.filter((v) => !Number(v.is_filled)), [vacancies]);
  const matchVac = openVacs.find((v) => String(v.id) === matchVacId) || null;

  const districts = useMemo(() => [...new Set(registrations.map((r) => r.district).filter(Boolean))].sort(), [registrations]);
  const departments = useMemo(() => [...new Set(registrations.map((r) => r.department).filter(Boolean))].sort(), [registrations]);
  const qualifications = useMemo(() => [...new Set(registrations.map((r) => r.highest_qualification).filter(Boolean))].sort(), [registrations]);
  const jobs = useMemo(() => [...new Set(registrations.map((r) => r.expecting_job).filter(Boolean))].sort(), [registrations]);

  const filtered = useMemo(() => {
    let result = [...registrations];
    if (keyword.trim()) {
      const q = keyword.toLowerCase();
      result = result.filter((r) =>
        `${r.name} ${r.registration_number} ${r.email} ${r.contact_number} ${r.place} ${r.highest_qualification} ${r.expecting_job} ${r.department} ${r.section} ${r.district}`.toLowerCase().includes(q)
      );
    }
    if (filterDistrict) result = result.filter((r) => r.district === filterDistrict);
    if (filterDept) result = result.filter((r) => r.department === filterDept);
    if (filterQual) result = result.filter((r) => r.highest_qualification === filterQual);
    if (filterJob) result = result.filter((r) => r.expecting_job === filterJob);
    if (filterMarital) result = result.filter((r) => String(r.marital_status || '').toLowerCase() === filterMarital.toLowerCase());
    if (filterMinRating) {
      const minR = parseFloat(filterMinRating);
      result = result.filter((r) => { const rv = parseFloat(String(r.rating || '').replace(/[^0-9.]/g, '')); return Number.isFinite(rv) && rv >= minR; });
    }
    if (filterHasExp) result = result.filter((r) => Array.isArray(r.experience_details) && r.experience_details.some((e) => e.title_post || e.company_name));
    if (filterHasResume) result = result.filter((r) => !!r.resume_file);
    if (filterStatus === 'placed') result = result.filter((r) => Number(r.payment_received) > 0 || r.interview_status === 'Placed');
    if (filterStatus === 'active') result = result.filter((r) => Number(r.is_shortlisted) > 0 && !Number(r.payment_received) && r.interview_status !== 'Placed');
    if (filterStatus === 'fresh') result = result.filter((r) => !Number(r.is_shortlisted) && !Number(r.payment_received));
    // Sort
    if (sortBy === 'rating') {
      result.sort((a, b) => (parseFloat(String(b.rating || '').replace(/[^0-9.]/g, '')) || 0) - (parseFloat(String(a.rating || '').replace(/[^0-9.]/g, '')) || 0));
    } else if (sortBy === 'match' && matchVac) {
      result.sort((a, b) => matchScore(b, matchVac).score - matchScore(a, matchVac).score);
    } else if (sortBy === 'district') {
      result.sort((a, b) => String(a.district || '').localeCompare(String(b.district || '')));
    } else if (sortBy === 'experience') {
      result.sort((a, b) =>
        (Array.isArray(b.experience_details) ? b.experience_details.filter((e) => e.title_post || e.company_name).length : 0) -
        (Array.isArray(a.experience_details) ? a.experience_details.filter((e) => e.title_post || e.company_name).length : 0)
      );
    } else {
      result.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    }
    return result;
  }, [registrations, keyword, filterDistrict, filterDept, filterQual, filterJob, filterMarital, filterMinRating, filterHasExp, filterHasResume, filterStatus, sortBy, matchVac]);

  const PAGE_SIZE = 25;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const districtBreakdown = useMemo(() => {
    const c = {};
    for (const r of registrations) { const d = r.district || 'Unknown'; c[d] = (c[d] || 0) + 1; }
    return Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [registrations]);

  const qualBreakdown = useMemo(() => {
    const c = {};
    for (const r of registrations) { const q = r.highest_qualification || 'Unknown'; c[q] = (c[q] || 0) + 1; }
    return Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [registrations]);

  const hasActiveFilters = !!(keyword || filterDistrict || filterDept || filterQual || filterJob || filterMarital || filterMinRating || filterHasExp || filterHasResume || filterStatus);

  const clearAll = () => {
    setKeyword(''); setFilterDistrict(''); setFilterDept(''); setFilterQual('');
    setFilterJob(''); setFilterMarital(''); setFilterMinRating('');
    setFilterHasExp(false); setFilterHasResume(false); setFilterStatus('');
  };

  return (
    <div className="flex gap-5 items-start min-h-0">

      {/* ══════════ LEFT PANEL — filters (sticky) ══════════ */}
      <div className="hidden xl:flex flex-col gap-4 w-[300px] flex-shrink-0 sticky top-4 max-h-[calc(100vh-100px)] overflow-y-auto pb-4">

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Total', value: registrations.length, dot: 'bg-gray-800' },
            { label: 'Experience', value: registrations.filter((r) => Array.isArray(r.experience_details) && r.experience_details.some((e) => e.title_post || e.company_name)).length, dot: 'bg-blue-500' },
            { label: 'Has Resume', value: registrations.filter((r) => !!r.resume_file).length, dot: 'bg-amber-400' },
            { label: 'Placed', value: registrations.filter((r) => Number(r.payment_received) > 0).length, dot: 'bg-yellow-400' },
          ].map(({ label, value, dot }) => (
            <div key={label} className="rounded-xl border border-gray-200 bg-white px-3 py-3 flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full flex-shrink-0 ${dot}`} />
              <div>
                <p className="text-lg font-black text-gray-900 leading-none">{value}</p>
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mt-0.5">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search + filters card */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-extrabold uppercase tracking-widest text-gray-500">Search & Filter</p>
            {hasActiveFilters && (
              <button type="button" onClick={clearAll} className="text-[10px] font-bold text-red-500 hover:underline">✕ Clear</button>
            )}
          </div>
          {/* Keyword */}
          <input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Name, reg no, phone, place…"
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          {/* All filters */}
          {[
            { label: 'District', value: filterDistrict, set: setFilterDistrict, opts: districts.map((d) => ({ value: d, label: d })), placeholder: 'All Districts' },
            { label: 'Department', value: filterDept, set: setFilterDept, opts: departments.map((d) => ({ value: d, label: d })), placeholder: 'All Departments' },
            { label: 'Qualification', value: filterQual, set: setFilterQual, opts: qualifications.map((q) => ({ value: q, label: q })), placeholder: 'All Qualifications' },
            { label: 'Expecting Job', value: filterJob, set: setFilterJob, opts: jobs.map((j) => ({ value: j, label: j })), placeholder: 'All Job Types' },
            { label: 'Marital Status', value: filterMarital, set: setFilterMarital, opts: [{ value: 'Single', label: 'Single' }, { value: 'Married', label: 'Married' }, { value: 'Divorced', label: 'Divorced' }, { value: 'Widowed', label: 'Widowed' }], placeholder: 'Any' },
            { label: 'Pipeline Status', value: filterStatus, set: setFilterStatus, opts: [{ value: 'fresh', label: 'Fresh (not shortlisted)' }, { value: 'active', label: 'Active in Pipeline' }, { value: 'placed', label: 'Placed' }], placeholder: 'All' },
          ].map(({ label, value, set, opts, placeholder }) => (
            <label key={label} className="block">
              <span className="text-[10px] font-extrabold uppercase text-gray-400">{label}</span>
              <select value={value} onChange={(e) => set(e.target.value)}
                className="mt-0.5 w-full rounded-xl border border-gray-200 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value="">{placeholder}</option>
                {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          ))}
          {/* Min Rating */}
          <label className="block">
            <span className="text-[10px] font-extrabold uppercase text-gray-400">Min Rating</span>
            <input type="number" min="0" max="10" step="0.5" value={filterMinRating} onChange={(e) => setFilterMinRating(e.target.value)}
              placeholder="e.g. 7" className="mt-0.5 w-full rounded-xl border border-gray-200 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </label>
          {/* Checkboxes */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={filterHasExp} onChange={(e) => setFilterHasExp(e.target.checked)} className="h-4 w-4 rounded" />
              <span className="text-xs font-bold text-gray-700">Has Exp.</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={filterHasResume} onChange={(e) => setFilterHasResume(e.target.checked)} className="h-4 w-4 rounded" />
              <span className="text-xs font-bold text-gray-700">Has Resume</span>
            </label>
          </div>
        </div>

        {/* Sort card */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
          <p className="text-xs font-extrabold uppercase tracking-widest text-gray-500">Sort</p>
          <label className="block">
            <span className="text-[10px] font-extrabold uppercase text-gray-400">Sort By</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="mt-0.5 w-full rounded-xl border border-gray-200 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
              <option value="name">Name A–Z</option>
              <option value="rating">Rating High–Low</option>
              <option value="experience">Experience Count</option>
              <option value="district">District A–Z</option>
              {matchVacId && <option value="match">AI Match Score</option>}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-extrabold uppercase text-gray-400">AI Sort — Vacancy</span>
            <select value={matchVacId} onChange={(e) => { setMatchVacId(e.target.value); if (e.target.value) setSortBy('match'); }}
              className="mt-0.5 w-full rounded-xl border border-gray-200 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
              <option value="">— Pick vacancy for AI ranking —</option>
              {openVacs.map((v) => <option key={v.id} value={String(v.id)}>{v.title} — {v.company}</option>)}
            </select>
          </label>
        </div>

        {/* Quick-click chips */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-4">
          <div>
            <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">By District</p>
            <div className="flex flex-wrap gap-1.5">
              {districtBreakdown.map(([d, c]) => (
                <button key={d} type="button"
                  onClick={() => setFilterDistrict(filterDistrict === d ? '' : d)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-bold transition ${filterDistrict === d ? 'bg-black text-yellow-400 border-black' : 'border-gray-200 text-gray-700 hover:border-black hover:text-black'}`}>
                  {d} <span className={filterDistrict === d ? 'text-yellow-300' : 'text-gray-400'}>{c}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">By Qualification</p>
            <div className="flex flex-wrap gap-1.5">
              {qualBreakdown.map(([q, c]) => (
                <button key={q} type="button"
                  onClick={() => setFilterQual(filterQual === q ? '' : q)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-bold transition ${filterQual === q ? 'bg-black text-yellow-400 border-black' : 'border-gray-200 text-gray-700 hover:border-black hover:text-black'}`}>
                  {q} <span className={filterQual === q ? 'text-yellow-300' : 'text-gray-400'}>{c}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ══════════ RIGHT PANEL — results ══════════ */}
      <div className="flex-1 min-w-0 space-y-4">

        {/* Mobile-only compact filter row */}
        <div className="flex flex-wrap gap-2 xl:hidden">
          <input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Search…"
            className="flex-1 min-w-[180px] rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          {hasActiveFilters && (
            <button type="button" onClick={clearAll} className="rounded-xl border border-red-200 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-50">✕ Clear</button>
          )}
        </div>

        {/* Result count header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`rounded-full px-4 py-1.5 text-sm font-extrabold ${hasActiveFilters ? 'bg-black text-yellow-400' : 'bg-gray-100 text-gray-600'}`}>
              {filtered.length} member{filtered.length !== 1 ? 's' : ''}
            </span>
            {hasActiveFilters && (
              <span className="text-xs text-gray-400">filtered from {registrations.length}</span>
            )}
          </div>
          {/* Mobile sort */}
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="xl:hidden rounded-xl border border-gray-200 px-2 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400">
            <option value="name">Name A–Z</option>
            <option value="rating">Rating ↓</option>
            <option value="experience">Exp. ↓</option>
            <option value="district">District A–Z</option>
          </select>
        </div>

        {/* Results table */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 py-16 text-center">
            <p className="text-4xl mb-3">🔍</p>
            <p className="font-bold text-gray-500">No members match your filters.</p>
            {hasActiveFilters && <button type="button" onClick={clearAll} className="mt-3 text-sm text-indigo-600 font-bold hover:underline">Clear all filters</button>}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-black">
                  {['Name / Reg No', 'Contact', 'District', 'Qualification', 'Dept / Job', 'Exp', 'Rating / AI', 'Status', ''].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-yellow-400 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((r, idx) => {
                  const score = matchVac ? matchScore(r, matchVac).score : null;
                  const isPlacedMember = Number(r.payment_received) > 0 || r.interview_status === 'Placed';
                  const expCount = Array.isArray(r.experience_details) ? r.experience_details.filter((e) => e.title_post || e.company_name).length : 0;
                  return (
                    <tr key={r.id} className={`border-t border-gray-100 transition-colors ${r.plan_type === 'prime' ? 'bg-yellow-50 hover:bg-yellow-100' : idx % 2 === 0 ? 'bg-white hover:bg-yellow-50/30' : 'bg-gray-50/50 hover:bg-yellow-50/30'}`}>
                      <td className="px-4 py-3 min-w-[160px]">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-black text-gray-900">{r.name}</p>
                          {r.plan_type === 'prime' && <span className="rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-black text-black leading-none">👑 PRIME</span>}
                        </div>
                        <p className="text-[11px] text-gray-400">{r.registration_number}</p>
                      </td>
                      <td className="px-4 py-3 min-w-[120px]">
                        <ContactLink value={r.contact_number} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">{r.district || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 min-w-[100px]">{r.highest_qualification || '—'}</td>
                      <td className="px-4 py-3 min-w-[130px]">
                        <p className="text-xs font-semibold text-gray-800">{r.department || '—'}</p>
                        <p className="text-[11px] text-gray-400">{r.expecting_job || '—'}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {expCount > 0
                          ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-extrabold text-blue-700">{expCount} job{expCount > 1 ? 's' : ''}</span>
                          : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 min-w-[80px]">
                        {r.rating ? <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-extrabold text-amber-700">{r.rating}</span> : <span className="text-gray-300 text-xs">—</span>}
                        {score != null && (
                          <div className={`mt-0.5 inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${sBadge(score)}`}>AI {score}%</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isPlacedMember
                          ? <span className="rounded-full bg-black px-2 py-0.5 text-[10px] font-extrabold text-yellow-400">🏆 Placed</span>
                          : r.interview_status && r.interview_status !== 'Not Scheduled'
                            ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${iBadge(r.interview_status)}`}>{r.interview_status}</span>
                            : Number(r.is_shortlisted) > 0
                              ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold text-amber-700">Active</span>
                              : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">Fresh</span>}
                      </td>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setProfileReg(r)} className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 whitespace-nowrap">👤 Profile</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">«</button>
            <button type="button" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
              .map((n, i, arr) => (
                <React.Fragment key={n}>
                  {i > 0 && arr[i - 1] !== n - 1 && <span className="text-gray-400 text-sm px-1">…</span>}
                  <button type="button" onClick={() => setPage(n)}
                    className={`rounded-xl border px-3 py-1.5 text-sm font-bold transition ${safePage === n ? 'bg-black text-yellow-400 border-black' : 'hover:bg-gray-50'}`}>
                    {n}
                  </button>
                </React.Fragment>
              ))}
            <button type="button" onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next ›</button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">»</button>
            <span className="text-sm text-gray-500 ml-2">Page {safePage} of {totalPages}</span>
          </div>
        )}
      </div>

      {profileReg && <ProfileModal reg={profileReg} onClose={() => setProfileReg(null)} />}
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

// ─── Resume Creator ───────────────────────────────────────────────────────────
const genResumeCareerSummary = (d) => {
  const allExp = d.experience || [];
  const techSkills = d.technicalSkills || [];
  const softSkills = d.softSkills || [];
  const role = d.targetRole || allExp[0]?.title || 'Professional';
  const industry = d.targetIndustry || '';
  const topSkills = [...techSkills, ...softSkills].slice(0, 4).join(', ');
  let totalMonths = 0;
  for (const exp of allExp) {
    if (!exp.from) continue;
    const from = new Date(exp.from + '-01');
    const toDate = exp.current ? new Date() : (exp.to ? new Date(exp.to + '-01') : new Date());
    totalMonths += Math.max(0, (toDate - from) / (1000 * 60 * 60 * 24 * 30));
  }
  const yrs = Math.floor(totalMonths / 12);
  const recentCompany = allExp[0]?.company || '';
  if (yrs === 0 && allExp.filter(e => e.company).length === 0) {
    return `A motivated and enthusiastic ${role}${industry ? ` aspiring to grow in the ${industry} sector` : ''}. Demonstrates a strong academic foundation${topSkills ? `, with competencies in ${topSkills}` : ''}. Eager to apply theoretical expertise in a professional environment, contribute positively to team goals, and deliver consistent results. Known for a proactive mindset, willingness to learn, and a commitment to personal and professional excellence.`;
  } else if (yrs <= 2) {
    return `A driven and results-focused ${role}${yrs > 0 ? ` with ${yrs}+ year${yrs > 1 ? 's' : ''} of professional experience` : ''}${industry ? ` in the ${industry} domain` : ''}${recentCompany ? `, currently contributing at ${recentCompany}` : ''}. ${topSkills ? `Proficient in ${topSkills}, and` : 'Demonstrates strong capabilities and'} consistently recognised for a diligent work ethic and collaborative approach. Committed to continuous learning and delivering solutions that create measurable value.`;
  } else {
    return `A seasoned ${role} with over ${yrs} years of progressive experience${industry ? ` across the ${industry} sector` : ''}${recentCompany ? `, presently associated with ${recentCompany}` : ''}. Proven track record of${topSkills ? ` expertise in ${topSkills}, and` : ''} driving strategic outcomes, leading cross-functional initiatives, and achieving business objectives. Known for strong decision-making, mentoring capabilities, and a consistent focus on quality, growth, and organisational excellence.`;
  }
};

const ChipInput = ({ chips, onAdd, onRemove, placeholder }) => {
  const [val, setVal] = useState('');
  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-2 flex flex-wrap gap-1.5 min-h-[48px] cursor-text"
      onClick={(e) => e.currentTarget.querySelector('input')?.focus()}
    >
      {chips.map((c, i) => (
        <span key={i} className="flex items-center gap-1 rounded-full bg-black text-yellow-400 px-3 py-1 text-xs font-bold">
          {c}
          <button type="button" className="ml-1 text-yellow-400/60 hover:text-yellow-200 font-black text-base leading-none" onClick={() => onRemove(i)}>×</button>
        </span>
      ))}
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ',') && val.trim()) {
            e.preventDefault();
            onAdd(val.trim());
            setVal('');
          }
        }}
        placeholder={chips.length === 0 ? placeholder : 'Add more… (Enter or comma)'}
        className="flex-1 min-w-[100px] bg-transparent text-sm outline-none px-1 py-0.5 text-gray-800 placeholder:text-gray-400"
      />
    </div>
  );
};

// ─── 10 Resume Templates ──────────────────────────────────────────────────────
const RESUME_TEMPLATES = [
  { id: 't1',  name: 'Executive Black',  cat: 'Bold',      desc: 'Dark gradient · gold accents',         layout: 'sidebar',     h: { bg: 'linear-gradient(135deg,#000 0%,#1c1c00 100%)',  text: '#fff',    accent: '#fbbf24' }, s: { bg: '#f4f4f4', text: '#222', accent: '#fbbf24', c1bg: '#111',    c1: '#fbbf24', c2bg: '#e5e7eb', c2: '#333',  c3bg: '#dbeafe',  c3: '#1e3a8a', w: '32%' }, b: { bg: '#fff',     text: '#111', accent: '#fbbf24', hc: '#000'    }, photo: 'rounded', eb: '#fbbf24' },
  { id: 't2',  name: 'Navy Corporate',   cat: 'Corporate', desc: 'Full-height navy sidebar',             layout: 'integrated',  h: { bg: '#1b2e4b',                                         text: '#fff',    accent: '#5da8d4' }, s: { bg: '#1b2e4b', text: '#cde0f0', accent: '#5da8d4', c1bg: '#1e3d5e', c1: '#9cc9e8', c2bg: '#162436', c2: '#b0d0e8', c3bg: '#0f1e30', c3: '#8ab8d8', w: '30%' }, b: { bg: '#fff',     text: '#222', accent: '#1b2e4b', hc: '#1b2e4b' }, photo: 'circle',  eb: '#1b2e4b' },
  { id: 't3',  name: 'Classic Clean',    cat: 'Minimal',   desc: 'White · bold red accents',            layout: 'minimal',     h: { bg: '#fff',                                             text: '#111',    accent: '#c0392b' }, s: { bg: '#f8f8f8', text: '#333', accent: '#c0392b', c1bg: '#fdf2f2', c1: '#c0392b', c2bg: '#f0f0f0', c2: '#444', c3bg: '#eef2ff', c3: '#4338ca', w: '36%' }, b: { bg: '#fff',     text: '#222', accent: '#c0392b', hc: '#c0392b' }, photo: 'circle',  eb: '#c0392b' },
  { id: 't4',  name: 'Sage Professional',cat: 'Nature',    desc: 'Elegant sage green sidebar',          layout: 'sidebar',     h: { bg: 'linear-gradient(135deg,#3a6044 0%,#4e7c59 100%)', text: '#fff',    accent: '#a7d4b2' }, s: { bg: '#4e7c59', text: '#e0f0e4', accent: '#a7d4b2', c1bg: 'rgba(255,255,255,0.22)', c1: '#fff', c2bg: 'rgba(255,255,255,0.12)', c2: '#d8f0dc', c3bg: 'rgba(255,255,255,0.08)', c3: '#c5e8c9', w: '30%' }, b: { bg: '#faf9f7', text: '#2a3d2e', accent: '#4e7c59', hc: '#3a6044' }, photo: 'circle',  eb: '#4e7c59' },
  { id: 't5',  name: 'Royal Indigo',     cat: 'Premium',   desc: 'Deep indigo · lavender shimmer',      layout: 'sidebar',     h: { bg: 'linear-gradient(135deg,#1e1b4b 0%,#312e81 100%)', text: '#fff',    accent: '#a5b4fc' }, s: { bg: '#1e1b4b', text: '#c7d2fe', accent: '#a5b4fc', c1bg: '#312e81', c1: '#c7d2fe', c2bg: '#272469', c2: '#e0e7ff', c3bg: '#1e1b58', c3: '#a5b4fc', w: '31%' }, b: { bg: '#fff',     text: '#1e1b4b', accent: '#818cf8', hc: '#312e81' }, photo: 'circle',  eb: '#818cf8' },
  { id: 't6',  name: 'Warm Terracotta',  cat: 'Warm',      desc: 'Earthy terracotta · cream tones',     layout: 'sidebar',     h: { bg: 'linear-gradient(135deg,#7a3621 0%,#c25c38 100%)', text: '#fff',    accent: '#f5ddd0' }, s: { bg: '#f5e6d8', text: '#4a2218', accent: '#c25c38', c1bg: '#7a3621', c1: '#f5e6d8', c2bg: '#edddd1', c2: '#5a2a1a', c3bg: '#e8d4c4', c3: '#4a1f12', w: '30%' }, b: { bg: '#fff',     text: '#3d1a0c', accent: '#c25c38', hc: '#7a3621' }, photo: 'rounded', eb: '#c25c38' },
  { id: 't7',  name: 'Steel & Orange',   cat: 'Tech',      desc: 'Dark steel · vibrant orange pop',     layout: 'sidebar',     h: { bg: 'linear-gradient(135deg,#111827 0%,#1f2937 100%)', text: '#fff',    accent: '#fb923c' }, s: { bg: '#111827', text: '#d1d5db', accent: '#fb923c', c1bg: '#fb923c', c1: '#fff',    c2bg: '#374151',  c2: '#e5e7eb', c3bg: '#1f2937',  c3: '#9ca3af', w: '31%' }, b: { bg: '#f9fafb', text: '#111827', accent: '#f97316', hc: '#111827' }, photo: 'rounded', eb: '#f97316' },
  { id: 't8',  name: 'Forest & Gold',    cat: 'Nature',    desc: 'Rich forest green · gold highlights', layout: 'sidebar',     h: { bg: 'linear-gradient(135deg,#052e16 0%,#14532d 100%)', text: '#fff',    accent: '#fbbf24' }, s: { bg: '#052e16', text: '#a7f3d0', accent: '#fbbf24', c1bg: '#ca8a04', c1: '#fff',    c2bg: '#14532d',  c2: '#a7f3d0', c3bg: '#166534',  c3: '#d1fae5', w: '30%' }, b: { bg: '#fff',     text: '#052e16', accent: '#ca8a04', hc: '#14532d' }, photo: 'circle',  eb: '#ca8a04' },
  { id: 't9',  name: 'Rose Elegant',     cat: 'Elegant',   desc: 'Sophisticated deep rose design',      layout: 'sidebar',     h: { bg: 'linear-gradient(135deg,#4c0519 0%,#881337 100%)', text: '#fff',    accent: '#fda4af' }, s: { bg: '#4c0519', text: '#fecdd3', accent: '#fda4af', c1bg: '#881337', c1: '#fecdd3', c2bg: '#3d0414',  c2: '#fecdd3', c3bg: '#360310',  c3: '#fda4af', w: '30%' }, b: { bg: '#fff',     text: '#3d0414', accent: '#be123c', hc: '#881337' }, photo: 'circle',  eb: '#be123c' },
  { id: 't10', name: 'Midnight Teal',    cat: 'Modern',    desc: 'Deep teal · luminous cyan accents',  layout: 'sidebar',     h: { bg: 'linear-gradient(135deg,#042f2e 0%,#0d3341 100%)', text: '#fff',    accent: '#2dd4bf' }, s: { bg: '#042f2e', text: '#99f6e4', accent: '#2dd4bf', c1bg: '#0f766e', c1: '#ccfbf1', c2bg: '#115e59',  c2: '#a7f3d0', c3bg: '#0d4a43',  c3: '#6ee7b7', w: '31%' }, b: { bg: '#fff',     text: '#042f2e', accent: '#2dd4bf', hc: '#0d3341' }, photo: 'circle',  eb: '#2dd4bf' },
];

const ResumeDocument = ({ data, careerSummary, templateId = 't1' }) => {
  const tpl = RESUME_TEMPLATES.find(t => t.id === templateId) || RESUME_TEMPLATES[0];
  const cs = careerSummary || genResumeCareerSummary(data);

  const photoShape = tpl.photo === 'circle' ? '50%' : tpl.photo === 'rounded' ? '14px' : '4px';
  const photoEl = (size = 72) => data.photoPreview
    ? <img src={data.photoPreview} alt="" style={{ width: size, height: size, borderRadius: photoShape, objectFit: 'cover', border: `3px solid ${tpl.h.accent}`, flexShrink: 0 }} />
    : <div style={{ width: size, height: size, borderRadius: photoShape, background: tpl.h.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: Math.round(size * 0.38), color: '#000', flexShrink: 0 }}>{(data.name || '?')[0]?.toUpperCase()}</div>;

  const SH = (title, accent, dark = false) => (
    <div style={{ fontSize: '8.5px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '2.5px', color: dark ? `${tpl.s.accent}bb` : '#999', borderBottom: `2px solid ${accent}`, paddingBottom: '4px', marginBottom: '10px' }}>{title}</div>
  );

  const Chips = (items, bg, color) => !items?.length ? null : (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '6px' }}>
      {items.map((s, i) => <span key={i} style={{ background: bg, color, borderRadius: '20px', padding: '2px 8px', fontSize: '9px', fontWeight: 700, lineHeight: 1.5 }}>{s}</span>)}
    </div>
  );

  const expDate = (exp) => [exp.from, exp.current ? 'Present' : exp.to].filter(Boolean).join(' – ');

  const Decl = () => (
    <div style={{ marginTop: 'auto', paddingTop: '14px', borderTop: `1px solid ${tpl.b.accent}30` }}>
      <div style={{ fontSize: '8px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '2px', color: tpl.b.accent, marginBottom: '5px' }}>Declaration</div>
      <p style={{ fontSize: '9.5px', color: '#888', lineHeight: 1.75, fontStyle: 'italic' }}>I hereby declare that all the information furnished above is true, accurate, and complete to the best of my knowledge and belief. Any false or misleading information may lead to disqualification or termination of employment.</p>
      <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '9.5px', color: '#666' }}>
        <span>Place: {data.place || '_______________'}   Date: {data.declarationDate || '_______________'}</span>
        <span style={{ fontWeight: 800, color: '#111' }}>{data.name || '(Signature)'}</span>
      </div>
    </div>
  );

  const SkillsBlk = (dark) => {
    const hasTech = data.technicalSkills?.length > 0, hasSoft = data.softSkills?.length > 0, hasT = data.tools?.length > 0;
    if (!hasTech && !hasSoft && !hasT) return null;
    return (
      <div style={{ marginBottom: '18px' }}>
        {SH('Skills', dark ? tpl.s.accent : tpl.b.accent, dark)}
        {hasTech && <div style={{ marginBottom: '7px' }}><div style={{ fontSize: '8px', fontWeight: 800, color: dark ? `${tpl.s.text}88` : '#bbb', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Technical</div>{Chips(data.technicalSkills, tpl.s.c1bg, tpl.s.c1)}</div>}
        {hasSoft && <div style={{ marginBottom: '7px' }}><div style={{ fontSize: '8px', fontWeight: 800, color: dark ? `${tpl.s.text}88` : '#bbb', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Soft Skills</div>{Chips(data.softSkills, tpl.s.c2bg, tpl.s.c2)}</div>}
        {hasT && <div><div style={{ fontSize: '8px', fontWeight: 800, color: dark ? `${tpl.s.text}88` : '#bbb', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Tools</div>{Chips(data.tools, tpl.s.c3bg, tpl.s.c3)}</div>}
      </div>
    );
  };

  const EduBlk = (dark) => {
    const items = data.education?.filter(e => e.degree || e.institution) || [];
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: '18px' }}>
        {SH('Education', dark ? tpl.s.accent : tpl.b.accent, dark)}
        {items.map((e, i) => (
          <div key={i} style={{ marginBottom: '10px' }}>
            <div style={{ fontWeight: 800, fontSize: '10px', color: dark ? '#fff' : tpl.b.hc, lineHeight: 1.3 }}>{e.degree}</div>
            {e.specialization && <div style={{ fontSize: '9.5px', color: dark ? tpl.s.text : '#666', fontStyle: 'italic' }}>{e.specialization}</div>}
            <div style={{ fontSize: '9.5px', color: dark ? tpl.s.text : '#555', fontWeight: 600 }}>{e.institution}</div>
            {(e.year || e.grade) && <div style={{ fontSize: '9px', color: dark ? `${tpl.s.text}80` : '#bbb' }}>{e.year}{e.grade ? ` · ${e.grade}` : ''}</div>}
          </div>
        ))}
      </div>
    );
  };

  const LangBlk = (dark) => {
    const items = data.languages?.filter(l => l.lang) || [];
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: '18px' }}>
        {SH('Languages', dark ? tpl.s.accent : tpl.b.accent, dark)}
        {items.map((l, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', fontSize: '10px' }}>
            <span style={{ fontWeight: 700, color: dark ? '#fff' : '#333' }}>{l.lang}</span>
            <span style={{ color: dark ? `${tpl.s.text}88` : '#aaa', fontSize: '9px', fontStyle: 'italic' }}>{l.proficiency}</span>
          </div>
        ))}
      </div>
    );
  };

  const CertBlk = (dark) => {
    const items = data.certifications?.filter(c => c.name) || [];
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: '18px' }}>
        {SH('Certifications', dark ? tpl.s.accent : tpl.b.accent, dark)}
        {items.map((c, i) => (
          <div key={i} style={{ marginBottom: '8px' }}>
            <div style={{ fontWeight: 700, fontSize: '10px', color: dark ? '#fff' : '#333' }}>{c.name}</div>
            {(c.issuer || c.year) && <div style={{ fontSize: '9px', color: dark ? `${tpl.s.text}88` : '#aaa' }}>{c.issuer}{c.year ? ` · ${c.year}` : ''}</div>}
          </div>
        ))}
      </div>
    );
  };

  const HobbiesBlk = (dark) => !data.hobbies ? null : (
    <div style={{ marginBottom: '18px' }}>
      {SH('Interests', dark ? tpl.s.accent : tpl.b.accent, dark)}
      <p style={{ fontSize: '9.5px', color: dark ? tpl.s.text : '#666', lineHeight: 1.65 }}>{data.hobbies}</p>
    </div>
  );

  const ExpBlk = () => {
    const items = data.experience?.filter(e => e.title || e.company) || [];
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: '18px' }}>
        {SH('Work Experience', tpl.b.accent)}
        {items.map((exp, i) => (
          <div key={i} style={{ marginBottom: '14px', paddingLeft: '10px', borderLeft: `2.5px solid ${tpl.eb}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: '11px', color: tpl.b.hc, lineHeight: 1.3 }}>{exp.title}</div>
                <div style={{ fontWeight: 600, fontSize: '10px', color: '#666', marginTop: '1px' }}>{exp.company}{exp.location ? ` · ${exp.location}` : ''}</div>
              </div>
              <div style={{ fontSize: '9px', color: '#aaa', whiteSpace: 'nowrap', flexShrink: 0, marginTop: '2px' }}>{expDate(exp)}</div>
            </div>
            {exp.responsibilities && (
              <ul style={{ marginTop: '5px', paddingLeft: '13px', color: '#555', fontSize: '10px', lineHeight: 1.8 }}>
                {exp.responsibilities.split('\n').filter(Boolean).map((line, j) => <li key={j} style={{ marginBottom: '2px', listStyleType: 'disc' }}>{line.replace(/^[-•*]\s*/, '')}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  };

  const AchBlk = () => {
    const items = data.achievements?.filter(a => a.text) || [];
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: '18px' }}>
        {SH('Key Achievements', tpl.b.accent)}
        <ul style={{ paddingLeft: '14px', color: '#555', fontSize: '10px', lineHeight: 1.8 }}>
          {items.map((a, i) => <li key={i} style={{ marginBottom: '3px', listStyleType: 'disc' }}>{a.text}</li>)}
        </ul>
      </div>
    );
  };

  const BASE = { fontFamily: "'Segoe UI', Arial, sans-serif", fontSize: '11px', lineHeight: '1.55', color: tpl.b.text };
  // Detect dark sidebar
  const darkSidebar = !tpl.s.bg.startsWith('#f') && !tpl.s.bg.startsWith('#e') && tpl.s.bg !== '#fff';

  // ── Layout: SIDEBAR-LEFT ─────────────────────────────────────────────────────
  if (tpl.layout === 'sidebar') return (
    <div style={{ ...BASE, width: '794px', minHeight: '1123px', background: tpl.b.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Full-width header */}
      <div style={{ background: tpl.h.bg, padding: '28px 96px 26px 96px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
          {photoEl(74)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '27px', fontWeight: 900, color: tpl.h.text, letterSpacing: '-0.5px', lineHeight: 1.1 }}>{data.name || 'Your Name'}</div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: tpl.h.accent, textTransform: 'uppercase', letterSpacing: '2.5px', marginTop: '4px', marginBottom: '9px' }}>{data.targetRole || 'Your Role'}</div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', color: `${tpl.h.text}b0`, fontSize: '9.5px', lineHeight: 1.4 }}>
              {data.phone && <span>📞 {data.phone}</span>}
              {data.email && <span>✉ {data.email}</span>}
              {(data.city || data.state) && <span>📍 {[data.city, data.state].filter(Boolean).join(', ')}</span>}
              {data.linkedin && <span>🔗 {data.linkedin.replace(/https?:\/\//, '')}</span>}
              {data.nationality && <span>🌍 {data.nationality}</span>}
            </div>
          </div>
        </div>
      </div>
      {/* Body */}
      <div style={{ display: 'flex', flex: 1 }}>
        {/* Sidebar */}
        <div style={{ width: tpl.s.w, background: tpl.s.bg, padding: '24px 16px 24px 96px', borderRight: `1px solid ${darkSidebar ? 'rgba(255,255,255,0.08)' : '#e8e8e8'}`, flexShrink: 0 }}>
          {SkillsBlk(darkSidebar)}
          {EduBlk(darkSidebar)}
          {LangBlk(darkSidebar)}
          {CertBlk(darkSidebar)}
          {HobbiesBlk(darkSidebar)}
        </div>
        {/* Main */}
        <div style={{ flex: 1, padding: '24px 96px 32px 24px', background: tpl.b.bg, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {cs && <div style={{ marginBottom: '18px' }}>{SH('Career Summary', tpl.b.accent)}<p style={{ fontSize: '10.5px', color: '#555', lineHeight: 1.8, textAlign: 'justify' }}>{cs}</p></div>}
          {ExpBlk()}
          {AchBlk()}
          {Decl()}
        </div>
      </div>
    </div>
  );

  // ── Layout: INTEGRATED SIDEBAR (name/contact inside sidebar) ─────────────────
  if (tpl.layout === 'integrated') return (
    <div style={{ ...BASE, width: '794px', minHeight: '1123px', background: tpl.b.bg, display: 'flex' }}>
      {/* Left full-height sidebar */}
      <div style={{ width: tpl.s.w, background: tpl.s.bg, padding: '40px 16px 32px 96px', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        {photoEl(80)}
        <div style={{ marginTop: '16px', marginBottom: '20px' }}>
          <div style={{ fontSize: '19px', fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>{data.name || 'Your Name'}</div>
          <div style={{ fontSize: '9.5px', fontWeight: 700, color: tpl.s.accent, textTransform: 'uppercase', letterSpacing: '1.5px', marginTop: '4px' }}>{data.targetRole || 'Your Role'}</div>
        </div>
        {SH('Contact', tpl.s.accent, true)}
        <div style={{ fontSize: '9.5px', color: tpl.s.text, lineHeight: 2, marginBottom: '20px' }}>
          {data.phone && <div>📞 {data.phone}</div>}
          {data.email && <div style={{ wordBreak: 'break-all' }}>✉ {data.email}</div>}
          {(data.city || data.state) && <div>📍 {[data.city, data.state].filter(Boolean).join(', ')}</div>}
          {data.linkedin && <div style={{ wordBreak: 'break-all' }}>🔗 {data.linkedin.replace(/https?:\/\//, '')}</div>}
          {data.nationality && <div>🌍 {data.nationality}</div>}
        </div>
        {SkillsBlk(true)}
        {LangBlk(true)}
        {CertBlk(true)}
        {HobbiesBlk(true)}
        {data.dob && <div style={{ fontSize: '9px', color: `${tpl.s.text}55`, marginTop: 'auto' }}>DOB: {data.dob}</div>}
      </div>
      {/* Right main */}
      <div style={{ flex: 1, padding: '40px 96px 32px 32px', display: 'flex', flexDirection: 'column', background: tpl.b.bg }}>
        {cs && <div style={{ marginBottom: '18px' }}>{SH('Profile Summary', tpl.b.accent)}<p style={{ fontSize: '10.5px', color: '#555', lineHeight: 1.8, textAlign: 'justify' }}>{cs}</p></div>}
        {ExpBlk()}
        {EduBlk(false)}
        {AchBlk()}
        {Decl()}
      </div>
    </div>
  );

  // ── Layout: MINIMAL (clean header, two-column no bg) ─────────────────────────
  return (
    <div style={{ ...BASE, width: '794px', minHeight: '1123px', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Header bar */}
      <div style={{ padding: '36px 96px 24px 96px', borderBottom: `3px solid ${tpl.h.accent}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '30px', fontWeight: 900, color: '#111', letterSpacing: '-1px', lineHeight: 1.1 }}>{data.name || 'Your Name'}</div>
            <div style={{ fontSize: '11.5px', fontWeight: 700, color: tpl.h.accent, textTransform: 'uppercase', letterSpacing: '2.5px', marginTop: '5px' }}>{data.targetRole || 'Your Role'}</div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', color: '#999', fontSize: '9.5px', marginTop: '9px', lineHeight: 1.4 }}>
              {data.phone && <span>📞 {data.phone}</span>}
              {data.email && <span>✉ {data.email}</span>}
              {(data.city || data.state) && <span>📍 {[data.city, data.state].filter(Boolean).join(', ')}</span>}
              {data.linkedin && <span>🔗 {data.linkedin.replace(/https?:\/\//, '')}</span>}
              {data.nationality && <span>🌍 {data.nationality}</span>}
            </div>
          </div>
          {photoEl(82)}
        </div>
      </div>
      {/* Two-column body */}
      <div style={{ display: 'flex', flex: 1, padding: '0 96px' }}>
        {/* Left (62%) */}
        <div style={{ flex: '0 0 62%', paddingRight: '28px', paddingTop: '22px', display: 'flex', flexDirection: 'column' }}>
          {cs && <div style={{ marginBottom: '18px' }}>{SH('Career Objective', tpl.b.accent)}<p style={{ fontSize: '10.5px', color: '#555', lineHeight: 1.8, textAlign: 'justify' }}>{cs}</p></div>}
          {ExpBlk()}
          {AchBlk()}
          {Decl()}
        </div>
        {/* Right (38%) no bg */}
        <div style={{ flex: '0 0 38%', borderLeft: '1.5px solid #f0f0f0', paddingLeft: '24px', paddingTop: '22px' }}>
          {SkillsBlk(false)}
          {EduBlk(false)}
          {LangBlk(false)}
          {CertBlk(false)}
          {HobbiesBlk(false)}
        </div>
      </div>
    </div>
  );
};

const RC_STEPS = [
  { label: 'Template', icon: '🎨' },
  { label: 'Personal Info', icon: '👤' },
  { label: 'Career Target', icon: '🎯' },
  { label: 'Experience', icon: '💼' },
  { label: 'Education', icon: '🎓' },
  { label: 'Skills', icon: '⚡' },
  { label: 'Languages & More', icon: '🌐' },
  { label: 'Preview & Download', icon: '📄' },
];

const EMPTY_EXP = () => ({ title: '', company: '', location: '', from: '', to: '', current: false, responsibilities: '' });
const EMPTY_EDU = () => ({ degree: '', institution: '', year: '', grade: '', specialization: '' });
const EMPTY_CERT = () => ({ name: '', issuer: '', year: '' });
const EMPTY_ACH = () => ({ text: '' });

const ResumeCreatorTab = () => {
  const [step, setStep] = useState(0);
  const [templateId, setTemplateId] = useState('t1');
  const [data, setData] = useState({
    name: '', email: '', phone: '', city: '', state: '',
    linkedin: '', website: '', dob: '', nationality: '',
    photoPreview: null,
    targetRole: '', targetIndustry: '',
    experience: [EMPTY_EXP()],
    education: [EMPTY_EDU()],
    technicalSkills: [], softSkills: [], tools: [],
    languages: [{ lang: 'English', proficiency: 'Fluent' }],
    certifications: [EMPTY_CERT()],
    achievements: [EMPTY_ACH()],
    hobbies: '',
    place: '', declarationDate: new Date().toISOString().split('T')[0],
  });
  const [careerSummary, setCareerSummary] = useState('');
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);
  const resumeRef = useRef(null);

  const upd = (key, val) => setData(d => ({ ...d, [key]: val }));

  const handleGenerateSummary = () => {
    setGenerating(true);
    setTimeout(() => {
      setCareerSummary(genResumeCareerSummary(data));
      setSummaryGenerated(true);
      setGenerating(false);
    }, 1400);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => upd('photoPreview', ev.target?.result);
    reader.readAsDataURL(file);
  };

  const updArr = (key, i, k, v) => { const arr = [...data[key]]; arr[i] = { ...arr[i], [k]: v }; upd(key, arr); };
  const addArr = (key, empty) => upd(key, [...data[key], empty()]);
  const remArr = (key, i) => upd(key, data[key].filter((_, idx) => idx !== i));

  const handlePrint = () => {
    if (!resumeRef.current) return;
    const content = resumeRef.current.innerHTML;
    const win = window.open('', '_blank', 'width=960,height=800');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Resume – ${data.name || 'Resume'}</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Segoe UI',Arial,sans-serif;font-size:11px;background:#fff;}ul{list-style:disc;}@page{size:A4 portrait;margin:25.4mm;}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style></head><body>${content}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  const fld = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100';
  const lbl = 'block text-[10px] font-extrabold text-gray-600 mb-1.5 uppercase tracking-wider';

  const renderStep = () => {
    switch (step) {
      /* ── Step 0: Template picker ───────────────────────────────────────────── */
      case 0: return (
        <div className="space-y-5">
          <p className="text-sm text-gray-500 leading-relaxed">Choose a design that best represents you. You can always switch later — your data is always preserved.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {RESUME_TEMPLATES.map(tpl => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => setTemplateId(tpl.id)}
                className={`rounded-2xl border-2 p-2 text-left transition focus:outline-none ${templateId === tpl.id ? 'border-black ring-2 ring-black ring-offset-2 shadow-lg' : 'border-gray-200 hover:border-gray-400'}`}
              >
                {/* Mini thumbnail */}
                <div className="rounded-xl overflow-hidden mb-2 border border-gray-100" style={{ height: '100px' }}>
                  {/* Header strip */}
                  <div style={{ height: '36px', background: tpl.h.bg, display: 'flex', alignItems: 'center', padding: '0 6px', gap: '5px' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: tpl.photo === 'circle' ? '50%' : '4px', background: tpl.h.accent, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ height: '4px', borderRadius: '2px', background: tpl.h.text, opacity: 0.9, marginBottom: '3px', width: '65%' }} />
                      <div style={{ height: '3px', borderRadius: '2px', background: tpl.h.accent, width: '45%' }} />
                    </div>
                  </div>
                  {/* Body */}
                  <div style={{ display: 'flex', height: '64px' }}>
                    {tpl.layout !== 'minimal' && (
                      <div style={{ width: tpl.s.w, background: tpl.s.bg, padding: '5px 4px', flexShrink: 0 }}>
                        {[70, 55, 80, 60, 45].map((w, j) => <div key={j} style={{ height: '3px', borderRadius: '2px', background: tpl.s.accent, opacity: 0.55, marginBottom: '4px', width: `${w}%` }} />)}
                      </div>
                    )}
                    <div style={{ flex: 1, background: tpl.b.bg, padding: '5px 5px' }}>
                      {[90, 75, 60, 80, 50, 70].map((w, j) => <div key={j} style={{ height: '3px', borderRadius: '2px', background: tpl.b.hc, opacity: 0.18, marginBottom: '4px', width: `${w}%` }} />)}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] font-extrabold text-gray-800 leading-tight">{tpl.name}</p>
                <p className="text-[10px] text-gray-400 font-semibold mt-0.5">{tpl.cat}</p>
                {templateId === tpl.id && <p className="text-[9px] font-black text-black mt-1">✓ Selected</p>}
              </button>
            ))}
          </div>
        </div>
      );
      /* ── Step 1: Personal Info ─────────────────────────────────────────────── */
      case 1: return (
        <div className="space-y-5">
          <div className="flex gap-4 items-start">
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
              <div className="h-24 w-24 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 overflow-hidden flex items-center justify-center text-3xl font-black text-gray-200">
                {data.photoPreview ? <img src={data.photoPreview} alt="" className="h-full w-full object-cover" /> : '📷'}
              </div>
              <label className="cursor-pointer rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-[10px] font-extrabold text-gray-600 hover:bg-gray-50 transition">
                Upload Photo
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
              </label>
            </div>
            <div className="flex-1 grid grid-cols-1 gap-3">
              <div><label className={lbl}>Full Name *</label><input className={fld} placeholder="e.g. Rahul Sharma" value={data.name} onChange={e => upd('name', e.target.value)} /></div>
              <div><label className={lbl}>Target Job Title</label><input className={fld} placeholder="e.g. Senior HR Manager" value={data.targetRole} onChange={e => upd('targetRole', e.target.value)} /></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Email *</label><input type="email" className={fld} placeholder="you@email.com" value={data.email} onChange={e => upd('email', e.target.value)} /></div>
            <div><label className={lbl}>Phone *</label><input className={fld} placeholder="+91 99999 99999" value={data.phone} onChange={e => upd('phone', e.target.value)} /></div>
            <div><label className={lbl}>City</label><input className={fld} placeholder="Kochi" value={data.city} onChange={e => upd('city', e.target.value)} /></div>
            <div><label className={lbl}>State</label><input className={fld} placeholder="Kerala" value={data.state} onChange={e => upd('state', e.target.value)} /></div>
            <div><label className={lbl}>LinkedIn</label><input className={fld} placeholder="linkedin.com/in/yourname" value={data.linkedin} onChange={e => upd('linkedin', e.target.value)} /></div>
            <div><label className={lbl}>Portfolio / Website</label><input className={fld} placeholder="yourwebsite.com" value={data.website} onChange={e => upd('website', e.target.value)} /></div>
            <div><label className={lbl}>Date of Birth</label><input type="date" className={fld} value={data.dob} onChange={e => upd('dob', e.target.value)} /></div>
            <div><label className={lbl}>Nationality</label><input className={fld} placeholder="Indian" value={data.nationality} onChange={e => upd('nationality', e.target.value)} /></div>
          </div>
        </div>
      );
      /* ── Step 2: Career Target + AI Summary ───────────────────────────────── */
      case 2: return (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Target Role / Job Title *</label><input className={fld} placeholder="e.g. Sales Manager" value={data.targetRole} onChange={e => upd('targetRole', e.target.value)} /></div>
            <div><label className={lbl}>Industry / Sector</label><input className={fld} placeholder="e.g. IT, Healthcare, Finance" value={data.targetIndustry} onChange={e => upd('targetIndustry', e.target.value)} /></div>
          </div>
          <div className="rounded-2xl border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-white p-5">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="font-black text-base text-black flex items-center gap-2">🤖 AI Career Summary Generator</p>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">Our AI analyses your job title, industry, years of experience, and skills to craft a tailored, professional career summary — ready to impress.</p>
              </div>
              <button
                type="button"
                onClick={handleGenerateSummary}
                disabled={generating}
                className="flex-shrink-0 rounded-xl bg-black px-5 py-2.5 text-xs font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60 transition"
              >
                {generating ? '⏳ Generating…' : summaryGenerated ? '🔄 Regenerate' : '✨ Generate'}
              </button>
            </div>
            {generating && (
              <div className="space-y-2">
                <div className="h-2 rounded-full bg-yellow-200 overflow-hidden">
                  <div className="h-full bg-yellow-400 rounded-full animate-pulse" style={{ width: '70%' }} />
                </div>
                <p className="text-xs text-gray-500">Crafting your professional career summary…</p>
              </div>
            )}
            {!generating && careerSummary && (
              <div>
                <label className={lbl + ' mb-2'}>Generated Summary (editable)</label>
                <textarea value={careerSummary} onChange={e => setCareerSummary(e.target.value)} rows={5} className="w-full rounded-xl border border-yellow-200 bg-white px-4 py-3 text-sm text-gray-800 outline-none focus:border-yellow-400 resize-none leading-relaxed" />
                <p className="text-[10px] text-gray-400 mt-1.5">✏️ You can freely edit the generated text above.</p>
              </div>
            )}
            {!generating && !careerSummary && (
              <div className="rounded-xl border border-dashed border-yellow-300 bg-yellow-100/50 py-4 text-center">
                <p className="text-xs text-gray-500">Click "Generate" to create your AI-crafted career summary.</p>
              </div>
            )}
          </div>
        </div>
      );
      /* ── Step 3: Experience ────────────────────────────────────────────────── */
      case 3: return (
        <div className="space-y-4">
          {data.experience.map((exp, i) => (
            <div key={i} className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-extrabold text-sm text-gray-800">💼 Experience #{i + 1}</p>
                {data.experience.length > 1 && <button type="button" onClick={() => remArr('experience', i)} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-bold text-red-500 hover:bg-red-50">Remove</button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Job Title *</label><input className={fld} placeholder="HR Manager" value={exp.title} onChange={e => updArr('experience', i, 'title', e.target.value)} /></div>
                <div><label className={lbl}>Company *</label><input className={fld} placeholder="Acme Corp Pvt Ltd" value={exp.company} onChange={e => updArr('experience', i, 'company', e.target.value)} /></div>
                <div><label className={lbl}>Location</label><input className={fld} placeholder="Kochi, Kerala" value={exp.location} onChange={e => updArr('experience', i, 'location', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={lbl}>From</label><input type="month" className={fld} value={exp.from} onChange={e => updArr('experience', i, 'from', e.target.value)} /></div>
                  <div><label className={lbl}>To</label><input type="month" className={fld} value={exp.to} disabled={exp.current} onChange={e => updArr('experience', i, 'to', e.target.value)} /></div>
                </div>
                <div className="col-span-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={exp.current} onChange={e => updArr('experience', i, 'current', e.target.checked)} className="w-4 h-4 rounded" />
                    <span className="text-xs font-bold text-gray-700">Currently working here</span>
                  </label>
                </div>
                <div className="col-span-2">
                  <label className={lbl}>Key Responsibilities</label>
                  <textarea className={fld + ' resize-none'} rows={4} placeholder={'• Managed end-to-end recruitment for 50+ positions\n• Led a team of 5 HR professionals\n• Reduced hiring cycle by 25% through process improvement'} value={exp.responsibilities} onChange={e => updArr('experience', i, 'responsibilities', e.target.value)} />
                  <p className="text-[10px] text-gray-400 mt-1">One point per line — AI will format them as bullets on the resume.</p>
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => addArr('experience', EMPTY_EXP)} className="w-full rounded-2xl border-2 border-dashed border-gray-300 bg-white py-3 text-sm font-bold text-gray-500 hover:border-gray-400 hover:text-gray-700 transition">+ Add Another Experience</button>
        </div>
      );
      /* ── Step 4: Education ─────────────────────────────────────────────────── */
      case 4: return (
        <div className="space-y-4">
          {data.education.map((edu, i) => (
            <div key={i} className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-extrabold text-sm text-gray-800">🎓 Education #{i + 1}</p>
                {data.education.length > 1 && <button type="button" onClick={() => remArr('education', i)} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-bold text-red-500 hover:bg-red-50">Remove</button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Degree / Qualification *</label><input className={fld} placeholder="B.Tech / MBA / B.Com" value={edu.degree} onChange={e => updArr('education', i, 'degree', e.target.value)} /></div>
                <div><label className={lbl}>Institution *</label><input className={fld} placeholder="University / College Name" value={edu.institution} onChange={e => updArr('education', i, 'institution', e.target.value)} /></div>
                <div><label className={lbl}>Specialization / Major</label><input className={fld} placeholder="Human Resources" value={edu.specialization} onChange={e => updArr('education', i, 'specialization', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={lbl}>Year of Passing</label><input className={fld} placeholder="2022" value={edu.year} onChange={e => updArr('education', i, 'year', e.target.value)} /></div>
                  <div><label className={lbl}>Grade / %</label><input className={fld} placeholder="8.5 CGPA / 85%" value={edu.grade} onChange={e => updArr('education', i, 'grade', e.target.value)} /></div>
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => addArr('education', EMPTY_EDU)} className="w-full rounded-2xl border-2 border-dashed border-gray-300 bg-white py-3 text-sm font-bold text-gray-500 hover:border-gray-400 hover:text-gray-700 transition">+ Add Education</button>
        </div>
      );
      /* ── Step 5: Skills ────────────────────────────────────────────────────── */
      case 5: return (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs text-gray-500 font-semibold mb-1">💡 <strong>Tip:</strong> Type a skill and press <kbd className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-bold">Enter</kbd> or <kbd className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-bold">,</kbd> to add it as a chip.</p>
          </div>
          <div>
            <label className={lbl}>Technical Skills</label>
            <ChipInput chips={data.technicalSkills} onAdd={s => upd('technicalSkills', [...data.technicalSkills, s])} onRemove={i => upd('technicalSkills', data.technicalSkills.filter((_, idx) => idx !== i))} placeholder="e.g. Recruitment, Payroll, HRMS, ATS… press Enter" />
          </div>
          <div>
            <label className={lbl}>Soft Skills</label>
            <ChipInput chips={data.softSkills} onAdd={s => upd('softSkills', [...data.softSkills, s])} onRemove={i => upd('softSkills', data.softSkills.filter((_, idx) => idx !== i))} placeholder="e.g. Leadership, Communication, Teamwork… press Enter" />
          </div>
          <div>
            <label className={lbl}>Tools & Software</label>
            <ChipInput chips={data.tools} onAdd={s => upd('tools', [...data.tools, s])} onRemove={i => upd('tools', data.tools.filter((_, idx) => idx !== i))} placeholder="e.g. MS Office, SAP, Zoho, Tally… press Enter" />
          </div>
        </div>
      );
      /* ── Step 6: Languages & More ──────────────────────────────────────────── */
      case 6: return (
        <div className="space-y-6">
          <div>
            <label className={lbl}>Languages</label>
            <div className="space-y-2">
              {data.languages.map((l, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input className={fld + ' flex-1'} placeholder="Language name" value={l.lang} onChange={e => { const a = [...data.languages]; a[i] = { ...a[i], lang: e.target.value }; upd('languages', a); }} />
                  <select className={fld + ' w-40'} value={l.proficiency} onChange={e => { const a = [...data.languages]; a[i] = { ...a[i], proficiency: e.target.value }; upd('languages', a); }}>
                    {['Native', 'Fluent', 'Professional', 'Intermediate', 'Basic'].map(p => <option key={p}>{p}</option>)}
                  </select>
                  {data.languages.length > 1 && <button type="button" onClick={() => remArr('languages', i)} className="text-red-400 hover:text-red-600 font-black text-xl leading-none">×</button>}
                </div>
              ))}
              <button type="button" onClick={() => upd('languages', [...data.languages, { lang: '', proficiency: 'Fluent' }])} className="text-xs font-bold text-gray-500 hover:text-black transition">+ Add Language</button>
            </div>
          </div>
          <div>
            <label className={lbl}>Certifications</label>
            <div className="space-y-3">
              {data.certifications.map((c, i) => (
                <div key={i} className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
                  <input className={fld} placeholder="Certification Name" value={c.name} onChange={e => updArr('certifications', i, 'name', e.target.value)} />
                  <div className="grid grid-cols-2 gap-2">
                    <input className={fld} placeholder="Issuing Organisation" value={c.issuer} onChange={e => updArr('certifications', i, 'issuer', e.target.value)} />
                    <input className={fld} placeholder="Year (e.g. 2023)" value={c.year} onChange={e => updArr('certifications', i, 'year', e.target.value)} />
                  </div>
                  {data.certifications.length > 1 && <button type="button" onClick={() => remArr('certifications', i)} className="text-xs text-red-400 hover:text-red-600 font-bold">Remove</button>}
                </div>
              ))}
              <button type="button" onClick={() => addArr('certifications', EMPTY_CERT)} className="text-xs font-bold text-gray-500 hover:text-black transition">+ Add Certification</button>
            </div>
          </div>
          <div>
            <label className={lbl}>Key Achievements</label>
            <div className="space-y-2">
              {data.achievements.map((a, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input className={fld + ' flex-1'} placeholder={`Achievement ${i + 1} — e.g. Recruited 200+ candidates in FY2023`} value={a.text} onChange={e => { const arr = [...data.achievements]; arr[i] = { text: e.target.value }; upd('achievements', arr); }} />
                  {data.achievements.length > 1 && <button type="button" onClick={() => remArr('achievements', i)} className="text-red-400 hover:text-red-600 font-black text-xl leading-none">×</button>}
                </div>
              ))}
              <button type="button" onClick={() => addArr('achievements', EMPTY_ACH)} className="text-xs font-bold text-gray-500 hover:text-black transition">+ Add Achievement</button>
            </div>
          </div>
          <div>
            <label className={lbl}>Hobbies & Interests</label>
            <input className={fld} placeholder="e.g. Reading, Travel, Photography, Cricket" value={data.hobbies} onChange={e => upd('hobbies', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Place (for Declaration)</label><input className={fld} placeholder="e.g. Kochi" value={data.place} onChange={e => upd('place', e.target.value)} /></div>
            <div><label className={lbl}>Declaration Date</label><input type="date" className={fld} value={data.declarationDate} onChange={e => upd('declarationDate', e.target.value)} /></div>
          </div>
        </div>
      );
      /* ── Step 7: Preview & Download ────────────────────────────────────────── */
      case 7: return (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-black text-lg text-gray-900">Your Resume is Ready! 🎉</p>
              <p className="text-xs text-gray-500 mt-0.5">Review the preview below. Click any step above to go back and edit.</p>
            </div>
            <button type="button" onClick={handlePrint} className="flex items-center gap-2 rounded-xl bg-black px-6 py-3 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 transition shadow-lg">
              🖨️ Download / Print PDF
            </button>
          </div>
          <div className="overflow-auto rounded-2xl border-2 border-gray-200 shadow-xl bg-gray-100 p-4">
            <div ref={resumeRef} style={{ width: '794px', boxShadow: '0 4px 32px rgba(0,0,0,0.12)' }}>
              <ResumeDocument data={data} careerSummary={careerSummary || genResumeCareerSummary(data)} templateId={templateId} />
            </div>
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="space-y-5">
      {/* Header banner */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg,#000 0%,#1a1a1a 60%,#2d2400 100%)' }}>
        <div className="px-8 py-6 flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.35em] text-yellow-400">Dani Job Club</p>
            <h2 className="text-3xl font-black text-white mt-1">AI Resume Creator</h2>
            <p className="text-sm text-gray-400 mt-1.5">Build a beautiful, professional resume in minutes. AI writes your career summary.</p>
          </div>
          <div className="hidden md:block text-8xl opacity-10 font-black text-white select-none">📄</div>
        </div>
      </div>

      {/* Step progress bar */}
      <div className="overflow-x-auto pb-1">
        <div className="flex items-center min-w-max gap-1">
          {RC_STEPS.map((s, i) => (
            <React.Fragment key={i}>
              <button
                type="button"
                onClick={() => setStep(i)}
                className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-2xl transition border ${i === step ? 'bg-black border-black' : i < step ? 'bg-gray-900 border-gray-800 hover:bg-gray-800 cursor-pointer' : 'bg-white border-gray-200 cursor-pointer hover:bg-gray-50'}`}
              >
                <span className="text-lg leading-tight">{s.icon}</span>
                <span className={`text-[9px] font-black uppercase tracking-wide whitespace-nowrap ${i === step ? 'text-yellow-400' : i < step ? 'text-gray-300' : 'text-gray-500'}`}>{s.label}</span>
                {i < step && <span className="text-[8px] font-black text-emerald-400">✓ Done</span>}
              </button>
              {i < RC_STEPS.length - 1 && <div className={`h-0.5 w-6 flex-shrink-0 rounded-full ${i < step ? 'bg-black' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step label */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-black text-xl">{RC_STEPS[step].icon}</div>
        <div>
          <p className="font-black text-base text-gray-900">{RC_STEPS[step].label}</p>
          <p className="text-[10px] text-gray-400 font-semibold">Step {step + 1} of {RC_STEPS.length}</p>
        </div>
      </div>

      {/* Content: form left, live preview right */}
      <div className={step < 7 ? 'grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 items-start' : ''}>
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          {renderStep()}
          <div className="flex items-center justify-between mt-7 pt-5 border-t border-gray-100">
            <button type="button" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-extrabold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              ← Previous
            </button>
            {step < RC_STEPS.length - 1 ? (
              <button type="button" onClick={() => { if (step === 2 && !careerSummary && !generating) handleGenerateSummary(); setStep(s => Math.min(RC_STEPS.length - 1, s + 1)); }} className="rounded-xl bg-black px-7 py-2.5 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 transition">
                {step === 2 && !careerSummary ? '✨ Generate & Continue →' : 'Next →'}
              </button>
            ) : (
              <button type="button" onClick={handlePrint} className="rounded-xl bg-yellow-400 px-7 py-2.5 text-sm font-extrabold text-black hover:bg-yellow-300 transition shadow-lg">
                🖨️ Download PDF
              </button>
            )}
          </div>
        </div>

        {/* Live preview — hidden on final preview step */}
        {step < 7 && (
          <div className="hidden xl:flex flex-col rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm sticky top-4">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Live Preview</p>
              <span className="text-[9px] text-gray-400 font-semibold">Updates as you type</span>
            </div>
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 240px)' }}>
              <div style={{ transform: 'scale(0.45)', transformOrigin: 'top left', width: '222%', pointerEvents: 'none' }}>
                <ResumeDocument data={data} careerSummary={careerSummary} templateId={templateId} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Active Plans Tab ─────────────────────────────────────────────────────────
const daysSince = (dateStr) => {
  if (!dateStr) return 0;
  const parts = dateStr.split('-');
  const start = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - start) / 86400000));
};

const ActivePlansTab = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'prime' | 'regular'
  const [primePage, setPrimePage] = useState(1);
  const [regularPage, setRegularPage] = useState(1);
  const [placedPage, setPlacedPage] = useState(1);
  const PAGE_SIZE = 25;

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/staff/recruiter/active-plans`, { headers: authHeader() });
      setPlans(res.data || []);
    } catch { setPlans([]); } finally { setLoading(false); }
  };

  useEffect(() => { fetchPlans(); }, []);

  useEffect(() => { setPrimePage(1); setRegularPage(1); setPlacedPage(1); }, [search, filter]);

  const matchSearch = (p) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (p.name || '').toLowerCase().includes(q)
      || (p.contact_number || '').includes(q)
      || (p.district || '').toLowerCase().includes(q);
  };

  const allActive = plans.filter((p) => p.placement_status === 'active' && matchSearch(p));
  const allPlaced = plans.filter((p) => p.placement_status === 'placed' && matchSearch(p));

  const activePrime   = allActive.filter((p) => p.plan_type === 'prime').sort((a, b) => {
    const aRem = 100 - daysSince(a.plan_start_date);
    const bRem = 100 - daysSince(b.plan_start_date);
    return aRem - bRem; // soonest deadline first
  });
  const activeRegular = allActive.filter((p) => p.plan_type !== 'prime').sort((a, b) =>
    daysSince(b.plan_start_date) - daysSince(a.plan_start_date)); // longest waiting first

  const showPrime   = filter === 'all' || filter === 'prime';
  const showRegular = filter === 'all' || filter === 'regular';
  const showPlaced  = filter === 'all' || filter === 'placed';

  const primeTotalPages = Math.max(1, Math.ceil(activePrime.length / PAGE_SIZE));
  const primeSafePage = Math.min(Math.max(primePage, 1), primeTotalPages);
  const pagedPrime = activePrime.slice((primeSafePage - 1) * PAGE_SIZE, primeSafePage * PAGE_SIZE);

  const regularTotalPages = Math.max(1, Math.ceil(activeRegular.length / PAGE_SIZE));
  const regularSafePage = Math.min(Math.max(regularPage, 1), regularTotalPages);
  const pagedRegular = activeRegular.slice((regularSafePage - 1) * PAGE_SIZE, regularSafePage * PAGE_SIZE);

  const placedTotalPages = Math.max(1, Math.ceil(allPlaced.length / PAGE_SIZE));
  const placedSafePage = Math.min(Math.max(placedPage, 1), placedTotalPages);
  const pagedPlaced = allPlaced.slice((placedSafePage - 1) * PAGE_SIZE, placedSafePage * PAGE_SIZE);

  const barColor = (elapsed) => {
    const pct = (elapsed / 100) * 100;
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 70) return 'bg-amber-400';
    return 'bg-emerald-500';
  };
  const countdownColor = (remaining) => {
    if (remaining <= 10) return 'text-red-600 font-black';
    if (remaining <= 25) return 'text-amber-600 font-extrabold';
    return 'text-emerald-700 font-bold';
  };
  const regularDayColor = (elapsed) => {
    if (elapsed >= 90) return 'text-red-600 font-black';
    if (elapsed >= 60) return 'text-amber-600 font-extrabold';
    return 'text-blue-600 font-bold';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-black text-gray-900">Member Plans</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {activePrime.length} Prime active · {activeRegular.length} Regular active · {allPlaced.length} Placed
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, contact, district…"
            className="rounded-2xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 min-w-[200px]" />
          {[
            { key: 'all',     label: 'All' },
            { key: 'prime',   label: '👑 Prime' },
            { key: 'regular', label: '⭐ Regular' },
            { key: 'placed',  label: '✅ Placed' },
          ].map(({ key, label }) => (
            <button key={key} type="button" onClick={() => setFilter(key)}
              className={`rounded-xl px-3 py-2 text-xs font-extrabold transition ${filter === key ? 'bg-black text-yellow-300' : 'border border-gray-300 text-black hover:border-yellow-400'}`}>
              {label}
            </button>
          ))}
          <button type="button" onClick={fetchPlans} className="rounded-xl bg-yellow-400 px-3 py-2 text-xs font-extrabold text-black hover:bg-yellow-500 transition">↺</button>
        </div>
      </div>

      {loading && <div className="py-12 text-center text-gray-400 font-semibold">Loading…</div>}

      {/* ── Prime Members — 100-Day Countdown ── */}
      {!loading && showPrime && (
        <div className="rounded-2xl border border-yellow-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-yellow-100 bg-yellow-50">
            <span>👑</span>
            <h3 className="text-sm font-black text-gray-900">Prime Members — 100-Day Countdown</h3>
            <span className="ml-auto rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-black text-yellow-700">{activePrime.length}</span>
          </div>
          {activePrime.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No active Prime members.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {pagedPrime.map((p) => {
                const elapsed   = daysSince(p.plan_start_date);
                const remaining = Math.max(0, 100 - elapsed);
                const pct       = Math.min(100, (elapsed / 100) * 100);
                return (
                  <div key={p.id} className={`flex items-center gap-3 px-4 py-2 ${remaining <= 7 ? 'bg-red-50' : remaining <= 25 ? 'bg-amber-50' : ''}`}>
                    {p.photo_file
                      ? <img src={p.photo_file.startsWith('http') ? p.photo_file : `${API_URL}${p.photo_file}`} alt={p.name} className="h-8 w-8 rounded-lg object-cover border border-gray-200 flex-shrink-0" />
                      : <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-100 text-sm flex-shrink-0">👑</span>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-extrabold text-gray-900 leading-tight">{p.name}</p>
                        {p.registration_number && <span className="text-[10px] text-gray-400">{p.registration_number}</span>}
                        {remaining <= 10 && <span className="text-[10px] text-red-500 font-black">⚠ URGENT</span>}
                      </div>
                      <p className="text-xs text-gray-500 leading-tight">{p.contact_number}{p.district ? ` · ${p.district}` : ''}{p.department ? ` · ${p.department}` : ''}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                          <div className={`h-full rounded-full ${barColor(elapsed)}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">D{elapsed} · ends {p.plan_end_date}{p.hr_name ? ` · ${p.hr_name}` : ''}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 w-14">
                      <p className={`text-xl font-black leading-none ${countdownColor(remaining)}`}>{remaining}</p>
                      <p className="text-[10px] text-gray-400">days left</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {primeTotalPages > 1 && (
            <div className="mt-5 flex items-center justify-center gap-2 flex-wrap px-4 pb-4">
              <button type="button" onClick={() => setPrimePage(1)} disabled={primeSafePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">«</button>
              <button type="button" onClick={() => setPrimePage(p => Math.max(p - 1, 1))} disabled={primeSafePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
              {Array.from({ length: primeTotalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === primeTotalPages || Math.abs(n - primeSafePage) <= 2)
                .map((n, i, arr) => (
                  <React.Fragment key={n}>
                    {i > 0 && arr[i - 1] !== n - 1 && <span className="text-gray-400 text-sm px-1">…</span>}
                    <button type="button" onClick={() => setPrimePage(n)}
                      className={`rounded-xl border px-3 py-1.5 text-sm font-bold transition ${primeSafePage === n ? 'bg-black text-yellow-400 border-black' : 'hover:bg-gray-50'}`}>
                      {n}
                    </button>
                  </React.Fragment>
                ))}
              <button type="button" onClick={() => setPrimePage(p => Math.min(p + 1, primeTotalPages))} disabled={primeSafePage === primeTotalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next ›</button>
              <button type="button" onClick={() => setPrimePage(primeTotalPages)} disabled={primeSafePage === primeTotalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">»</button>
              <span className="text-sm text-gray-500 ml-2">Page {primeSafePage} of {primeTotalPages}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Regular Members — Days Counter (counting up) ── */}
      {!loading && showRegular && (
        <div className="rounded-2xl border border-blue-100 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-blue-100 bg-blue-50">
            <span>⭐</span>
            <h3 className="text-sm font-black text-gray-900">Regular Members</h3>
            <span className="text-xs text-gray-400 ml-1">· days since reg. fee paid</span>
            <span className="ml-auto rounded-full bg-blue-100 border border-blue-200 px-2.5 py-0.5 text-xs font-black text-blue-700">{activeRegular.length}</span>
          </div>
          {activeRegular.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No active Regular plan members.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {pagedRegular.map((p) => {
                const elapsed = daysSince(p.plan_start_date);
                return (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-2">
                    {p.photo_file
                      ? <img src={p.photo_file.startsWith('http') ? p.photo_file : `${API_URL}${p.photo_file}`} alt={p.name} className="h-8 w-8 rounded-lg object-cover border border-gray-200 flex-shrink-0" />
                      : <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-sm flex-shrink-0">⭐</span>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-extrabold text-gray-900 leading-tight truncate">{p.name}</p>
                        {p.registration_number && <span className="text-[10px] text-gray-400">{p.registration_number}</span>}
                      </div>
                      <p className="text-xs text-gray-500 leading-tight">{p.contact_number}{p.district ? ` · ${p.district}` : ''}{p.department ? ` · ${p.department}` : ''}{p.expecting_job ? ` · ${p.expecting_job}` : ''}</p>
                      <p className="text-[10px] text-gray-400">since {p.plan_start_date}</p>
                    </div>
                    <div className="text-right flex-shrink-0 w-14">
                      <p className={`text-xl font-black leading-none ${regularDayColor(elapsed)}`}>{elapsed}</p>
                      <p className="text-[10px] text-gray-400">days</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {regularTotalPages > 1 && (
            <div className="mt-5 flex items-center justify-center gap-2 flex-wrap px-4 pb-4">
              <button type="button" onClick={() => setRegularPage(1)} disabled={regularSafePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">«</button>
              <button type="button" onClick={() => setRegularPage(p => Math.max(p - 1, 1))} disabled={regularSafePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
              {Array.from({ length: regularTotalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === regularTotalPages || Math.abs(n - regularSafePage) <= 2)
                .map((n, i, arr) => (
                  <React.Fragment key={n}>
                    {i > 0 && arr[i - 1] !== n - 1 && <span className="text-gray-400 text-sm px-1">…</span>}
                    <button type="button" onClick={() => setRegularPage(n)}
                      className={`rounded-xl border px-3 py-1.5 text-sm font-bold transition ${regularSafePage === n ? 'bg-black text-yellow-400 border-black' : 'hover:bg-gray-50'}`}>
                      {n}
                    </button>
                  </React.Fragment>
                ))}
              <button type="button" onClick={() => setRegularPage(p => Math.min(p + 1, regularTotalPages))} disabled={regularSafePage === regularTotalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next ›</button>
              <button type="button" onClick={() => setRegularPage(regularTotalPages)} disabled={regularSafePage === regularTotalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">»</button>
              <span className="text-sm text-gray-500 ml-2">Page {regularSafePage} of {regularTotalPages}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Placed Members ── */}
      {!loading && showPlaced && (
        <div className="rounded-2xl border border-emerald-100 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-emerald-100 bg-emerald-50">
            <span>✅</span>
            <h3 className="text-sm font-black text-gray-900">Placed Members</h3>
            <span className="ml-auto rounded-full bg-emerald-100 border border-emerald-200 px-2.5 py-0.5 text-xs font-black text-emerald-700">{allPlaced.length}</span>
          </div>
          {allPlaced.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No placed members yet.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {pagedPlaced.map((p) => {
                const daysToPlace = daysSince(p.plan_start_date);
                const securedDate = p.job_secured_date || p.updated_at?.slice(0, 10) || '—';
                return (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-2 bg-emerald-50/40">
                    {p.photo_file
                      ? <img src={p.photo_file.startsWith('http') ? p.photo_file : `${API_URL}${p.photo_file}`} alt={p.name} className="h-8 w-8 rounded-lg object-cover border border-emerald-300 flex-shrink-0" />
                      : <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-sm flex-shrink-0">✅</span>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-extrabold text-gray-900 leading-tight">{p.name}</p>
                        {p.plan_type === 'prime'
                          ? <span className="rounded-full bg-yellow-400 px-1.5 py-0.5 text-[10px] font-black text-black">👑 Prime</span>
                          : <span className="rounded-full bg-blue-100 border border-blue-300 px-1.5 py-0.5 text-[10px] font-black text-blue-700">⭐ Regular</span>}
                      </div>
                      <p className="text-xs text-gray-500 leading-tight">{p.contact_number}{p.district ? ` · ${p.district}` : ''}{p.department ? ` · ${p.department}` : ''}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-black text-emerald-700">✅ Placed</p>
                      <p className="text-[10px] text-gray-500">{securedDate}</p>
                      <p className="text-[10px] text-gray-400">in {daysToPlace}d</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {placedTotalPages > 1 && (
            <div className="mt-5 flex items-center justify-center gap-2 flex-wrap px-4 pb-4">
              <button type="button" onClick={() => setPlacedPage(1)} disabled={placedSafePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">«</button>
              <button type="button" onClick={() => setPlacedPage(p => Math.max(p - 1, 1))} disabled={placedSafePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
              {Array.from({ length: placedTotalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === placedTotalPages || Math.abs(n - placedSafePage) <= 2)
                .map((n, i, arr) => (
                  <React.Fragment key={n}>
                    {i > 0 && arr[i - 1] !== n - 1 && <span className="text-gray-400 text-sm px-1">…</span>}
                    <button type="button" onClick={() => setPlacedPage(n)}
                      className={`rounded-xl border px-3 py-1.5 text-sm font-bold transition ${placedSafePage === n ? 'bg-black text-yellow-400 border-black' : 'hover:bg-gray-50'}`}>
                      {n}
                    </button>
                  </React.Fragment>
                ))}
              <button type="button" onClick={() => setPlacedPage(p => Math.min(p + 1, placedTotalPages))} disabled={placedSafePage === placedTotalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next ›</button>
              <button type="button" onClick={() => setPlacedPage(placedTotalPages)} disabled={placedSafePage === placedTotalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">»</button>
              <span className="text-sm text-gray-500 ml-2">Page {placedSafePage} of {placedTotalPages}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ─── Registrations Tab ───────────────────────────────────────────────────────
const REG_PAGE_SIZE = 25;

const RegistrationsTab = () => {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ name: '', contact: '', district: '', department: '', expectingJob: '', forwardStatus: 'all' });
  const [page, setPage] = useState(1);
  const [hrList, setHrList] = useState([]);
  const [forwardModal, setForwardModal] = useState(null);
  const [selectedHr, setSelectedHr] = useState('');
  const [forwarding, setForwarding] = useState(false);
  const [forwardMsg, setForwardMsg] = useState('');
  const [viewModal, setViewModal] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/staff/recruiter/all-registrations`, { headers: authHeader() });
      setRegs(res.data || []);
    } catch { setRegs([]); }
    finally { setLoading(false); }
  };

  const fetchHr = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff/recruiter/hr-assistants`, { headers: authHeader() });
      setHrList(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchAll(); fetchHr(); }, []);

  const handleFilterChange = (e) => { setFilters((f) => ({ ...f, [e.target.name]: e.target.value })); setPage(1); };
  const clearFilters = () => { setFilters({ name: '', contact: '', district: '', department: '', expectingJob: '', forwardStatus: 'all' }); setSearch(''); setPage(1); };

  const filtered = regs.filter((r) => {
    const q = search.toLowerCase();
    if (q && !(
      (r.name || '').toLowerCase().includes(q) ||
      (r.contact_number || '').includes(q) ||
      (r.district || '').toLowerCase().includes(q) ||
      (r.department || '').toLowerCase().includes(q) ||
      (r.expecting_job || '').toLowerCase().includes(q) ||
      (r.registration_number || '').toLowerCase().includes(q)
    )) return false;
    if (filters.name && !(r.name || '').toLowerCase().includes(filters.name.toLowerCase())) return false;
    if (filters.contact && !(r.contact_number || '').includes(filters.contact)) return false;
    if (filters.district && !(r.district || '').toLowerCase().includes(filters.district.toLowerCase())) return false;
    if (filters.department && !(r.department || '').toLowerCase().includes(filters.department.toLowerCase())) return false;
    if (filters.expectingJob && !(r.expecting_job || '').toLowerCase().includes(filters.expectingJob.toLowerCase())) return false;
    if (filters.forwardStatus === 'forwarded' && !(Number(r.is_forwarded) > 0)) return false;
    if (filters.forwardStatus === 'not-forwarded' && Number(r.is_forwarded) > 0) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / REG_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * REG_PAGE_SIZE, safePage * REG_PAGE_SIZE);

  const hasActiveFilters = filters.name || filters.contact || filters.district || filters.department || filters.expectingJob || filters.forwardStatus !== 'all';

  const openForward = (row) => { setForwardModal(row); setSelectedHr(hrList[0]?.user_id || ''); setForwardMsg(''); };
  const closeForward = () => { setForwardModal(null); setForwarding(false); setForwardMsg(''); };

  const handleForward = async () => {
    if (!selectedHr) return;
    setForwarding(true);
    setForwardMsg('');
    try {
      await axios.post(
        `${API_URL}/api/staff/recruiter/all-registrations/${forwardModal.id}/forward`,
        { forwarded_to_user_id: selectedHr },
        { headers: authHeader() }
      );
      const hrName = (hrList.find((h) => h.user_id === selectedHr) || {}).name || selectedHr;
      setForwardMsg(`✅ Forwarded to ${hrName}`);
      fetchAll();
      setTimeout(closeForward, 1500);
    } catch (err) {
      setForwardMsg(`❌ ${err?.response?.data?.error || 'Failed to forward'}`);
    } finally { setForwarding(false); }
  };

  const fileUrl = (path) => !path ? null : (path.startsWith('http') ? path : `${API_URL}${path}`);

  // page number buttons (max 7 shown)
  const pageButtons = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, safePage, safePage - 1, safePage + 1].filter((p) => p >= 1 && p <= totalPages));
    return [...pages].sort((a, b) => a - b);
  })();

  return (
    <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
      {/* ── Header ── */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-black text-gray-900">Talent Registrations</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Showing {filtered.length === 0 ? 0 : (safePage - 1) * REG_PAGE_SIZE + 1}–{Math.min(safePage * REG_PAGE_SIZE, filtered.length)} of {filtered.length}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`rounded-xl border px-4 py-2.5 text-sm font-extrabold transition ${showFilters || hasActiveFilters ? 'border-black bg-black text-yellow-300' : 'border-gray-300 bg-white text-black hover:border-yellow-400'}`}
          >
            {showFilters ? 'Hide Filters' : `Filters${hasActiveFilters ? ' ●' : ''}`}
          </button>
          <button type="button" onClick={fetchAll} className="rounded-xl bg-yellow-400 px-4 py-2.5 text-sm font-extrabold text-black hover:bg-yellow-500 transition">
            ↺ Refresh
          </button>
        </div>
      </div>

      {/* ── Search ── */}
      <div className="mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Quick search — name, contact, district, department, reg. number…"
          className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
      </div>

      {/* ── Filter Panel ── */}
      {showFilters && (
        <div className="mb-4 rounded-[24px] border border-yellow-200 bg-[linear-gradient(135deg,#fffdf7_0%,#fff8e6_50%,#ffffff_100%)] p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <input name="name" value={filters.name} onChange={handleFilterChange} placeholder="Filter by name"
              className="rounded-2xl border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            <input name="contact" value={filters.contact} onChange={handleFilterChange} placeholder="Filter by contact number"
              className="rounded-2xl border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            <input name="district" value={filters.district} onChange={handleFilterChange} placeholder="Filter by district"
              className="rounded-2xl border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            <input name="department" value={filters.department} onChange={handleFilterChange} placeholder="Filter by department"
              className="rounded-2xl border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            <input name="expectingJob" value={filters.expectingJob} onChange={handleFilterChange} placeholder="Filter by expecting job"
              className="rounded-2xl border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            <select name="forwardStatus" value={filters.forwardStatus} onChange={handleFilterChange}
              className="rounded-2xl border border-gray-300 bg-white px-3 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-yellow-400">
              <option value="all">All forward statuses</option>
              <option value="forwarded">Forwarded only</option>
              <option value="not-forwarded">Not forwarded only</option>
            </select>
          </div>
          <div className="mt-3 flex justify-start">
            <button type="button" onClick={clearFilters}
              className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-black hover:border-yellow-400 transition">
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {loading && <div className="py-12 text-center text-gray-400 font-semibold">Loading…</div>}

      {!loading && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-100 text-left text-xs font-extrabold uppercase tracking-wider text-gray-400">
                <th className="px-3 py-2.5">#</th>
                <th className="px-3 py-2.5">Photo</th>
                <th className="px-3 py-2.5">Name</th>
                <th className="px-3 py-2.5">Contact</th>
                <th className="px-3 py-2.5">District</th>
                <th className="px-3 py-2.5">Department</th>
                <th className="px-3 py-2.5">Expecting Job</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Registered</th>
                <th className="px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 && (
                <tr><td colSpan={10} className="py-12 text-center text-gray-400 font-semibold">No registrations found.</td></tr>
              )}
              {paginated.map((row, idx) => {
                const forwarded = Number(row.is_forwarded || 0) > 0;
                const isPrime = row.plan_type === 'prime';
                const isRegular = row.plan_type === 'regular';
                const rowBg = isPrime
                  ? 'bg-yellow-50 border-l-4 border-l-yellow-400'
                  : isRegular
                  ? 'bg-blue-50 border-l-4 border-l-blue-400'
                  : forwarded ? 'bg-emerald-50/50' : '';
                return (
                  <tr key={row.id} className={`border-b border-gray-100 transition hover:brightness-95 ${rowBg}`}>
                    <td className="px-3 py-3 text-xs text-gray-400">{(safePage - 1) * REG_PAGE_SIZE + idx + 1}</td>
                    <td className="px-3 py-3">
                      {fileUrl(row.photo_file)
                        ? <img src={fileUrl(row.photo_file)} alt={row.name} className="h-10 w-10 rounded-xl object-cover border border-gray-200" />
                        : <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-lg">👤</span>}
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-extrabold text-gray-900 whitespace-nowrap">{row.name || '—'}</p>
                      {row.registration_number && <p className="text-[10px] text-gray-400 mt-0.5">{row.registration_number}</p>}
                      {isPrime && (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-yellow-400 px-2.5 py-0.5 text-[11px] font-black text-black shadow-sm">👑 PRIME</span>
                      )}
                      {isRegular && (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-500 px-2.5 py-0.5 text-[11px] font-black text-white shadow-sm">⭐ REGULAR</span>
                      )}
                      <PartnerTag row={row} />
                    </td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{row.contact_number || '—'}</td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{row.district || '—'}</td>
                    <td className="px-3 py-3 text-gray-600">{row.department || '—'}</td>
                    <td className="px-3 py-3 text-gray-600">{row.expecting_job || '—'}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {forwarded
                        ? <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-700">✓ Forwarded</span>
                        : <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-black text-gray-500">Pending</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5">
                        <button type="button" onClick={() => setViewModal(row)}
                          className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition">
                          View
                        </button>
                        <button
                          type="button"
                          disabled={forwarded}
                          onClick={() => openForward(row)}
                          title={forwarded ? 'Already forwarded' : 'Forward to HR Assistant'}
                          className={`rounded-lg border px-2.5 py-1.5 text-xs font-extrabold transition ${forwarded ? 'cursor-not-allowed border-gray-200 text-gray-300' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-50'}`}
                        >
                          {forwarded ? '✓ Fwd' : '→ Fwd'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">«</button>
          <button type="button" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
            .map((n, i, arr) => (
              <React.Fragment key={n}>
                {i > 0 && arr[i - 1] !== n - 1 && <span className="text-gray-400 text-sm px-1">…</span>}
                <button type="button" onClick={() => setPage(n)}
                  className={`rounded-xl border px-3 py-1.5 text-sm font-bold transition ${safePage === n ? 'bg-black text-yellow-400 border-black' : 'hover:bg-gray-50'}`}>
                  {n}
                </button>
              </React.Fragment>
            ))}
          <button type="button" onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next ›</button>
          <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">»</button>
          <span className="text-sm text-gray-500 ml-2">Page {safePage} of {totalPages}</span>
        </div>
      )}

      {/* ── View Modal ── */}
      {viewModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-8">
          <div className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-4 bg-black px-6 py-4">
              {fileUrl(viewModal.photo_file)
                ? <img src={fileUrl(viewModal.photo_file)} alt={viewModal.name} className="h-14 w-14 rounded-2xl object-cover border-2 border-yellow-400 flex-shrink-0" />
                : <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-800 text-2xl flex-shrink-0">👤</span>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-lg font-black text-white">{viewModal.name}</p>
                  <PartnerTag row={viewModal} />
                </div>
                <p className="text-xs text-yellow-400 font-bold mt-0.5">{viewModal.registration_number || `#${viewModal.id}`}</p>
              </div>
              <button type="button" onClick={() => setViewModal(null)} className="rounded-full p-2 hover:bg-white/10 text-white text-xl flex-shrink-0">✕</button>
            </div>
            <div className="p-6 space-y-5">
              {/* Status badge */}
              <div className="flex items-center gap-2">
                {Number(viewModal.is_forwarded) > 0
                  ? <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-700">✓ Forwarded to HR Assistant</span>
                  : <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-700">⏳ Awaiting Forward</span>}
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-500">{fmtDate(viewModal.created_at)}</span>
              </div>

              {/* Contact Info */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Contact Information</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {[
                    ['Name', viewModal.name],
                    ['Email', viewModal.email],
                    ['Contact', viewModal.contact_number],
                    ['WhatsApp', viewModal.whatsapp_number],
                    ['Place', viewModal.place],
                    ['District', viewModal.district],
                    ['Guardian Number', viewModal.guardian_number],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                      <p className="text-[10px] font-bold text-gray-400 uppercase">{label}</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5 break-all">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Professional Info */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Professional Details</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {[
                    ['Highest Qualification', viewModal.highest_qualification],
                    ['Qualification Dept', viewModal.qualification_department],
                    ['Completed Year', viewModal.completed_year],
                    ['Exam Status', viewModal.exam_status],
                    ['Department', viewModal.department],
                    ['Section', viewModal.section],
                    ['Expecting Job', viewModal.expecting_job],
                    ['Preferred Location', viewModal.preferred_location],
                    ['Preferred District', viewModal.preferred_district],
                    ['Experience', Array.isArray(viewModal.experience_details) && viewModal.experience_details.length > 0
                      ? viewModal.experience_details.map((e) => [e.exp_department, e.title_post, e.company_name].filter(Boolean).join(' · ')).join(' | ')
                      : null],
                    ['Job Priority', viewModal.job_priority],
                    ['Rating', viewModal.rating],
                    ['Driving License', viewModal.driving_license],
                    ['License Category', viewModal.license_category],
                    ['Marital Status', viewModal.marital_status],
                    ['Languages', viewModal.highlight_language],
                    ['Age', viewModal.age],
                    ['Gender', viewModal.gender],
                    ['How You Heard', viewModal.how_you_heard],
                    ['Contact Through', viewModal.contact_through],
                    ['Additional Qualification', viewModal.additional_qualification],
                    ['Remarks', viewModal.remarks],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                      <p className="text-[10px] font-bold text-gray-400 uppercase">{label}</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Files */}
              {(fileUrl(viewModal.resume_file) || fileUrl(viewModal.photo_file)) && (
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Documents</p>
                  <div className="flex flex-wrap gap-2">
                    {fileUrl(viewModal.resume_file) && (
                      <a href={fileUrl(viewModal.resume_file)} target="_blank" rel="noreferrer"
                        className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700 hover:bg-blue-100 transition">
                        📄 View Resume ↗
                      </a>
                    )}
                    {fileUrl(viewModal.photo_file) && (
                      <a href={fileUrl(viewModal.photo_file)} target="_blank" rel="noreferrer"
                        className="rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-100 transition">
                        🖼 View Photo ↗
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Forward action from view modal */}
              {!Number(viewModal.is_forwarded) && (
                <div className="pt-2 border-t border-gray-100">
                  <button type="button"
                    onClick={() => { setViewModal(null); openForward(viewModal); }}
                    className="w-full rounded-2xl bg-emerald-600 py-3 text-sm font-extrabold text-white hover:bg-emerald-700 transition">
                    → Forward to HR Assistant
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Forward Modal ── */}
      {forwardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="bg-black px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-base font-black text-yellow-400">→ Forward Registration</p>
                <p className="text-xs text-gray-300 mt-0.5 truncate max-w-[260px]">{forwardModal.name}</p>
              </div>
              <button type="button" onClick={closeForward} className="rounded-full p-1.5 hover:bg-white/10 text-white text-lg">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm space-y-1">
                <p><span className="font-bold text-gray-500">Contact:</span> {forwardModal.contact_number || '—'}</p>
                <p><span className="font-bold text-gray-500">District:</span> {forwardModal.district || '—'}</p>
                <p><span className="font-bold text-gray-500">Department:</span> {forwardModal.department || '—'}</p>
                <p><span className="font-bold text-gray-500">Expecting:</span> {forwardModal.expecting_job || '—'}</p>
              </div>
              {hrList.length === 0
                ? <p className="text-sm text-red-500 font-semibold">No HR Assistants found.</p>
                : (
                  <div>
                    <label className="block text-xs font-extrabold text-gray-700 mb-1.5">Select HR Assistant <span className="text-red-500">*</span></label>
                    <select value={selectedHr} onChange={(e) => setSelectedHr(e.target.value)}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-yellow-400">
                      {hrList.map((hr) => (
                        <option key={hr.user_id} value={hr.user_id}>{hr.name} ({hr.user_id})</option>
                      ))}
                    </select>
                  </div>
                )}
              {forwardMsg && (
                <p className={`text-sm font-semibold rounded-xl px-3 py-2 ${forwardMsg.startsWith('✅') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{forwardMsg}</p>
              )}
              {hrList.length > 0 && (
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={closeForward}
                    className="flex-1 rounded-2xl border border-gray-300 py-2.5 text-sm font-extrabold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
                  <button type="button" onClick={handleForward} disabled={forwarding || !selectedHr}
                    className="flex-1 rounded-2xl bg-emerald-600 py-2.5 text-sm font-extrabold text-white hover:bg-emerald-700 disabled:opacity-60 transition">
                    {forwarding ? 'Forwarding…' : '→ Forward'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Dashboard Root ───────────────────────────────────────────────────────────
const RecruiterDashboard = () => {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [registrations, setRegistrations] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [pipelineJumpId, setPipelineJumpId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recruiterName, setRecruiterName] = useState('Recruiter');

  const fetchDashboard = useCallback(async () => {
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/staff/recruiter/dashboard`, { headers: authHeader() });
      setRegistrations(res.data?.registrations || []);
      setVacancies(res.data?.vacancies || []);
      if (res.data?.recruiterName) setRecruiterName(res.data.recruiterName);
    } catch { setError('Failed to load dashboard. Check your network connection.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { const t = setInterval(fetchDashboard, 30000); return () => clearInterval(t); }, [fetchDashboard]);

  const clearPipelineJump = useCallback(() => setPipelineJumpId(null), []);
  const goToPipeline = useCallback(async (reg, vacId) => {
    if (vacId && String(reg.selected_job_id) !== String(vacId)) {
      try {
        await putWorkflow(reg.id, reg, { selected_job_id: vacId, is_shortlisted: Math.max(Number(reg.is_shortlisted || 0), 1) });
        await fetchDashboard();
      } catch {}
    }
    setPipelineJumpId(reg.id);
    setActiveTab('pipeline');
  }, [fetchDashboard]);

  const pendingAvail = registrations.filter((r) => Number(r.is_shortlisted) > 0 && (r.interview_status === 'Not Scheduled' || !r.interview_status || r.interview_status === 'Shortlisted') && !Number(r.payment_received) && !(r.candidate_availability === 'Available' && r.vacancy_availability === 'Available' && r.hr_assistant_confirmation === 'Accepted' && r.assistant_recruiter_confirmation === 'Accepted')).length;

  const TABS = [
    { id: 'pipeline', label: '🏠 Pipeline', badge: null },
    { id: 'registrations', label: '📋 Registrations', badge: null },
    { id: 'applications', label: '📨 Job Applications', badge: null },
    { id: 'active-plans', label: '📊 Active Plans', badge: null },
    { id: 'vacancies', label: '💼 Vacancies', badge: vacancies.filter((v) => !Number(v.is_filled)).length || null },
    { id: 'website-enquiries', label: '🌐 Website Enquiries', badge: null },
    { id: 'search', label: '🔍 Search', badge: null },
    { id: 'match', label: '🤖 AI Match', badge: null },
    { id: 'resume', label: '📝 Resume Creator', badge: null },
  ];

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-black" />
        <p className="mt-4 font-black text-gray-400 tracking-wide">Loading dashboard…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="text-center">
        <p className="text-red-600 font-bold mb-4">{error}</p>
        <button type="button" onClick={fetchDashboard} className="rounded-xl bg-black px-6 py-2.5 text-sm font-extrabold text-yellow-400 hover:bg-gray-900">Retry</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      {/* ── Header ── */}
      <div className="bg-black">
        <div className="mx-auto max-w-[1600px] px-3">
          <div className="flex items-center justify-between py-4 gap-3">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-yellow-400">Recruiter Dashboard</p>
              <h1 className="text-xl font-black text-white mt-0.5">{recruiterName}</h1>
            </div>
            <div className="flex items-center gap-3">
              {pendingAvail > 0 && (
                <button type="button" onClick={() => setActiveTab('pipeline')} className="flex items-center gap-1.5 rounded-xl bg-yellow-400 px-3 py-2 text-xs font-extrabold text-black hover:bg-yellow-300 transition">
                  ⏳ {pendingAvail} awaiting
                </button>
              )}
              <NotificationBell />
            </div>
          </div>
          {/* ── Tabs ── */}
          <div className="flex border-t border-white/10">
            {TABS.map((tab) => (
              <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-extrabold border-b-[3px] transition ${activeTab === tab.id ? 'border-yellow-400 text-yellow-300' : 'border-transparent text-gray-400 hover:text-white hover:border-white/20'}`}>
                {tab.label}
                {tab.badge != null && (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-black leading-none ${activeTab === tab.id ? 'bg-yellow-400 text-black' : 'bg-white/20 text-white'}`}>{tab.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="mx-auto max-w-[1600px] px-3 py-5">
        {activeTab === 'pipeline' && (
          <div className="space-y-4">
            <InstallmentDuesPanel API_URL={API_URL} endpoint="/api/staff/recruiter/payment-dues" hideWhenEmpty />
            <PipelineTab registrations={registrations} vacancies={vacancies} onRefresh={fetchDashboard} jumpToId={pipelineJumpId} onJumpHandled={clearPipelineJump} />
          </div>
        )}
        {activeTab === 'registrations' && <RegistrationsTab />}
        {activeTab === 'applications' && <AppliedMembersTab />}
        {activeTab === 'active-plans' && <ActivePlansTab />}
        {activeTab === 'vacancies' && <VacanciesTab vacancies={vacancies} onRefresh={fetchDashboard} />}
        {activeTab === 'website-enquiries' && <WebsiteEnquiriesTab />}
        {activeTab === 'search' && <SearchTab registrations={registrations} vacancies={vacancies} />}
        {activeTab === 'match' && <MatchTab registrations={registrations} vacancies={vacancies} onRefresh={fetchDashboard} onGoToPipeline={goToPipeline} />}
        {activeTab === 'resume' && <ResumeCreatorTab />}
      </div>
    </div>
  );
};

export default RecruiterDashboard;
